import React, { useEffect, useState, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart, Legend
} from 'recharts';
import {
  // Charts and Analytics
  BarChart3, PieChart as PieChartIcon, TrendingUp,
  // Navigation Icons
  Home, User, Users, Store, CreditCard, RotateCcw, AlertCircle,
  Bell, Settings, FileText, ReceiptText, Shield, Activity, LayoutTemplate, LogOut, ShoppingBag,
  Menu, X as CloseIcon, ChevronLeft, ChevronRight as ChevronRightIcon,
  // Financial Icons
  DollarSign, Wallet, Building2 as Building, ArrowUpRight, Truck,
  // System Icons
  Zap, Database, Cpu, HardDrive, Wifi, WifiOff,
  // UI Icons
  ChevronRight, ChevronDown, Plus, X, Check, CheckCircle, XCircle, Info, Clock, Calendar,
  Filter, Search, Download, Upload, Edit, Trash2, Archive, MoreHorizontal, MoreVertical,
  Star, StarOff, Heart, HeartOff, Bookmark, BookmarkOff, Share2,
  Copy, ExternalLink, Link, Unlink, Lock, Unlock, Key, KeyOff,
  Mail, Phone, MapPin, Globe, Globe2, Monitor, Smartphone, Tablet,
  MessageSquare, MessageCircle, Package, ShoppingCart,
  Server, Cloud, CloudOff, Signal, SignalZero, SignalLow, SignalMedium, SignalHigh,
  Battery, BatteryLow, BatteryMedium, BatteryHigh, BatteryFull,
  Power, PowerOff, Play, Pause, Stop, SkipBack, SkipForward,
  Volume2, VolumeX, Volume1, Mic, MicOff, Camera, CameraOff,
  Video, VideoOff, Headphones, HeadphonesOff, Speaker, SpeakerOff,
  Radio, RadioOff, Bluetooth, BluetoothOff, Usb, UsbOff,
  AlertTriangle, Eye, MousePointer, Ban, Target, LogIn, Megaphone, RefreshCw, Layers, Gift, Sparkles,
  BookOpen, Percent
} from 'lucide-react';
import './theme.css';
import './components/Merchants.css';
import './components/Customers.css';
import ConsolidatedAIInsights from './components/ConsolidatedAIInsights';
import LivechatAdoption from './components/LivechatAdoption';
import KnowledgeBaseManagement from './components/KnowledgeBaseManagement';
import AIAssistant from './components/AIAssistant';
import Settlements from './components/Settlements';
import PlatformRevenue from './components/PlatformRevenue';
import MerchantGatewayManagement from './components/MerchantGatewayManagement';
import Refunds from './components/Refunds';
import WhatsAppAdmin from './components/WhatsAppAdmin';
import WhatsAppControlCenter from './components/WhatsAppControlCenter';
import SMSAdmin from './components/SMSAdmin';
import PushNotifications from './components/PushNotifications';
import FeatureManagement from './components/FeatureManagement';
import TierManagement from './components/TierManagement';
import TreasuryPage from './components/TreasuryPage';
import TierLimitsManagement from './components/TierLimitsManagement';
import AILimitsManagement from './components/AILimitsManagement';
import AIProviders from './components/AIProviders';
import CustomDomains from './components/CustomDomains';
import CurrencyManagement from './components/CurrencyManagement';
import KYCManagement from './components/KYCManagement';
import PaymentGatewayManagement from './components/PaymentGatewayManagement';
import ShippingManagement from './components/ShippingManagement';
import EmailCampaignManagement from './components/EmailCampaignManagement';
import EmailCampaigns from './components/EmailCampaigns';
import ProductManagement from './components/ProductManagement';
import NotFound from './components/NotFound';
import ErrorBoundary from './components/ErrorBoundary';
import SubscriptionManagement from './components/SubscriptionManagement';
import MerchantAnalytics from './components/MerchantAnalytics';
import AppointmentManagement from './components/AppointmentManagement';
import GoogleOAuthConfig from './components/GoogleOAuthConfig';
import Dashboard from './components/Dashboard';
import PlatformInsights from './components/PlatformInsights';
import MerchantCurrencyManagement from './components/MerchantCurrencyManagement';
import ServiceCharge from './components/ServiceCharge';
import CentralVatManagement from './components/CentralVatManagement';
import MerchantSegmentation from './components/MerchantSegmentation';
import MarketingHub from './components/marketing/MarketingHub';
import StaffActivity from './components/StaffActivity';
import GA4PlatformConfig from './components/GA4PlatformConfig';
import POSManagement from './components/POSManagement';
import POSSettlement from './components/POSSettlement';
import TeamMembersManagement from './components/TeamMembersManagement';
import LocationManagement from './components/LocationManagement';
import WaitlistManagement from './components/WaitlistManagement';
import WebsiteLeadsManagement from './components/WebsiteLeadsManagement';
import DVAManagement from './components/DVAManagement';
import DVASettlements from './components/DVASettlements';
import GroupDealSettlements from './components/GroupDealSettlements';
import AdFeatureManagement from './components/AdFeatureManagement';
import WalletManagement from './components/WalletManagement';
import ChowdeckManagement from './components/ChowdeckManagement';
import AffiliateOversight from './components/AffiliateOversight';
import ReferralManagement from './components/ReferralManagement';
import MerchantActivityInsights from './components/MerchantActivityInsights';
import MarketplaceManagement from './components/MarketplaceManagement';
import ThemeManagement from './components/ThemeManagement';
import MerchantDetail from './components/MerchantDetail';
import MerchantOrders from './components/MerchantOrders';
import MerchantCustomers from './components/MerchantCustomers';
import CustomersDirectory from './components/CustomersDirectory';
import DiscountOversight from './components/DiscountOversight';
import MerchantNudges from './components/MerchantNudges';
import PlatformTeam from './components/PlatformTeam';
import AnnouncementManagement from './components/AnnouncementManagement';
import ChangelogManagement from './components/ChangelogManagement';
import AccountDeletionManagement from './components/AccountDeletionManagement';
import { ToastProvider, useToast } from './components/ui/Toast';
import Slide from './components/ui/Slide';
import NotificationCenter from './components/NotificationCenter';
import CommunityManagement from './components/CommunityManagement';

const api = (path, opts={}) => {
  // ALWAYS use proxy - ignore REACT_APP_BACKEND_URL to avoid CORS issues
  const base = '/api';
  const headers = { ...(opts.headers||{}) };

  // Debug logging for development
  if (process.env.NODE_ENV === 'development') {
    console.log('API call:', base + path, 'Base URL:', base);
  }

  // Tokens are now handled via HttpOnly cookies - no client-side access needed
  return fetch(base + path, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    }
  });
};

// API function for unauthenticated requests (like invitation acceptance)
const apiPublic = (path, opts={}) => {
  const base = (process.env.REACT_APP_BACKEND_URL || '/api');
  const headers = { ...(opts.headers||{}) };
  return fetch(base + path, { 
    ...opts, 
    headers: {
      'Content-Type': 'application/json',
      ...headers
    }
  });
};

// Permission hook for RBAC
const usePermissions = () => {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const res = await api('/admin/rbac/permissions');
      if (res.ok) {
        const data = await res.json();
        // Handle both direct and wrapped response formats
        setPermissions(data.data || data);
      } else {
        setPermissions(null);
      }
    } catch (e) {
      console.error('Failed to load permissions:', e);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  };

  // Load permissions when hook is first used
  useEffect(() => {
    loadPermissions();
  }, []);

  const hasPermission = (permission) => {
    if (!permissions) return false;
    // Check userPermissions (not all available permissions)
    const userPerms = permissions.userPermissions || [];
    // Admin has all permissions with wildcard
    if (userPerms.includes('*')) return true;
    return userPerms.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (!permissions) return false;
    const userPerms = permissions.userPermissions || [];
    // Admin has all permissions with wildcard
    if (userPerms.includes('*')) return true;
    return permissionList.some(permission => userPerms.includes(permission));
  };

  const hasAllPermissions = (permissionList) => {
    if (!permissions) return false;
    const userPerms = permissions.userPermissions || [];
    // Admin has all permissions with wildcard
    if (userPerms.includes('*')) return true;
    return permissionList.every(permission => userPerms.includes(permission));
  };

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    role: permissions?.role,
    loadPermissions
  };
};

// Access Control Context
const AccessControlContext = createContext();

// Access Control Provider
function AccessControlProvider({ children }) {
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');

  // Global function to show access denied popup
  // Memoized to maintain stable reference for apiWithAccessControl
  const showAccessDeniedPopup = useCallback((message) => {
    setAccessDeniedMessage(message);
    setShowAccessDenied(true);
  }, []);

  // Enhanced API function that handles 403 errors globally
  // Memoized to prevent unnecessary re-renders and useEffect re-runs
  const apiWithAccessControl = useCallback(async (path, opts = {}) => {
    try {
      const response = await api(path, opts);

      // Check for 403 Forbidden (access denied)
      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || 'You do not have permission to access this feature.';
        showAccessDeniedPopup(errorMessage);
        return response;
      }

      return response;
    } catch (error) {
      // Only log API errors in development mode
      if (process.env.NODE_ENV === 'development') {
        console.error('API Error:', error);
      }
      throw error;
    }
  }, [showAccessDeniedPopup]);

  return (
    <AccessControlContext.Provider value={{ showAccessDeniedPopup, apiWithAccessControl }}>
      {children}
      <AccessDeniedPopup 
        show={showAccessDenied} 
        onClose={() => setShowAccessDenied(false)}
        message={accessDeniedMessage}
      />
    </AccessControlContext.Provider>
  );
}

// Hook to use access control
export const useAccessControl = () => {
  const context = useContext(AccessControlContext);
  if (!context) {
    throw new Error('useAccessControl must be used within an AccessControlProvider');
  }
  return context;
};

// Access Denied Popup Component
function AccessDeniedPopup({ show, onClose, message = "You don't have permission to access this feature. Please contact your administrator if you believe this is an error." }) {
  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: '280px', // Start after sidebar (sidebar is 280px wide)
      right: 0,
      bottom: 0,
      background: 'rgba(255, 255, 255, 0.2)', // 0.2 opacity white overlay
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      backdropFilter: 'blur(2px)'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '48px',
        textAlign: 'center',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        maxWidth: '500px',
        width: '90%',
        height: '35vh', // 35% of viewport height
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          fontSize: '80px',
          marginBottom: '24px',
          lineHeight: '1'
        }}>
          🛑
        </div>
        <h2 style={{
          fontSize: '32px',
          fontWeight: '800',
          color: '#dc2626',
          margin: '0 0 16px 0',
          letterSpacing: '-0.025em'
        }}>
          Insufficient Permission
        </h2>
        <p style={{
          fontSize: '18px',
          color: '#6b7280',
          margin: '0 0 32px 0',
          lineHeight: '1.6',
          maxWidth: '400px'
        }}>
          {message}
        </p>
        <button
          onClick={onClose}
          style={{
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '16px 32px',
            fontSize: '18px',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 14px 0 rgba(220, 38, 38, 0.9)'
          }}
          onMouseOver={(e) => {
            e.target.style.background = '#b91c1c';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px 0 rgba(220, 38, 38, 0.4)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = '#dc2626';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 14px 0 rgba(220, 38, 38, 0.3)';
          }}
        >
          Understood
        </button>
      </div>
    </div>
  );
}

// PermissionGuard - Page-level permission check
// Shows persistent banner if user doesn't have required permissions
function PermissionGuard({ children, requiredPermissions = [], pageName = "this page" }) {
  const { hasPermission, hasAnyPermission, loading } = usePermissions();

  // Wait for permissions to load
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Check if user has required permissions
  const permissionsArray = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
  const hasAccess = permissionsArray.length === 0 || hasAnyPermission(permissionsArray);

  // If no access, show persistent banner (no close button)
  if (!hasAccess) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        background: '#f9fafb'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '48px',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '100%',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            fontSize: '64px',
            marginBottom: '24px',
            lineHeight: '1'
          }}>
            🛑
          </div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#dc2626',
            margin: '0 0 16px 0'
          }}>
            Access Denied
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            margin: '0',
            lineHeight: '1.6'
          }}>
            You don't have permission to access {pageName}. Please contact your administrator if you believe this is an error.
          </p>
          <div style={{
            marginTop: '24px',
            padding: '12px',
            background: '#fef2f2',
            borderRadius: '8px',
            border: '1px solid #fecaca'
          }}>
            <p style={{
              fontSize: '14px',
              color: '#991b1b',
              margin: 0,
              fontWeight: '500'
            }}>
              Required: {permissionsArray.join(' or ')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // User has access, render the page
  return children;
}

function AdminHeader({ toggleSidebar, toggleMobileMenu, sidebarCollapsed }) {
  // Clock and system health pill were removed from the header — both were
  // cosmetic (clock added zero value; system pill polled /admin/health
  // every 30s from every open admin tab, driving Vercel edge traffic).
  const [onlineStatus, setOnlineStatus] = useState('online');
  const [staffOnlineStatus, setStaffOnlineStatus] = useState([]);
  const { apiWithAccessControl } = useAccessControl();
  const { hasPermission } = usePermissions();

  // Monitor online status and idle detection
  useEffect(() => {
    let idleTimer;
    const IDLE_TIME = 5 * 60 * 1000; // 5 minutes

    const handleOnline = () => setOnlineStatus('online');
    const handleOffline = () => setOnlineStatus('offline');
    
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      if (navigator.onLine) {
        setOnlineStatus('online');
        idleTimer = setTimeout(() => {
          setOnlineStatus('idle');
        }, IDLE_TIME);
      }
    };

    // Event listeners for activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, resetIdleTimer, true);
    });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check if initially online
    setOnlineStatus(navigator.onLine ? 'online' : 'offline');
    resetIdleTimer();
    
    return () => {
      clearTimeout(idleTimer);
      events.forEach(event => {
        document.removeEventListener(event, resetIdleTimer, true);
      });
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Single heartbeat that writes own status AND fetches the staff roster
  // in one roundtrip. Previously this was two calls per tick
  // (updateStaffStatus + fetchStaffStatus) — cut in half to reduce Vercel
  // edge traffic without losing any UI signal.
  const sendHeartbeat = async (status) => {
    try {
      const response = await apiWithAccessControl('/admin/staff-status/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          currentPage: window.location.pathname,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setStaffOnlineStatus(data.staff || []);
      }
    } catch (error) {
      // Silent — this is background polling, no need to surface transient failures
    }
  };

  // Push status changes immediately (e.g. going idle from user inactivity)
  useEffect(() => {
    sendHeartbeat(onlineStatus);
  }, [onlineStatus]);

  // Periodic heartbeat. Gated on document visibility — no need to poll
  // when the tab is backgrounded (admin isn't looking; still counts as
  // Vercel edge traffic if we fire). Sending a final 'idle' heartbeat
  // on hide so the server knows to stop showing us as "online".
  useEffect(() => {
    sendHeartbeat(onlineStatus); // initial

    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => sendHeartbeat(onlineStatus), 60000);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };

    if (!document.hidden) start();

    const onVisibility = () => {
      if (document.hidden) {
        stop();
        sendHeartbeat('idle'); // final beat so we appear idle server-side
      } else {
        sendHeartbeat('online'); // immediate refresh on tab return
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [onlineStatus]);

  // (Removed the duplicate "initial status" useEffect + its extra 1s
  // delayed retry — the heartbeat useEffect above already sends the
  // initial beat on mount, and its onVisibility handler covers tab
  // return. That block was firing 2 extra requests per admin session.)

  // Cleanup on page unload — best-effort offline signal.
  useEffect(() => {
    const handleBeforeUnload = () => {
      // beforeunload is not a great context for async fetch, but we try;
      // the server-side "no heartbeat in 5min → mark offline" fallback
      // in /staff-status/dashboard covers us if this doesn't complete.
      sendHeartbeat('offline');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#16a34a';
      case 'idle': return '#f59e0b';
      case 'offline': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'idle': return 'Idle';
      case 'offline': return 'Offline';
      default: return 'Unknown';
    }
  };

  return (
    <div className="admin-header" style={{
      position: 'sticky',
      top: 0,
      zIndex: 100,
      backgroundColor: 'white',
      borderBottom: '1px solid var(--border)',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      padding: '12px 24px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '100%'
      }}>
        {/* Left side - Menu Toggles + Date and Time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle mobile-only"
            onClick={toggleMobileMenu}
            title="Open menu"
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              color: 'var(--brand)',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <Menu size={24} />
          </button>

          {/* Desktop Sidebar Expand Toggle (only shown when collapsed) */}
          {sidebarCollapsed && (
            <button
              className="desktop-sidebar-expand desktop-only"
              onClick={toggleSidebar}
              title="Expand sidebar"
              style={{
                background: 'none',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                color: 'var(--brand)',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
            >
              <ChevronRightIcon size={20} />
            </button>
          )}

          {/* Clock + System Health pill removed from header — see AdminHeader
              state comment. If you need system health, use the dedicated
              /health page which polls only while open. */}
        </div>

        {/* Right side - Online Status and Staff */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {/* Staff Online Status */}
          {staffOnlineStatus.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {staffOnlineStatus.slice(0, 3).map((staff, index) => (
                  <div
                    key={staff.id}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(staff.status),
                      animation: staff.status === 'online' ? 'pulse 2s infinite' : 'none',
                      border: '1px solid white',
                      marginLeft: index > 0 ? '-4px' : '0'
                    }}
                    title={`${staff.name} - ${getStatusText(staff.status)}`}
                  />
                ))}
                {staffOnlineStatus.length > 3 && (
                  <span style={{ 
                    fontSize: '12px', 
                    color: 'var(--text-muted)',
                    marginLeft: '4px'
                  }}>
                    +{staffOnlineStatus.length - 3}
                  </span>
                )}
              </div>
              <span style={{ 
                fontSize: '12px', 
                color: 'var(--text-muted)',
                fontWeight: '500'
              }}>
                {staffOnlineStatus.filter(s => s.status === 'online').length} online
              </span>
            </div>
          )}

          {/* Notification Center */}
          <NotificationCenter api={api} />

          {/* Current User Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className={`status-indicator ${onlineStatus}`} style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: getStatusColor(onlineStatus),
              animation: onlineStatus === 'online' ? 'pulse 2s infinite' : 'none'
            }}></div>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: getStatusColor(onlineStatus)
            }}>
              {getStatusText(onlineStatus)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Shell({ children }){
  const { showAccessDeniedPopup } = useAccessControl();

  // Function to handle navigation with permission check
  const handleNavClick = (e, requiredPermission) => {
    // For now, we'll let the backend handle the permission check
    // and show the popup when the API call fails
    // In a more advanced implementation, we could check permissions client-side first
  };

  // User state management
  const [currentUser, setCurrentUser] = React.useState(null);
  const [userLoading, setUserLoading] = React.useState(true);

  // Fetch current user information
  React.useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api('/admin-auth/me');
        if (response.ok) {
          const data = await response.json();
          setCurrentUser(data.user);
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      } finally {
        setUserLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  // Sidebar state management
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);

  const [expandedSections, setExpandedSections] = React.useState({
    merchants: true,
    engagement: true,
    content: true,
    financial: true,
    platform: true,
    communication: true,
    security: true,
    ai: true,
    system: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close mobile menu on route change
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [children]);

  // Logout handler
  const handleLogout = async () => {
    try {
      // Try new public logout first
      await api('/admin-auth/logout', { method: 'POST' });
      // Also attempt legacy session logout (best-effort)
      await api('/admin/session/logout', { method: 'POST' });
      // Clear any sensitive data from localStorage
      localStorage.clear();
      sessionStorage.clear();
      // Force redirect to login
      window.location.replace('/login');
    } catch (e) {
      console.error('Logout error:', e);
      // Even if logout fails, redirect to login
      window.location.replace('/login');
    }
  };

  return (
    <div className="admin-shell">
      <AdminHeader toggleSidebar={toggleSidebar} toggleMobileMenu={toggleMobileMenu} sidebarCollapsed={sidebarCollapsed} />

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="mobile-menu-overlay" onClick={toggleMobileMenu} />}

      <div style={{ display: 'flex', flex: 1 }}>
        <aside className={`admin-aside ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Sidebar Header with Close Button */}
          <div className="sidebar-header">
            <h3>{!sidebarCollapsed && 'Revstar'}</h3>
            {!sidebarCollapsed && (
              <button className="sidebar-toggle-btn desktop-only" onClick={toggleSidebar} title="Collapse sidebar">
                <ChevronLeft size={20} />
              </button>
            )}
            <button className="sidebar-close-btn mobile-only" onClick={toggleMobileMenu} title="Close menu">
              <CloseIcon size={24} />
            </button>
          </div>
        <nav className="admin-nav">
          <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/">
            <Home size={16} />
            <span>Dashboard</span>
          </NavLink>

          <div className="nav-section">
            <div className="group-collapsible" onClick={() => toggleSection('merchants')}>
              <span>Merchant Management</span>
              <span className="toggle-icon">{expandedSections.merchants ? '−' : '+'}</span>
            </div>
            {expandedSections.merchants && (
              <>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/merchants">
                  <Store size={16} />
                  <span>Merchants</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/currency-management">
                  <Globe size={16} />
                  <span>Currency Management</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/marketing">
                  <Mail size={16} />
                  <span>Marketing & Automation</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/discounts">
                  <Gift size={16} />
                  <span>Discounts</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/announcements">
                  <Megaphone size={16} />
                  <span>Announcements</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/changelog">
                  <Sparkles size={16} />
                  <span>What's New</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/merchant-engagement">
                  <Bell size={16} />
                  <span>Merchant Engagement</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/waitlist">
                  <Clock size={16} />
                  <span>Waitlist</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/website-leads">
                  <Globe size={16} />
                  <span>Website Leads</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/customers">
                  <Users size={16} />
                  <span>Customers</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/team-members">
                  <User size={16} />
                  <span>Team Members</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/account-deletion-requests">
                  <Trash2 size={16} />
                  <span>Account Deletions</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/products">
                  <ShoppingBag size={16} />
                  <span>Products</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/affiliates-oversight">
                  <Users size={16} />
                  <span>Affiliate Oversight</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/platform-referrals">
                  <Gift size={16} />
                  <span>Platform Referrals</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/merchant-activity">
                  <Activity size={16} />
                  <span>Merchant Activity</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/themes">
                  <LayoutTemplate size={16} />
                  <span>Theme Management</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/community">
                  <Users size={16} />
                  <span>Community Hub</span>
                </NavLink>
              </>
            )}
          </div>

          <div className="nav-section">
            <div className="group-collapsible" onClick={() => toggleSection('engagement')}>
              <span>Engagement</span>
              <span className="toggle-icon">{expandedSections.engagement ? '−' : '+'}</span>
            </div>
            {expandedSections.engagement && (
              <>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/engagement/livechat">
                  <MessageCircle size={16} />
                  <span>Livechat</span>
                </NavLink>
              </>
            )}
          </div>

          <div className="nav-section">
            <div className="group-collapsible" onClick={() => toggleSection('content')}>
              <span>Content</span>
              <span className="toggle-icon">{expandedSections.content ? '−' : '+'}</span>
            </div>
            {expandedSections.content && (
              <>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/content/knowledgebase">
                  <BookOpen size={16} />
                  <span>Knowledge Base</span>
                </NavLink>
              </>
            )}
          </div>

          <div className="nav-section">
            <div className="group-collapsible" onClick={() => toggleSection('financial')}>
              <span>Financial Controls</span>
              <span className="toggle-icon">{expandedSections.financial ? '−' : '+'}</span>
            </div>
            {expandedSections.financial && (
              <>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/finance/service-charge">
                  <Settings size={16} />
                  <span>Service Charges</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/finance/vat">
                  <Percent size={16} />
                  <span>Central VAT</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/disputes">
                  <AlertCircle size={16} />
                  <span>Disputes</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/treasury">
                  <Wallet size={16} />
                  <span>Treasury</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/settlements">
                  <DollarSign size={16} />
                  <span>Settlements</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/platform-revenue">
                  <DollarSign size={16} />
                  <span>Platform Revenue</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/merchant-gateways">
                  <CreditCard size={16} />
                  <span>Merchant Gateways</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/refunds">
                  <ReceiptText size={16} />
                  <span>Refunds</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/pos">
                  <Store size={16} />
                  <span>POS Oversight</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/pos-settlement">
                  <Wallet size={16} />
                  <span>POS Settlement</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/group-deal-settlements">
                  <Package size={16} />
                  <span>Group Deal Settlements</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/appointments">
                  <Calendar size={16} />
                  <span>Appointments</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/payments">
                  <CreditCard size={16} />
                  <span>Payments</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/wallets">
                  <Wallet size={16} />
                  <span>Merchant Wallets</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/shipping">
                  <Truck size={16} />
                  <span>Shipping</span>
                </NavLink>
                {/* Legacy /chowdeck route retained as an emergency
                    fallback but no longer in the sidebar — the unified
                    Shipping page covers merchant + shipment monitoring
                    for both providers. Delete the route + component in
                    a follow-up sweep once no support ticket references
                    it for 30 days. */}
              </>
            )}
          </div>

          <div className="nav-section">
            <div className="group-collapsible" onClick={() => toggleSection('platform')}>
              <span>Platform Management</span>
              <span className="toggle-icon">{expandedSections.platform ? '−' : '+'}</span>
            </div>
            {expandedSections.platform && (
              <>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/features">
                  <Settings size={16} />
                  <span>Feature Management</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/tiers">
                  <DollarSign size={16} />
                  <span>Tier Management</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/tier-limits">
                  <Layers size={16} />
                  <span>Tier Limits</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/ai-limits">
                  <Zap size={16} />
                  <span>AI Limits</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/ai-providers">
                  <Cpu size={16} />
                  <span>AI Providers</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/locations">
                  <MapPin size={16} />
                  <span>Locations</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/subscriptions">
                  <CreditCard size={16} />
                  <span>Subscriptions</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/storefront-ads">
                  <Megaphone size={16} />
                  <span>Storefront Ads</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/marketplace">
                  <Globe2 size={16} />
                  <span>Marketplace</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/currency">
                  <Globe size={16} />
                  <span>Currency Management</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/custom-domains">
                  <Globe size={16} />
                  <span>Custom Domains</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/analytics-config">
                  <BarChart3 size={16} />
                  <span>Analytics Configuration</span>
                </NavLink>
              </>
            )}
          </div>

          <div className="nav-section">
            <div className="group-collapsible" onClick={() => toggleSection('communication')}>
              <span>Communication</span>
              <span className="toggle-icon">{expandedSections.communication ? '−' : '+'}</span>
            </div>
            {expandedSections.communication && (
              <>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/whatsapp">
                  <MessageCircle size={16} />
                  <span>WhatsApp</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/email">
                  <Mail size={16} />
                  <span>Email</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/google-calendar">
                  <Calendar size={16} />
                  <span>Google Calendar OAuth</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/sms">
                  <Phone size={16} />
                  <span>SMS</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/push-notifications">
                  <Bell size={16} />
                  <span>Push Notifications</span>
                </NavLink>
              </>
            )}
          </div>

          <div className="nav-section">
            <div className="group-collapsible" onClick={() => toggleSection('security')}>
              <span>Security & Access</span>
              <span className="toggle-icon">{expandedSections.security ? '−' : '+'}</span>
            </div>
            {expandedSections.security && (
              <>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/kyc">
                  <Shield size={16} />
                  <span>KYC Management</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/dva">
                  <Wallet size={16} />
                  <span>Payment Rails</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/rbac">
                  <Shield size={16} />
                  <span>RBAC</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/platform-team">
                  <Users size={16} />
                  <span>Platform Team</span>
                </NavLink>
              </>
            )}
          </div>

          <div className="nav-section">
            <div className="group-collapsible" onClick={() => toggleSection('ai')}>
              <span>AI & Analytics</span>
              <span className="toggle-icon">{expandedSections.ai ? '−' : '+'}</span>
            </div>
            {expandedSections.ai && (
              <>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/analytics">
                  <TrendingUp size={16} />
                  <span>Analytics</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/insights">
                  <BarChart3 size={16} />
                  <span>Platform Insights</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/ai-insights">
                  <Zap size={16} />
                  <span>AI Insights</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/ai-assistant">
                  <MessageSquare size={16} />
                  <span>AI Assistant</span>
                </NavLink>
              </>
            )}
          </div>

          <div className="nav-section">
            <div className="group-collapsible" onClick={() => toggleSection('system')}>
              <span>System</span>
              <span className="toggle-icon">{expandedSections.system ? '−' : '+'}</span>
            </div>
            {expandedSections.system && (
              <>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/health">
                  <Activity size={16} />
                  <span>System Health</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/logs">
                  <FileText size={16} />
                  <span>Audit Logs</span>
                </NavLink>
                <NavLink className={({isActive})=>`admin-link ${isActive?'is-active':''}`} to="/staff-activity">
                  <Users size={16} />
                  <span>Staff Activity</span>
                </NavLink>
              </>
            )}
          </div>
        </nav>
        
        {/* Modern User Profile Section with Dropdown */}
        <div className="user-menu-container">
          <button
            className="user-menu-trigger"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className="user-avatar">
              <Shield size={18} color="#0f766e" />
            </div>
            {!sidebarCollapsed && (
              <>
                <div className="user-info">
                  <div className="user-name">
                    {userLoading ? 'Loading...' : (currentUser?.username || currentUser?.email?.split('@')[0] || 'Admin User')}
                  </div>
                  <div className="user-email">
                    {userLoading ? '...' : (currentUser?.email || 'admin@revstar.com')}
                  </div>
                </div>
                <ChevronDown size={16} className={`chevron-icon ${userMenuOpen ? 'open' : ''}`} />
              </>
            )}
          </button>

          {/* Dropdown Menu */}
          {userMenuOpen && (
            <div className="user-dropdown-menu">
              <NavLink to="/profile" className="user-menu-item" onClick={() => setUserMenuOpen(false)}>
                <Users size={16} />
                <span>User Profile</span>
              </NavLink>
              <NavLink to="/settings" className="user-menu-item" onClick={() => setUserMenuOpen(false)}>
                <Settings size={16} />
                <span>Settings</span>
              </NavLink>
              <div className="user-menu-divider" />
              <button className="user-menu-item logout" onClick={handleLogout}>
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
        </aside>
        <main className="admin-main">{children}</main>
      </div>
    </div>
  );
}

function Login(){
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  const navigate = useNavigate();
  
  // If session already valid, redirect away from login
  useEffect(()=>{ 
    (async()=>{ 
      try{ 
        const r=await api('/admin/rbac/permissions'); 
        
        // Debug logging for development
        if (process.env.NODE_ENV === 'development') {
          console.log('Login page session check:', r.status, r.ok);
        }
        
        if(r.ok) navigate('/', { replace:true }); 
      }catch(e){ 
        // Silently fail - user needs to login
        if (process.env.NODE_ENV === 'development') {
          console.log('No valid session, user needs to login:', e);
        }
      } 
    })(); 
  },[navigate]);
  
  const handleLogin = async () => {
    try{
      setStatus('auth'); setError('');
      const r = await api('/admin-auth/login', { 
        method:'POST', 
        body: JSON.stringify({ email, password }) 
      });
      const d = await r.json();
      
      // Debug logging for development
      if (process.env.NODE_ENV === 'development') {
        console.log('Login response:', r.status, r.ok, d);
      }
      
      if(!r.ok){ 
        setError(d.error || 'Invalid credentials'); 
        setStatus('idle'); 
        return; 
      }
      
      // Check if MFA is required
      if(d.requiresMfa) {
        setShowMfa(true);
        setStatus('mfa');
        return;
      }
      
      // Direct session establishment
      await establishSession();
      
    }catch(e){ 
      // Debug logging for development
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', e);
      }
      
      setError('Network error'); 
      setStatus('idle'); 
    }
  };
  
  const handleMfa = async () => {
    try{
      setStatus('mfa-verify');
      const r = await api('/admin-auth/verify-mfa', { 
        method:'POST', 
        body: JSON.stringify({ email, mfaCode }) 
      });
      const d = await r.json();
      
      if(!r.ok){ 
        setError(d.error || 'Invalid MFA code'); 
        setStatus('mfa'); 
        return; 
      }
      
      await establishSession();
      
    }catch(e){ 
      setError('Network error'); 
      setStatus('mfa'); 
    }
  };
  
  const establishSession = async () => {
    try{
      setStatus('session');
      // Check if we have a valid session by calling permissions endpoint
      const resp = await api('/admin/rbac/permissions');
      const data = await resp.json();
      
      // Debug logging for development
      if (process.env.NODE_ENV === 'development') {
        console.log('Establish session response:', resp.status, resp.ok, data);
      }
      
      if(!resp.ok){ 
        setError(data.error || 'Session failed'); 
        setStatus('idle'); 
        return; 
      }
      
      navigate('/');
    }catch(e){ 
      // Debug logging for development
      if (process.env.NODE_ENV === 'development') {
        console.error('Establish session error:', e);
      }
      
      setError('Network error'); 
    } finally {
      setStatus('idle');
    }
  };
  
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', background: 'var(--bg)' }}>
      <div className="card" style={{ width: 400, maxWidth: '90vw' }}>
        <div className="card-body">
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h2 className="admin-h1" style={{ fontSize: 24, marginBottom: 8 }}>Admin Console</h2>
            <p className="muted">Secure access to Revstar administration</p>
          </div>
          
          {error && (
            <div style={{ 
              color: '#dc2626', 
              marginBottom: 16, 
              padding: 12, 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: 8,
              fontSize: 14
            }}>
              {error}
            </div>
          )}
          
          {!showMfa ? (
            <div style={{ display:'grid', gap:16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Email</label>
                <input 
                  className="input" 
                  placeholder="Enter your email address" 
                  type="email" 
                  value={email} 
                  onChange={e=>setEmail(e.target.value)}
                  disabled={status !== 'idle'}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Password</label>
                <input 
                  className="input" 
                  placeholder="Enter your password" 
                  type="password" 
                  value={password} 
                  onChange={e=>setPassword(e.target.value)}
                  disabled={status !== 'idle'}
                />
              </div>
              <button 
                className="btn btn-primary" 
                onClick={handleLogin} 
                disabled={status !== 'idle' || !email || !password}
                style={{ width: '100%', padding: 12 }}
              >
                {status === 'auth' ? 'Authenticating...' : 'Sign In'}
              </button>
              
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6B7280',
                    fontSize: '14px',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: '8px'
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display:'grid', gap:16 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
                <h3 style={{ marginBottom: 8 }}>Two-Factor Authentication</h3>
                <p className="muted">Enter the 6-digit code from your authenticator app</p>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>MFA Code</label>
                <input 
                  className="input" 
                  placeholder="123456" 
                  type="text" 
                  value={mfaCode} 
                  onChange={e=>setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={status === 'mfa-verify'}
                  style={{ textAlign: 'center', fontSize: 18, letterSpacing: 2 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button 
                  className="btn" 
                  onClick={() => { setShowMfa(false); setMfaCode(''); setError(''); setStatus('idle'); }}
                  disabled={status === 'mfa-verify'}
                  style={{ flex: 1 }}
                >
                  Back
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleMfa} 
                  disabled={status === 'mfa-verify' || mfaCode.length !== 6}
                  style={{ flex: 2 }}
                >
                  {status === 'mfa-verify' ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </div>
          )}
          
          <div style={{ marginTop: 24, padding: 16, background: '#f8fafc', borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Security Notice</div>
            <div className="muted">
              This is a secure admin console. All actions are logged and monitored. 
              Use strong passwords and enable 2FA for enhanced security.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequireAdmin({ children }){
  const [ok, setOk] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  
  useEffect(()=>{
    let mounted = true;
    (async()=>{
      try{
        // Check if we have a valid admin session
        const resp = await api('/admin/rbac/permissions');
        if(!mounted) return;
        
        // Debug logging for development
        if (process.env.NODE_ENV === 'development') {
          console.log('Session validation response:', resp.status, resp.ok);
        }
        
        setOk(resp.ok);
      }catch(e){ 
        // Debug logging for development
        if (process.env.NODE_ENV === 'development') {
          console.error('Session validation error:', e);
        }
        
        if(mounted) setOk(false); 
      } finally {
        if(mounted) setLoading(false);
      }
    })();
    
    // Keepalive ping every 4 minutes to maintain session.
    // Gated on tab visibility — no point paying Vercel edge to ping
    // /admin/rbac/permissions on a backgrounded tab that no one's using.
    // When the tab returns, ping immediately (session may have expired
    // while hidden) then resume the schedule.
    let id = null;
    const keepalivePing = async () => {
      try {
        const resp = await api('/admin/rbac/permissions');
        if (process.env.NODE_ENV === 'development') {
          console.log('Keepalive ping response:', resp.status, resp.ok);
        }
        if (!resp.ok && mounted) {
          console.log('Session expired during keepalive ping');
          setOk(false);
        }
      } catch (e) {
        console.error('Keepalive ping error:', e);
        if (mounted) setOk(false);
      }
    };
    const startKeepalive = () => {
      if (id) return;
      id = setInterval(keepalivePing, 4 * 60 * 1000);
    };
    const stopKeepalive = () => {
      if (id) { clearInterval(id); id = null; }
    };
    if (!document.hidden) startKeepalive();
    const onKeepaliveVis = () => {
      if (document.hidden) {
        stopKeepalive();
      } else {
        keepalivePing();
        startKeepalive();
      }
    };
    document.addEventListener('visibilitychange', onKeepaliveVis);

    return ()=>{
      mounted=false;
      stopKeepalive();
      document.removeEventListener('visibilitychange', onKeepaliveVis);
    };
  },[]);
  
  // If we're on the login page, don't check permissions
  if(location.pathname === '/login') {
    return children;
  }
  
  if(loading) return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      background: 'var(--bg)'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ 
          width: 40, 
          height: 40, 
          border: '3px solid var(--border)', 
          borderTop: '3px solid var(--brand)', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        <div className="muted">Verifying admin session...</div>
      </div>
    </div>
  );
  
  return ok ? children : <Navigate to="/login" replace />;
}

function OldDashboard(){
  const { apiWithAccessControl } = useAccessControl();
  const [data, setData] = useState({
    revenue: null,
    merchants: null,
    health: null,
    analytics: null,
    revenueChart: null
  });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setErr('');
        setLoading(true);
        
        // Load all dashboard data in parallel
        const [revenueRes, merchantsRes, healthRes, analyticsRes, revenueChartRes, userRes] = await Promise.allSettled([
          apiWithAccessControl('/admin/finance/revenue/summary'),
          apiWithAccessControl('/admin/merchants'),
          apiWithAccessControl('/admin/health'),
          apiWithAccessControl('/admin/analytics/platform'),
          apiWithAccessControl('/admin/analytics/revenue-chart'),
          apiWithAccessControl('/admin/rbac/permissions')
        ]);

        const newData = {};
        
        if (revenueRes.status === 'fulfilled' && revenueRes.value.ok) {
          newData.revenue = await revenueRes.value.json();
        }
        
        if (merchantsRes.status === 'fulfilled' && merchantsRes.value.ok) {
          newData.merchants = await merchantsRes.value.json();
        }
        
        if (healthRes.status === 'fulfilled' && healthRes.value.ok) {
          newData.health = await healthRes.value.json();
        }

        if (analyticsRes.status === 'fulfilled' && analyticsRes.value.ok) {
          newData.analytics = await analyticsRes.value.json();
        }

        if (revenueChartRes.status === 'fulfilled' && revenueChartRes.value.ok) {
          newData.revenueChart = await revenueChartRes.value.json();
        }

        if (userRes.status === 'fulfilled' && userRes.value.ok) {
          const userData = await userRes.value.json();
          setUserInfo(userData);
        }

        setData(newData);
      } catch (e) {
        console.error('Dashboard load error:', e);
        setErr('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh',
        background: 'var(--bg)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: 40, 
            height: 40, 
            border: '3px solid var(--border)', 
            borderTop: '3px solid var(--brand)', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div className="muted">Loading dashboard data…</div>
        </div>
      </div>
    );
  }

  const totalGmv = data.revenue ? data.revenue.gmv || 0 : 0;
  const serviceChargeIncome = data.revenue ? data.revenue.serviceChargeIncome || 0 : 0;
  const netPayouts = data.revenue ? data.revenue.netPayouts || 0 : 0;
  const activeMerchants = data.merchants ? data.merchants.merchants?.length || 0 : 0;
  const approvedMerchants = data.merchants ? (data.merchants.merchants?.filter(m => m.applicationStatus === 'approved')?.length || 0) : 0;
  const pendingApplications = data.merchants ? (data.merchants.merchants?.filter(m => m.applicationStatus === 'pending')?.length || 0) : 0;
  const suspendedStores = data.merchants ? (data.merchants.merchants?.filter(m => m.isSuspended)?.length || 0) : 0;

  // Revenue chart data from backend
  const revenueData = data.revenueChart?.chartData || [];

  const merchantStatusData = [
    { name: 'Approved', value: approvedMerchants, color: 'var(--success-color)' },
    { name: 'Pending', value: pendingApplications, color: 'var(--warning-color)' },
    { name: 'Suspended', value: suspendedStores, color: 'var(--error-color)' }
  ];

  return (
    <div>
      {/* Header */}
      <div className="dashboard-header">
        <h1>Welcome back, {userInfo?.user?.username || 'Admin'}! Here's what's happening with your platform today.</h1>
        <p>Monitor your merchants, track revenue, and manage the Revstar ecosystem.</p>
      </div>

      {err && (
        <div style={{ 
          color: '#dc2626', 
          marginBottom: 24, 
          padding: 16, 
          background: '#fef2f2', 
          border: '1px solid #fecaca', 
          borderRadius: 12,
          fontSize: 14
        }}>
          {err}
        </div>
      )}
      
      {/* Key Metrics Cards */}
      <div className="cards-4">
        <div className="stat-card">
          <div className="icon">
            <DollarSign size={24} color="var(--brand)" />
          </div>
          <div className="label">Total GMV</div>
          <div className="value">₦{Intl.NumberFormat('en-NG').format(Math.round(totalGmv))}</div>
          <div className="change positive">
            <ArrowUpRight size={14} />
            <span>+12%</span>
            <span>from last month</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="icon">
            <Building size={24} color="var(--brand)" />
          </div>
          <div className="label">Service Charge Income</div>
          <div className="value">₦{Intl.NumberFormat('en-NG').format(Math.round(serviceChargeIncome))}</div>
          <div className="change positive">
            <ArrowUpRight size={14} />
            <span>+8%</span>
            <span>platform earnings</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="icon">
            <CreditCard size={24} color="var(--brand)" />
          </div>
          <div className="label">Net Payouts</div>
          <div className="value">₦{Intl.NumberFormat('en-NG').format(Math.round(netPayouts))}</div>
          <div className="change positive">
            <ArrowUpRight size={14} />
            <span>+15%</span>
            <span>to merchants</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="icon">
            <Store size={24} color="var(--brand)" />
          </div>
          <div className="label">Active Merchants</div>
          <div className="value">{activeMerchants}</div>
          <div className="change positive">
            <ArrowUpRight size={14} />
            <span>+3</span>
            <span>this week</span>
          </div>
        </div>
      </div>

      {/* Charts and Analytics */}
      <div className="two-col">
        <div className="section">
          <div className="section-header">
            Revenue Growth
            <select style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px' }}>
              <option>Last 6 months</option>
              <option>Last year</option>
              <option>All time</option>
            </select>
          </div>
          <div className="section-body">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="month" stroke="var(--text-muted)" fontSize={12} />
                <YAxis stroke="var(--text-muted)" fontSize={12} />
                <Tooltip 
                  formatter={(value) => [`₦${Intl.NumberFormat('en-NG').format(value)}`, 'Revenue']}
                  labelStyle={{ color: 'var(--text)' }}
                  contentStyle={{ 
                    backgroundColor: 'var(--panel)', 
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="var(--brand)" 
                  fill="var(--brand-50)" 
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="section">
          <div className="section-header">
            Merchant Status Overview
            <a className="muted-link" href="#" style={{ fontSize: '14px' }}>View all</a>
          </div>
          <div className="section-body">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={merchantStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {merchantStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [value, 'Merchants']}
                  contentStyle={{ 
                    backgroundColor: 'var(--panel)', 
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Transaction History and Goals */}
      <div className="two-col">
        <div className="section">
          <div className="section-header">
            Recent Transactions
            <a className="muted-link" href="#" style={{ fontSize: '14px' }}>View all</a>
          </div>
          <div className="section-body">
            <div className="transaction-list">
              <div className="transaction-item">
                <div className="transaction-icon">
                  <Store size={16} color="var(--brand)" />
                </div>
                <div className="transaction-details">
                  <div className="transaction-name">Kwika Digits Store</div>
                  <div className="transaction-desc">New merchant application</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>2m ago</div>
                  <div className="transaction-amount credit">+₦0</div>
                </div>
              </div>
              
              <div className="transaction-item">
                <div className="transaction-icon">
                  <DollarSign size={16} color="var(--success-color)" />
                </div>
                <div className="transaction-details">
                  <div className="transaction-name">Payout Processed</div>
                  <div className="transaction-desc">Settlement to merchant</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>1h ago</div>
                  <div className="transaction-amount debit">-₦15,000</div>
                </div>
              </div>
              
              <div className="transaction-item">
                <div className="transaction-icon">
                  <RotateCcw size={16} color="var(--warning-color)" />
                </div>
                <div className="transaction-details">
                  <div className="transaction-name">Refund Request</div>
                  <div className="transaction-desc">Customer refund pending</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>3h ago</div>
                  <div className="transaction-amount debit">-₦2,500</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="section">
          <div className="section-header">
            Platform Goals
            <a className="muted-link" href="#" style={{ fontSize: '14px' }}>Manage</a>
          </div>
          <div className="section-body">
            <div className="goal-item">
              <div className="goal-header">
                <span className="goal-name">Monthly GMV Target</span>
                <span className="goal-amount">₦{Intl.NumberFormat('en-NG').format(Math.round(totalGmv))} / ₦50M</span>
              </div>
              <div className="goal-progress">
                <div className="goal-progress-bar green" style={{ width: `${Math.min((totalGmv / 50000000) * 100, 100)}%` }}></div>
              </div>
            </div>
            
            <div className="goal-item">
              <div className="goal-header">
                <span className="goal-name">Active Merchants</span>
                <span className="goal-amount">{activeMerchants} / 100</span>
              </div>
              <div className="goal-progress">
                <div className="goal-progress-bar blue" style={{ width: `${Math.min((activeMerchants / 100) * 100, 100)}%` }}></div>
              </div>
            </div>
            
            <div className="goal-item">
              <div className="goal-header">
                <span className="goal-name">Platform Revenue</span>
                <span className="goal-amount">₦{Intl.NumberFormat('en-NG').format(Math.round(serviceChargeIncome))} / ₦5M</span>
              </div>
              <div className="goal-progress">
                <div className="goal-progress-bar orange" style={{ width: `${Math.min((serviceChargeIncome / 5000000) * 100, 100)}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Health(){
  const { apiWithAccessControl } = useAccessControl();
  const [healthData, setHealthData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [networkStats, setNetworkStats] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds

  const downloadLogs = async () => {
    try {
      const response = await apiWithAccessControl('/admin/health/logs/download?type=today');
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `system-logs-${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download logs');
      }
    } catch (error) {
      console.error('Error downloading logs:', error);
    }
  };

  const loadHealthData = async () => {
    try {
      setError('');
      const [healthRes, metricsRes, alertsRes, networkRes, performanceRes, logsRes] = await Promise.all([
        apiWithAccessControl('/admin/health'),
        apiWithAccessControl('/admin/health/metrics'),
        apiWithAccessControl('/admin/health/alerts'),
        apiWithAccessControl('/admin/health/network'),
        apiWithAccessControl('/admin/health/performance'),
        apiWithAccessControl('/admin/health/logs')
      ]);

      if (healthRes.ok) {
        const healthData = await healthRes.json();
        setHealthData(healthData);
      }

      if (metricsRes.ok) {
        const metricsData = await metricsRes.json();
        setMetrics(metricsData.metrics || metricsData);
      }

      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        setAlerts(alertsData.alerts || []);
      }

      if (networkRes.ok) {
        const networkData = await networkRes.json();
        setNetworkStats(networkData.network || networkData);
      }

      if (performanceRes.ok) {
        const performanceData = await performanceRes.json();
        setPerformance(performanceData.performance || performanceData);
      }

      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setLogs(logsData.logs || []);
      }

      setLastUpdate(new Date());
    } catch (e) {
      setError('Failed to load health data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHealthData();
    if (!autoRefresh) return;

    // Visibility-gated refresh — health dashboard doesn't need to poll
    // while the tab is backgrounded (nothing to render into).
    let interval = null;
    const start = () => { if (!interval) interval = setInterval(loadHealthData, refreshInterval); };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    if (!document.hidden) start();

    const onVis = () => {
      if (document.hidden) stop();
      else { loadHealthData(); start(); }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [autoRefresh, refreshInterval]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'critical': return '#ef4444';
      case 'down': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy': return <Check size={20} color="white" />;
      case 'warning': return <AlertCircle size={20} color="white" />;
      case 'critical': return <XCircle size={20} color="white" />;
      case 'down': return <X size={20} color="white" />;
      default: return <Info size={20} color="white" />;
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatPercentage = (value) => {
    const percentage = value * 100;
    // Show 1 decimal place for values less than 1%
    if (percentage > 0 && percentage < 1) {
      return `${percentage.toFixed(1)}%`;
    }
    return `${Math.round(percentage)}%`;
  };

  if (loading && !healthData) {
    return (
      <div className="admin-content">
        <div className="admin-h1">System Health</div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
            <div style={{ marginTop: '16px', color: 'var(--text-muted)' }}>Loading system health data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div className="admin-h1" style={{ margin: 0 }}>System Health</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ margin: 0 }}
            />
            <label htmlFor="autoRefresh" style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
              Auto Refresh
            </label>
          </div>
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            className="input"
            style={{ width: 'auto', minWidth: '120px' }}
            disabled={!autoRefresh}
          >
            <option value={2000}>2s</option>
            <option value={5000}>5s</option>
            <option value={10000}>10s</option>
            <option value={30000}>30s</option>
          </select>
          <button
            onClick={loadHealthData}
            className="btn btn-secondary"
            style={{ padding: '8px 16px' }}
          >
            <RotateCcw size={16} style={{ marginRight: '8px' }} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="card" style={{ borderColor: '#ef4444', marginBottom: '24px' }}>
          <div className="card-body" style={{ color: '#ef4444' }}>
            <AlertCircle size={20} style={{ marginRight: '8px' }} />
            {error}
          </div>
        </div>
      )}

      {lastUpdate && (
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      )}

      {/* System Status Overview */}
      <div className="cards-4" style={{ marginBottom: '24px' }}>
        <div className="stat-card">
          <div className="icon" style={{ background: 'none', border: '2px solid var(--brand)', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={20} color="var(--brand)" />
          </div>
          <div style={{ padding: '8px 0' }}>
            <div className="stat-value">{healthData?.status || 'Unknown'}</div>
            <div className="stat-label">Overall Status</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="icon" style={{ background: 'none', border: '2px solid #3b82f6', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Clock size={20} color="#3b82f6" />
          </div>
          <div style={{ padding: '8px 0' }}>
            <div className="stat-value">{formatUptime(healthData?.uptime || 0)}</div>
            <div className="stat-label">Uptime</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="icon" style={{ background: 'none', border: '2px solid #8b5cf6', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={20} color="#8b5cf6" />
          </div>
          <div style={{ padding: '8px 0' }}>
            <div className="stat-value">{healthData?.responseTime || 0}ms</div>
            <div className="stat-label">Response Time</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="icon" style={{ background: 'none', border: '2px solid #f59e0b', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertTriangle size={20} color="#f59e0b" />
          </div>
          <div style={{ padding: '8px 0' }}>
            <div className="stat-value">{alerts.filter(alert => alert.severity === 'critical').length}</div>
            <div className="stat-label">Critical Alerts</div>
          </div>
        </div>
      </div>

      {/* System Components Status */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
          <h3 style={{ margin: 0 }}>System Components</h3>
        </div>
        <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
          <div style={{ display: 'grid', gap: '16px' }}>
            {healthData?.components?.map((component, index) => {
              const getComponentIcon = (name) => {
                switch (name?.toLowerCase()) {
                  case 'api server': return <Server size={16} />;
                  case 'database': return <Database size={16} />;
                  case 'email service': return <Mail size={16} />;
                  case 'payment gateway': return <CreditCard size={16} />;
                  default: return <Monitor size={16} />;
                }
              };

              return (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '16px',
                  background: 'var(--bg)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '8px', 
                      background: 'none',
                      border: `2px solid ${getStatusColor(component.status)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: getStatusColor(component.status)
                    }}>
                      {getComponentIcon(component.name)}
                    </div>
                    <div style={{ padding: '4px 0' }}>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{component.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{component.description}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '12px', 
                      color: getStatusColor(component.status),
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {component.status}
                    </div>
                    {component.responseTime && (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {component.responseTime}ms
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      {metrics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '24px' }}>
          <div className="card">
            <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Cpu size={16} color="var(--brand)" />
                <h3 style={{ margin: 0 }}>CPU Usage</h3>
              </div>
            </div>
            <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={120}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Used', value: (metrics.cpu?.usage || 0) * 100, fill: '#ef4444' },
                        { name: 'Free', value: (1 - (metrics.cpu?.usage || 0)) * 100, fill: '#f3f4f6' }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={50}
                      paddingAngle={0}
                      dataKey="value"
                    >
                      <Cell key="Used" fill="#ef4444" />
                      <Cell key="Free" fill="#f3f4f6" />
                    </Pie>
                    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '16px', fontWeight: '600' }}>
                      {formatPercentage(metrics.cpu?.usage || 0)}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                  <span>0%</span>
                  <span style={{ fontWeight: '600' }}>{formatPercentage(metrics.cpu?.usage || 0)}</span>
                  <span>100%</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', textAlign: 'center' }}>
                  {metrics.cpu?.cores || 0} cores
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <HardDrive size={16} color="var(--brand)" />
                <h3 style={{ margin: 0 }}>Memory Usage</h3>
              </div>
            </div>
            <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={120}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Used', value: (metrics.memory?.usage || 0) * 100, fill: '#3b82f6' },
                        { name: 'Free', value: (1 - (metrics.memory?.usage || 0)) * 100, fill: '#f3f4f6' }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={50}
                      paddingAngle={0}
                      dataKey="value"
                    >
                      <Cell key="Used" fill="#3b82f6" />
                      <Cell key="Free" fill="#f3f4f6" />
                    </Pie>
                    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '16px', fontWeight: '600' }}>
                      {formatPercentage(metrics.memory?.usage || 0)}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                  <span>0%</span>
                  <span style={{ fontWeight: '600' }}>{formatPercentage(metrics.memory?.usage || 0)}</span>
                  <span>100%</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', textAlign: 'center' }}>
                  {formatBytes(metrics.memory?.used || 0)} / {formatBytes(metrics.memory?.total || 0)}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Database size={16} color="var(--brand)" />
                <h3 style={{ margin: 0 }}>Disk Usage</h3>
              </div>
            </div>
            <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={120}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Used', value: (metrics.disk?.usage || 0) * 100, fill: '#10b981' },
                        { name: 'Free', value: (1 - (metrics.disk?.usage || 0)) * 100, fill: '#f3f4f6' }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={50}
                      paddingAngle={0}
                      dataKey="value"
                    >
                      <Cell key="Used" fill="#10b981" />
                      <Cell key="Free" fill="#f3f4f6" />
                    </Pie>
                    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '16px', fontWeight: '600' }}>
                      {formatPercentage(metrics.disk?.usage || 0)}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                  <span>0%</span>
                  <span style={{ fontWeight: '600' }}>{formatPercentage(metrics.disk?.usage || 0)}</span>
                  <span>100%</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', textAlign: 'center' }}>
                  {metrics.disk?.free || 0}% free space
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Database Status */}
      {healthData?.services?.database && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Database size={16} color="var(--brand)" />
              <h3 style={{ margin: 0 }}>Database Status & Connection Pool</h3>
            </div>
          </div>
          <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Status</div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: getStatusColor(healthData.services.database.status),
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${getStatusColor(healthData.services.database.status)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'none'
                  }}>
                    {getStatusIcon(healthData.services.database.status)}
                  </div>
                  {healthData.services.database.status}
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Database Type</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {healthData.services.database.type} ({healthData.services.database.provider})
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Latency</div>
                <div style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Clock size={14} color="var(--text-muted)" />
                  {healthData.services.database.latency}
                </div>
              </div>
            </div>

            {healthData.services.database.connectionPool && (
              <div style={{
                padding: '16px',
                background: 'var(--bg)',
                borderRadius: '8px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-muted)' }}>
                  CONNECTION POOL
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>Active</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#10b981' }}>
                      {healthData.services.database.connectionPool.active || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>Idle</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
                      {healthData.services.database.connectionPool.idle || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>Waiting</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#f59e0b' }}>
                      {healthData.services.database.connectionPool.waiting || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>Max</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>
                      {healthData.services.database.connectionPool.maxConnections || 10}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Service Health */}
      {healthData?.components && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Monitor size={16} color="var(--brand)" />
              <h3 style={{ margin: 0 }}>Service Health Details</h3>
            </div>
          </div>
          <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
            <div style={{ display: 'grid', gap: '12px' }}>
              {healthData.components
                .filter(c => ['Payment Gateways', 'WhatsApp Service', 'Email Service', 'Transaction Processing'].includes(c.name))
                .map((service, index) => (
                  <div key={index} style={{
                    padding: '16px',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>{service.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{service.description}</div>
                      {service.details && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                          {service.details.activeCount !== undefined && `Active: ${service.details.activeCount}`}
                          {service.details.activeConnections !== undefined && `Connections: ${service.details.activeConnections}`}
                          {service.details.configured !== undefined && (service.details.configured ? 'Configured' : 'Not Configured')}
                          {service.details.recentCount !== undefined && `Recent: ${service.details.recentCount}`}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '12px',
                        color: getStatusColor(service.status),
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        marginBottom: '4px'
                      }}>
                        {service.status}
                      </div>
                      {service.responseTime > 0 && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          {service.responseTime}ms
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Network Statistics and Application Performance */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginBottom: '24px' }}>
        <div className="card">
          <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Wifi size={16} color="var(--brand)" />
              <h3 style={{ margin: 0 }}>Network Statistics</h3>
            </div>
          </div>
          <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Active Connections</div>
                <div style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Users size={14} color="var(--text-muted)" />
                  {networkStats?.activeConnections || 0}
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Requests/min</div>
                <div style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Activity size={14} color="var(--text-muted)" />
                  {networkStats?.requestsPerMinute || 0}
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Bandwidth Usage</div>
                <div style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Zap size={14} color="var(--text-muted)" />
                  {networkStats?.bandwidthUsage || 0} Mbps
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Packet Loss</div>
                <div style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertCircle size={14} color="var(--text-muted)" />
                  {networkStats?.packetLoss || 0}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={16} color="var(--brand)" />
              <h3 style={{ margin: 0 }}>Application Performance</h3>
            </div>
          </div>
          <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>API Response Time</div>
                <div style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Clock size={14} color="var(--text-muted)" />
                  {performance?.apiResponseTime || 0}ms
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Error Rate</div>
                <div style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertCircle size={14} color="var(--text-muted)" />
                  {performance?.errorRate || 0}%
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Throughput</div>
                <div style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Zap size={14} color="var(--text-muted)" />
                  {performance?.throughput || 0} req/s
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Cache Hit Rate</div>
                <div style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Database size={14} color="var(--text-muted)" />
                  {performance?.cacheHitRate || 0}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Logs */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header" style={{ padding: '20px 20px 16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={16} color="var(--brand)" />
            <h3 style={{ margin: 0 }}>Recent System Logs</h3>
          </div>
          <button
            onClick={downloadLogs}
            style={{
              padding: '8px 16px',
              background: 'var(--brand)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.9'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            <Download size={16} />
            Download Logs
          </button>
        </div>
        <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
          <div style={{ display: 'grid', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
            {logs.map((log, index) => (
              <div key={index} style={{
                padding: '12px 16px',
                background: log.level === 'error' ? '#fef2f2' : log.level === 'warning' ? '#fffbeb' : '#f8fafc',
                border: `1px solid ${log.level === 'error' ? '#fecaca' : log.level === 'warning' ? '#fed7aa' : '#e2e8f0'}`,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  color: log.level === 'error' ? '#dc2626' : log.level === 'warning' ? '#d97706' : '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'none',
                  border: `2px solid ${log.level === 'error' ? '#dc2626' : log.level === 'warning' ? '#d97706' : '#10b981'}`
                }}>
                  {log.level === 'error' ? <XCircle size={12} /> : log.level === 'warning' ? <AlertCircle size={12} /> : <Check size={12} />}
                </div>
                <div style={{ flex: 1, padding: '2px 0' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text)', marginBottom: '2px' }}>
                    {typeof log.message === 'object' ? (
                      <span>
                        <span style={{ fontWeight: '600' }}>{log.message.method}</span> {log.message.url}
                        <span style={{
                          marginLeft: '8px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          background: log.message.statusCode >= 500 ? '#fef2f2' : log.message.statusCode >= 400 ? '#fffbeb' : '#f0fdf4',
                          color: log.message.statusCode >= 500 ? '#dc2626' : log.message.statusCode >= 400 ? '#d97706' : '#10b981'
                        }}>
                          {log.message.statusCode}
                        </span>
                        <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>
                          {log.message.duration}
                        </span>
                      </span>
                    ) : log.message}
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '2px 0' }}>
                  {log.timestamp || log.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Active Alerts */}
      {alerts.length > 0 && (
        <div className="card">
          <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={16} color="var(--brand)" />
                <h3 style={{ margin: 0 }}>Active Alerts ({alerts.length})</h3>
              </div>
              <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#dc2626' }}></div>
                  <span>Critical: {alerts.filter(a => a.severity === 'critical').length}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b' }}></div>
                  <span>Warning: {alerts.filter(a => a.severity === 'warning').length}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }}></div>
                  <span>Info: {alerts.filter(a => a.severity === 'info').length}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
            <div style={{ display: 'grid', gap: '12px' }}>
              {alerts.map((alert, index) => {
                const getSeverityColor = (severity) => {
                  switch(severity) {
                    case 'critical': return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' };
                    case 'warning': return { bg: '#fffbeb', border: '#fed7aa', text: '#f59e0b' };
                    case 'info': return { bg: '#eff6ff', border: '#bfdbfe', text: '#3b82f6' };
                    default: return { bg: '#f3f4f6', border: '#e5e7eb', text: '#6b7280' };
                  }
                };
                const colors = getSeverityColor(alert.severity);

                return (
                  <div key={index} style={{
                    padding: '16px 20px',
                    background: colors.bg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{
                        color: colors.text,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'none',
                        border: `2px solid ${colors.text}`,
                        flexShrink: 0
                      }}>
                        {alert.severity === 'critical' ? <XCircle size={16} /> :
                         alert.severity === 'warning' ? <AlertCircle size={16} /> :
                         <Info size={16} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                          <div>
                            <div style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              color: colors.text,
                              letterSpacing: '0.5px',
                              marginBottom: '4px'
                            }}>
                              {alert.type || alert.severity}
                            </div>
                            <div style={{ fontWeight: '600', fontSize: '14px' }}>
                              {alert.message}
                            </div>
                          </div>
                        </div>
                        {(alert.threshold || alert.current) && (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                            gap: '12px',
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: `1px solid ${colors.border}`
                          }}>
                            {alert.threshold && (
                              <div>
                                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px' }}>Threshold</div>
                                <div style={{ fontSize: '13px', fontWeight: '600' }}>{alert.threshold}</div>
                              </div>
                            )}
                            {alert.current && (
                              <div>
                                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px' }}>Current</div>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>{alert.current}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {alerts.length === 0 && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-body" style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: '#f0fdf4',
              border: '2px solid #86efac',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Check size={28} color="#10b981" />
            </div>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>All Systems Operational</div>
            <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>No alerts detected. All systems are running smoothly.</div>
          </div>
        </div>
      )}

      {/* Environment & Version Info */}
      {healthData?.services?.api && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header" style={{ padding: '20px 20px 16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Info size={16} color="var(--brand)" />
              <h3 style={{ margin: 0 }}>System Information</h3>
            </div>
          </div>
          <div className="card-body" style={{ padding: '0 20px 20px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Environment</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {healthData.environment || 'development'}
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Version</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {healthData.version || '1.0.0'}
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Node.js Version</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {healthData.services.api.nodeVersion || 'N/A'}
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Platform</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {healthData.services.api.platform || 'N/A'}
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Process ID</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {healthData.services.api.pid || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Merchants(){
  const { apiWithAccessControl } = useAccessControl();
  const toast = useToast();
  const [list, setList] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [perf, setPerf] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [resetUserEmail, setResetUserEmail] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const { hasPermission } = usePermissions();

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState('all'); // 'all', 'true', 'false'
  const [kycStatusFilter, setKycStatusFilter] = useState('all');
  const [kycLevelFilter, setKycLevelFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [joinedAfterFilter, setJoinedAfterFilter] = useState('');
  const [joinedBeforeFilter, setJoinedBeforeFilter] = useState('');
  // CSRF token is now handled server-side via HttpOnly cookies

  // Helper function to get KYC badge information
  const getKYCBadge = (kyc) => {
    if (!kyc) return { text: 'L0 - Not Started', class: 'kyc-not-started', level: 'LEVEL_0', canWithdraw: false };

    const statusClasses = {
      APPROVED: 'kyc-approved',
      PENDING_REVIEW: 'kyc-pending',
      REJECTED: 'kyc-rejected',
      NOT_STARTED: 'kyc-not-started'
    };

    const levelTexts = {
      LEVEL_0: 'L0',
      LEVEL_1: 'L1',
      LEVEL_2: 'L2',
      LEVEL_3: 'L3'
    };

    const statusText = kyc.status === 'APPROVED' ? 'Approved' :
                      kyc.status === 'PENDING_REVIEW' ? 'Pending' :
                      kyc.status === 'REJECTED' ? 'Rejected' : 'Not Started';

    return {
      text: `${levelTexts[kyc.level] || 'L0'} - ${statusText}`,
      class: statusClasses[kyc.status] || 'kyc-not-started',
      level: kyc.level,
      canWithdraw: kyc.canWithdrawFunds
    };
  };

  const loadList = async (page = 1, search = '') => {
    setErr(''); setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });
      if (search) queryParams.append('search', search);

      // Add filters to query params
      if (emailVerifiedFilter !== 'all') {
        queryParams.append('emailVerified', emailVerifiedFilter);
      }
      if (kycStatusFilter !== 'all') {
        queryParams.append('kycStatus', kycStatusFilter);
      }
      if (kycLevelFilter !== 'all') {
        queryParams.append('kycLevel', kycLevelFilter);
      }
      if (tierFilter !== 'all') {
        queryParams.append('tier', tierFilter);
      }
      if (joinedAfterFilter) {
        queryParams.append('joinedAfter', joinedAfterFilter);
      }
      if (joinedBeforeFilter) {
        queryParams.append('joinedBefore', joinedBeforeFilter);
      }

      const r = await apiWithAccessControl(`/admin/merchants?${queryParams.toString()}`);
      const d = await r.json();
      if(r.ok) {
        setList(d.merchants||[]);
        setPagination(d.pagination || { page: 1, limit: 10, total: 0, pages: 0 });
      } else {
        toast.error(d.error||'Failed to load merchants');
      }
    } catch {
      toast.error('Network error. Please check your connection.');
    } finally { setLoading(false); }
  };

  // Reset all filters
  const resetFilters = () => {
    setEmailVerifiedFilter('all');
    setKycStatusFilter('all');
    setKycLevelFilter('all');
    setTierFilter('all');
    setJoinedAfterFilter('');
    setJoinedBeforeFilter('');
    setSearchTerm('');
  };

  // Check if any filter is active
  const hasActiveFilters = emailVerifiedFilter !== 'all' ||
    kycStatusFilter !== 'all' ||
    kycLevelFilter !== 'all' ||
    tierFilter !== 'all' ||
    joinedAfterFilter ||
    joinedBeforeFilter;

  useEffect(()=>{ loadList(); },[]);

  // Reload when filters change
  useEffect(() => {
    loadList(1, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailVerifiedFilter, kycStatusFilter, kycLevelFilter, tierFilter, joinedAfterFilter, joinedBeforeFilter]);

  // Auto-search with debouncing
  useEffect(() => {
    if (searchTerm === '') {
      return;
    }

    const debounceTimer = setTimeout(() => {
      loadList(1, searchTerm);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const loadDetails = (id) => {
    setSelected(id);
  };

  const doPost = async (path, body) => {
    setErr(''); setActionMsg('');
    try{
      const r = await apiWithAccessControl(path, { method:'POST', body: body?JSON.stringify(body):undefined });
      const d = await r.json();
      if(!r.ok){
        toast.error(d.error||'Action failed');
        return;
      }
      toast.success(d.message||'Action completed successfully');
      await loadList();
      if(selected) await loadDetails(selected);
    }catch{
      toast.error('Network error. Please try again.');
    }
  };

  const resetPassword = async (merchantId, merchantEmail) => {
    if (!window.confirm(`Are you sure you want to reset the password for ${merchantEmail}?\n\nA new temporary password will be generated and shown to you.`)) return;
    setErr(''); setActionMsg('');
    try {
      const r = await apiWithAccessControl(`/admin/users/${merchantId}/reset-password`, { method: 'POST' });
      const d = await r.json();
      if(r.ok && d.success) {
        setTemporaryPassword(d.data.temporaryPassword);
        setResetUserEmail(merchantEmail);
        setShowPasswordModal(true);
        toast.success('Password reset successfully!');
        await loadList(pagination.page, searchTerm);
        if(selected === merchantId) await loadDetails(merchantId);
      } else {
        toast.error(d.error || 'Failed to reset password');
      }
    } catch {
      toast.error('Network error. Please try again.');
    }
  };

  const impersonateMerchant = async (merchantId, merchantEmail) => {
    if (!window.confirm(`Sign in as ${merchantEmail}?\n\nYou will be logged into their merchant dashboard for troubleshooting. This action is logged for security.`)) return;

    toast.success(`Starting impersonation session for ${merchantEmail}...`);

    // Use window.location to navigate to the backend endpoint via proxy
    // The backend will set the cookie and redirect to the merchant dashboard
    // Use proxy path (same as other API calls) to avoid CORS/URL issues
    window.location.href = `/api/admin/impersonate/${merchantId}`;
  };

  // If a merchant is selected, show the full detail page
  if (selected) {
    return (
      <MerchantDetail
        merchantId={selected}
        onBack={() => { setSelected(null); setDetails(null); setPerf(null); }}
        toast={toast}
      />
    );
  }

  return (
    <div className="mp-page">
      <div className="mp-header">
        <div className="mp-header-left">
          <h1>Merchants</h1>
          <p>Manage and monitor all registered merchants</p>
        </div>
        <div className="mp-header-actions">
          <button className="mp-btn" onClick={() => loadList(pagination.page)} disabled={loading}>
            <RefreshCw size={16} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="mp-search-section">
        <div className="mp-search-bar">
          <Search size={18} className="mp-search-icon" />
          <input
            type="text"
            className="mp-search-input"
            placeholder="Search by CRN, name, email, or store…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && loadList(1, searchTerm)}
          />
          {searchTerm && (
            <button
              className="mp-search-clear"
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
          <button
            className="mp-search-submit"
            onClick={() => loadList(1, searchTerm)}
            disabled={loading}
          >
            Search
          </button>
        </div>
        <button
          className={`mp-filter-toggle ${showFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
          title="Toggle Filters"
        >
          <Filter size={18} />
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mp-filters-panel">
          <div className="mp-filters-grid">
            <div className="mp-filter-group">
              <label>Email:</label>
              <select
                value={emailVerifiedFilter}
                onChange={(e) => setEmailVerifiedFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="true">Verified</option>
                <option value="false">Unverified</option>
              </select>
            </div>

            <div className="mp-filter-group">
              <label>KYC:</label>
              <select
                value={kycStatusFilter}
                onChange={(e) => setKycStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="APPROVED">Approved</option>
                <option value="PENDING_REVIEW">Pending</option>
                <option value="REJECTED">Rejected</option>
                <option value="NOT_STARTED">Not Started</option>
              </select>
            </div>

            <div className="mp-filter-group">
              <label>Level:</label>
              <select
                value={kycLevelFilter}
                onChange={(e) => setKycLevelFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="LEVEL_0">L0</option>
                <option value="LEVEL_1">L1</option>
                <option value="LEVEL_2">L2</option>
                <option value="LEVEL_3">L3</option>
              </select>
            </div>

            <div className="mp-filter-group">
              <label>Tier:</label>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="FREE">Free</option>
                <option value="STARTER">Starter</option>
                <option value="PROFESSIONAL">Pro</option>
                <option value="BUSINESS">Business</option>
                <option value="ENTERPRISE">Enterprise</option>
              </select>
            </div>

            <div className="mp-filter-group">
              <label>From:</label>
              <input
                type="date"
                value={joinedAfterFilter}
                onChange={(e) => setJoinedAfterFilter(e.target.value)}
              />
            </div>

            <div className="mp-filter-group">
              <label>To:</label>
              <input
                type="date"
                value={joinedBeforeFilter}
                onChange={(e) => setJoinedBeforeFilter(e.target.value)}
              />
            </div>

            {hasActiveFilters && (
              <button className="mp-clear-filters" onClick={() => { resetFilters(); loadList(1, ''); }}>
                <X size={14} /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Merchant List Section - Card-Based */}
      <div className="mp-list-header">
        <div className="mp-list-header-left">
          <h2>All Merchants</h2>
          <span className="mp-count">{pagination.total} merchants</span>
        </div>
        <div className="mp-view-toggle">
          <button
            className={`mp-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid View"
          >
            <LayoutTemplate size={16} />
          </button>
          <button
            className={`mp-view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List View"
          >
            <MoreHorizontal size={16} style={{ transform: 'rotate(90deg)' }} />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="mp-content">
        {loading && (
          <div className="mp-loading">
            <div className="mp-spinner"></div>
            <p>Loading merchants...</p>
          </div>
        )}

        {!loading && list.length === 0 && (
          <div className="mp-empty">
            <div className="mp-empty-icon">
              <Store size={36} strokeWidth={1.5} />
            </div>
            <h3>No merchants found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}

        {!loading && list.length > 0 && (
          <>
            <div className={`mp-${viewMode}`}>
              {list.map(m => {
                const orderCount = m.store?._count?.orders || m._count?.orders || 0;
                const customerCount = m.store?._count?.customers || m._count?.customers || 0;
                const revenue = m.totalRevenue || 0;
                const kycBadge = getKYCBadge(m.kyc);
                const tier = m.merchantTier || 'FREE';
                const joinedDate = new Date(m.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                const appStatus = m.store?.applicationStatus || 'PENDING';
                const isSuspended = m.store?.isSuspended;
                const isBanned = m.store?.isBanned;

                const merchantName = m.firstName && m.lastName
                  ? `${m.firstName} ${m.lastName}`
                  : m.firstName || m.lastName || null;

                return (
                  <div
                    key={m._id || m.id}
                    className="mp-card"
                    onClick={() => loadDetails(m._id || m.id)}
                  >
                    {/* Top - Avatar, Name, Tier */}
                    <div className="mp-card-header">
                      <div className="mp-avatar">
                        {(merchantName || m.store?.name || m.username || 'M').charAt(0).toUpperCase()}
                      </div>
                      <div className="mp-card-info">
                        <h4 className="mp-card-name">{merchantName || m.store?.name || m.username || '—'}</h4>
                        <p className="mp-card-email">{m.email}</p>
                        {merchantName && m.store?.name && (
                          <p className="mp-card-store">{m.store.name}</p>
                        )}
                        {m.merchantCode && (
                          <p className="mp-card-crn" title={`Merchant CRN: ${m.merchantCode}`}>
                            {m.merchantCode}
                          </p>
                        )}
                      </div>
                      <span className={`mp-tier ${tier}`}>{tier}</span>
                    </div>

                    {/* Middle - Badges + Stats */}
                    <div className="mp-card-body">
                      <div className="mp-badges">
                        <span className={`mp-badge ${appStatus.toLowerCase()}`}>{appStatus}</span>
                        <span className={`mp-badge ${m.isEmailVerified ? 'verified' : 'unverified'}`}>
                          {m.isEmailVerified ? 'Verified' : 'Unverified'}
                        </span>
                        <span className={`mp-badge ${kycBadge.class}`}>{kycBadge.text}</span>
                        {isSuspended && <span className="mp-badge suspended">Suspended</span>}
                        {isBanned && <span className="mp-badge banned">Banned</span>}
                      </div>
                      <div className="mp-stats">
                        <div className="mp-stat">
                          <span className="mp-stat-value">{orderCount.toLocaleString()}</span>
                          <span className="mp-stat-label">Orders</span>
                        </div>
                        <div className="mp-stat">
                          <span className="mp-stat-value">{customerCount.toLocaleString()}</span>
                          <span className="mp-stat-label">Customers</span>
                        </div>
                        <div className="mp-stat">
                          <span className={`mp-stat-value ${revenue > 0 ? 'success' : ''}`}>
                            ₦{revenue.toLocaleString()}
                          </span>
                          <span className="mp-stat-label">Revenue</span>
                        </div>
                      </div>
                    </div>

                    {/* Bottom - Date + Actions */}
                    <div className="mp-card-footer">
                      <div className="mp-date">
                        <Calendar size={14} />
                        <span>Joined {joinedDate}</span>
                      </div>
                      <div className="mp-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="mp-btn mp-btn-sm mp-btn-icon" onClick={() => loadDetails(m._id || m.id)}>
                          <Eye size={15} />
                        </button>
                        <button
                          className="mp-btn mp-btn-sm mp-btn-icon"
                          onClick={() => impersonateMerchant(m._id || m.id, m.email)}
                          title="Sign in as merchant"
                        >
                          <LogIn size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="mp-pagination">
                <div className="mp-pagination-info">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </div>
                <div className="mp-pagination-controls">
                  <button
                    className="mp-btn"
                    onClick={() => loadList(pagination.page - 1, searchTerm)}
                    disabled={loading || pagination.page <= 1}
                  >
                    <ChevronLeft size={16} /> Previous
                  </button>
                  <div className="mp-pagination-pages">
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(pagination.pages - 4, pagination.page - 2)) + i;
                      return (
                        <button
                          key={pageNum}
                          className={`mp-page-btn ${pageNum === pagination.page ? 'active' : ''}`}
                          onClick={() => loadList(pageNum, searchTerm)}
                          disabled={loading}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className="mp-btn"
                    onClick={() => loadList(pagination.page + 1, searchTerm)}
                    disabled={loading || pagination.page >= pagination.pages}
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Password Reset Success Modal */}
      {showPasswordModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setShowPasswordModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✓</div>
              <h2 style={{ margin: 0, color: '#16a34a', fontSize: '24px' }}>
                Password Reset Successful
              </h2>
            </div>

            {/* User Info */}
            <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '24px' }}>
              Temporary password for <strong style={{ color: '#1f2937' }}>{resetUserEmail}</strong>
            </p>

            {/* Password Display */}
            <div
              style={{
                backgroundColor: '#f3f4f6',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '2px dashed #d1d5db'
              }}
            >
              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  color: '#1f2937',
                  letterSpacing: '2px',
                  userSelect: 'all',
                  wordBreak: 'break-all'
                }}
              >
                {temporaryPassword}
              </div>
            </div>

            {/* Copy Button */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(temporaryPassword);
                toast.success('Password copied to clipboard!');
              }}
              style={{
                width: '100%',
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '20px' }}>📋</span>
              Copy Password
            </button>

            {/* Warning Message */}
            <div
              style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}
            >
              <div style={{ display: 'flex', gap: '12px', alignItems: 'start' }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ color: '#92400e', display: 'block', marginBottom: '4px' }}>
                    Important:
                  </strong>
                  <span style={{ color: '#78350f', fontSize: '14px' }}>
                    This password will not be shown again. Please save it securely and share it with the merchant through a secure channel.
                  </span>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowPasswordModal(false)}
              style={{
                width: '100%',
                padding: '12px 24px',
                backgroundColor: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      </div>
  );
}

function FinanceServiceCharge(){
  const { apiWithAccessControl } = useAccessControl();
  const [merchantId, setMerchantId] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMerchants, setLoadingMerchants] = useState(true);

  useEffect(() => {
    loadMerchants();
  }, []);

  const loadMerchants = async () => {
    try {
      setLoadingMerchants(true);
      const r = await apiWithAccessControl('/admin/merchants?page=1&limit=1000');
      const d = await r.json();
      if (r.ok) {
        setMerchants(d.merchants || []);
      }
    } catch (e) {
      console.error('Failed to load merchants:', e);
    } finally {
      setLoadingMerchants(false);
    }
  };

  const handleMerchantSelect = (e) => {
    const selectedId = e.target.value;
    setMerchantId(selectedId);
    const merchant = merchants.find(m => m.id === selectedId);
    setSelectedMerchant(merchant);
    setData(null);
    setErr('');
    setMsg('');
  };

  const fetchSettings = async () => {
    if (!merchantId) return;
    setErr(''); setMsg(''); setData(null); setLoading(true);
    try{
      const r = await apiWithAccessControl(`/admin/finance/service-charge/${merchantId}`);
      const d = await r.json();
      if(r.ok) setData(d); else setErr(d.error||'Error');
    }catch{ setErr('Network'); } finally { setLoading(false); }
  };

  const save = async (payload) => {
    setErr(''); setMsg(''); setLoading(true);
    console.log('Saving service charge settings:', payload);
    try{
      const r = await apiWithAccessControl(`/admin/finance/service-charge/${merchantId}`, { method:'POST', body: JSON.stringify(payload) });
      const d = await r.json();
      console.log('Save response:', d);
      if(r.ok) {
        setMsg('Settings saved successfully');
        // Update data immediately with response
        setData(d);
      } else {
        setErr(d.error||'Error saving settings');
      }
    }catch(e){
      console.error('Save error:', e);
      setErr('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (merchantId) {
      fetchSettings();
    }
  }, [merchantId]);

  return (
    <div>
      <div className="admin-h1">Service Charges</div>
      <div className="card">
        <div className="card-body">
          <div className="card-title">Merchant Service Charge Settings</div>

          {loadingMerchants ? (
            <div style={{ padding: 16, textAlign: 'center' }}>Loading merchants...</div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Select Merchant</label>
                <select
                  className="input"
                  value={merchantId}
                  onChange={handleMerchantSelect}
                  style={{ width: '100%', padding: '8px 12px' }}
                >
                  <option value="">-- Select a merchant --</option>
                  {merchants.map(merchant => (
                    <option key={merchant.id} value={merchant.id}>
                      {merchant.username || merchant.email}
                      {merchant.store?.name ? ` - ${merchant.store.name}` : ''}
                      ({merchant.merchantTier || 'FREE'})
                    </option>
                  ))}
                </select>
              </div>

              {selectedMerchant && (
                <div style={{ padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, fontSize: 14 }}>
                    <div><b>Email:</b> {selectedMerchant.email}</div>
                    <div><b>Username:</b> {selectedMerchant.username || 'N/A'}</div>
                    <div><b>Store:</b> {selectedMerchant.store?.name || 'N/A'}</div>
                    <div><b>Tier:</b> {selectedMerchant.merchantTier || 'FREE'}</div>
                  </div>
                </div>
              )}
            </>
          )}

          {err && <div style={{ color:'#dc2626', marginBottom:12, padding:12, background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8 }}>{err}</div>}
          {msg && <div style={{ color:'#16a34a', marginBottom:12, padding:12, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8 }}>{msg}</div>}
          {data && (
            <>
              {data.tierConfig && (
                <div className="card" style={{ border:'1px solid #3b82f6', background: '#eff6ff', marginBottom: 16 }}>
                  <div className="card-body">
                    <div className="card-title" style={{ color: '#1e40af' }}>
                      Tier Default Configuration ({data.merchantTier})
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap:12, fontSize: 14 }}>
                      <div><b>Default Rate:</b> {data.tierConfig.rate}%</div>
                      <div><b>Default Who Pays:</b> {data.tierConfig.whoPays}</div>
                      <div style={{ gridColumn: '1 / -1' }}><b>Description:</b> {data.tierConfig.description}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="card" style={{ border:'1px solid var(--border)' }}>
                <div className="card-body">
                  <div className="card-title">Merchant-Specific Settings</div>
                  <div style={{ display:'grid', gap:12, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <b>Service Charge Enabled:</b>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 13,
                        fontWeight: 600,
                        background: data.serviceChargeSettings?.enabled ? '#dcfce7' : '#fee2e2',
                        color: data.serviceChargeSettings?.enabled ? '#166534' : '#991b1b'
                      }}>
                        {data.serviceChargeSettings?.enabled ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </div>
                    <div><b>Who Pays:</b> <span style={{ textTransform: 'capitalize' }}>{data.serviceChargeSettings?.whoPays || 'customer'}</span></div>
                    <div><b>Rate:</b> {data.serviceChargeSettings?.rate || (data.tierConfig ? `${data.tierConfig.rate}%` : '2.5%')}</div>
                  </div>
                  <div style={{ display:'flex', gap:8, flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-primary"
                      onClick={()=> save({
                        enabled: true,
                        whoPays: 'customer',
                        rate: data.tierConfig ? data.tierConfig.rate : '2.5'
                      })}
                      disabled={loading}
                    >
                      Enable (Customer Pays)
                    </button>
                    <button
                      className="btn"
                      onClick={()=> save({
                        enabled: true,
                        whoPays: 'merchant',
                        rate: data.tierConfig ? data.tierConfig.rate : '2.5'
                      })}
                      disabled={loading}
                    >
                      Enable (Merchant Pays)
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={()=> save({ enabled: false })}
                      disabled={loading}
                    >
                      Disable Service Charge
                    </button>
                  </div>
                  <div style={{ marginTop: 16, padding: 12, background: '#f9fafb', borderRadius: 8, fontSize: 13, color: '#6b7280' }}>
                    <b>Note:</b> Changes here override the tier defaults. Service charges are automatically calculated during checkout based on these settings.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FinancePayouts(){
  const { apiWithAccessControl } = useAccessControl();
  const [list, setList] = useState([]); 
  const [err, setErr] = useState(''); 
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const loadPayouts = async () => {
    setErr(''); setLoading(true);
    try{ 
      const r = await apiWithAccessControl('/admin/finance/payouts'); 
      const d = await r.json(); 
      if(r.ok) setList(d.payouts||[]); else setErr(d.error||'Error'); 
    }catch{ setErr('Network'); } finally { setLoading(false); }
  };

  const doPost = async (path, body) => {
    setErr(''); setMsg('');
    try{ 
      const r = await apiWithAccessControl(path, { method:'POST', body: body?JSON.stringify(body):undefined }); 
      const d = await r.json(); 
      if(!r.ok){ setErr(d.error||'Action failed'); return; }
      setMsg(d.message||'Action completed');
      await loadPayouts();
    }catch{ setErr('Network'); }
  };

  useEffect(()=>{ loadPayouts(); },[]);

  return (
    <div>
      <div className="admin-h1">Payouts</div>
      {err && <div style={{ color:'#dc2626', marginBottom:12, padding:12, background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8 }}>{err}</div>}
      {msg && <div style={{ color:'#16a34a', marginBottom:12, padding:12, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8 }}>{msg}</div>}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontWeight:700 }}>Payout History</div>
        <button className="btn" onClick={loadPayouts} disabled={loading}>{loading?'Loading…':'Refresh'}</button>
      </div>
      <div className="card" style={{ overflow:'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Merchant</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Method</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map(p => (
              <tr key={p._id}>
                <td>{p.merchantName || p.merchantId}</td>
                <td>₦{Intl.NumberFormat('en-NG').format(Math.round(p.amount))}</td>
                <td>
                  <span style={{ 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '12px',
                    fontWeight: '600',
                    background: p.status === 'completed' ? '#f0fdf4' : p.status === 'pending' ? '#fef3c7' : '#fef2f2',
                    color: p.status === 'completed' ? '#16a34a' : p.status === 'pending' ? '#d97706' : '#dc2626'
                  }}>
                    {p.status}
                  </span>
                </td>
                <td className="muted">{p.method || 'Bank Transfer'}</td>
                <td className="muted">{new Date(p.createdAt).toLocaleDateString()}</td>
                <td style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {p.status === 'pending' && (
                    <button className="btn btn-primary" onClick={()=> doPost(`/admin/finance/payouts/${p._id}/process`)}>
                      Process
                    </button>
                  )}
                  {p.status === 'completed' && (
                    <span className="muted" style={{ fontSize:12 }}>Completed</span>
                  )}
                </td>
              </tr>
            ))}
            {list.length===0 && (
              <tr><td colSpan={6} className="muted" style={{ padding:14 }}>No payouts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Notifications(){
  const [msg, setMsg] = useState(''); 
  const [to, setTo] = useState('merchants'); 
  const [resp, setResp] = useState(''); 
  const [loading, setLoading] = useState(false);

  const send = async () => { 
    setResp(''); setLoading(true);
    try{ 
      const response = await api('/admin/notifications/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, message: msg })
      });
      
      if (response.ok) {
        const result = await response.json();
        setResp(result.message || 'Notification sent successfully!');
      } else {
        const error = await response.json();
        setResp(error.error || 'Failed to send notification');
      }
    }catch{ 
      setResp('Failed to send notification'); 
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="admin-h1">Notifications</div>
      <div className="card">
        <div className="card-body">
          <div className="card-title">Send Broadcast Notification</div>
          <div style={{ display:'grid', gap:16, maxWidth:600 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Recipients</label>
              <select 
                className="input" 
                value={to} 
                onChange={e=>setTo(e.target.value)}
              >
                <option value="merchants">All Merchants</option>
                <option value="customers">All Customers</option>
                <option value="active_merchants">Active Merchants Only</option>
                <option value="pending_merchants">Pending Merchants Only</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Message</label>
              <textarea 
                className="input" 
                rows={5} 
                placeholder="Enter your notification message..." 
                value={msg} 
                onChange={e=>setMsg(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button 
                className="btn btn-primary" 
                onClick={send} 
                disabled={!msg || loading}
              >
                {loading ? 'Sending...' : 'Send Notification'}
              </button>
              <button 
                className="btn" 
                onClick={() => { setMsg(''); setResp(''); }}
                disabled={loading}
              >
                Clear
              </button>
            </div>
            {resp && (
              <div style={{ 
                padding: 12, 
                borderRadius: 8, 
                background: resp.includes('successfully') ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${resp.includes('successfully') ? '#bbf7d0' : '#fecaca'}`,
                color: resp.includes('successfully') ? '#16a34a' : '#dc2626'
              }}>
                {resp}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-body">
          <div className="card-title">Recent Notifications</div>
          <div style={{ display:'grid', gap:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border-light)' }}>
              <div>
                <div style={{ fontWeight:600, fontSize:14 }}>System Maintenance Notice</div>
                <div className="muted" style={{ fontSize:12 }}>Sent to all merchants • 2 hours ago</div>
              </div>
              <span style={{ fontSize:12, color:'var(--success-color)' }}>Delivered</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border-light)' }}>
              <div>
                <div style={{ fontWeight:600, fontSize:14 }}>New Feature Announcement</div>
                <div className="muted" style={{ fontSize:12 }}>Sent to all customers • 1 day ago</div>
              </div>
              <span style={{ fontSize:12, color:'var(--success-color)' }}>Delivered</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0' }}>
              <div>
                <div style={{ fontWeight:600, fontSize:14 }}>Payment Processing Update</div>
                <div className="muted" style={{ fontSize:12 }}>Sent to active merchants • 3 days ago</div>
              </div>
              <span style={{ fontSize:12, color:'var(--success-color)' }}>Delivered</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RefundsLegacy(){
  const { apiWithAccessControl } = useAccessControl();
  const [list,setList]=useState([]); const [err,setErr]=useState(''); const [csrf,setCsrf]=useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const { hasPermission } = usePermissions();
  
  const load = async (status) => { 
    setErr(''); 
    setLoading(true);
    try{ 
      const r=await apiWithAccessControl(`/refund-requests/admin/refund-requests${status?`?status=${status}`:''}`); 
      const d=await r.json(); 
      if(r.ok) setList(d.refundRequests||[]); 
      else setErr(d.error||'Error'); 
    }catch{ 
      setErr('Network'); 
    } finally {
      setLoading(false);
    }
  };
  
  const getCsrf = async () => { 
    try{ 
      const r=await apiWithAccessControl('/admin/session/csrf'); 
      const d=await r.json(); 
      if(r.ok) setCsrf(d.csrfToken); 
    }catch{} 
  };

  const act = async (id, action) => { 
    setErr(''); 
    setLoading(true);
    try{ 
      const r=await apiWithAccessControl(`/refund-requests/admin/refund-requests/${id}/process`, { 
        method:'POST', 
        headers:{ 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, notes: adminNotes })
      }); 
      const d=await r.json(); 
      if(!r.ok){ 
        setErr(d.error||'Action failed'); 
        return; 
      } 
      setShowModal(false);
      setAdminNotes('');
      load(); 
    }catch{ 
      setErr('Network'); 
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_merchant': return { background: '#fef3c7', color: '#d97706' };
      case 'pending_admin': return { background: '#dbeafe', color: '#1d4ed8' };
      case 'processing': return { background: '#f0f9ff', color: '#0369a1' };
      case 'approved': return { background: '#f0fdf4', color: '#16a34a' };
      case 'rejected': return { background: '#fef2f2', color: '#dc2626' };
      case 'completed': return { background: '#f0fdf4', color: '#15803d' };
      default: return { background: '#f3f4f6', color: '#6b7280' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending_merchant': return '⏳';
      case 'pending_admin': return '🛡️';
      case 'processing': return '⚙️';
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'completed': return '🎉';
      default: return '❓';
    }
  };

  useEffect(()=>{ load(); getCsrf(); },[]);

  return (
    <div>
      <div className="admin-h1">Refund Requests</div>
      {err && <div style={{ color:'#dc2626', marginBottom:12, padding: '8px 12px', background: '#fef2f2', borderRadius: '6px' }}>{err}</div>}
      
      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap: 'wrap' }}>
        <button className="btn" onClick={()=> load()}>All</button>
        <button className="btn" onClick={()=> load('pending_merchant')}>Pending Merchant</button>
        <button className="btn" onClick={()=> load('pending_admin')}>Pending Admin</button>
        <button className="btn" onClick={()=> load('processing')}>Processing</button>
        <button className="btn" onClick={()=> load('approved')}>Approved</button>
        <button className="btn" onClick={()=> load('rejected')}>Rejected</button>
        <button className="btn" onClick={()=> load('completed')}>Completed</button>
      </div>
      
      <div className="card" style={{ overflow:'hidden' }}>
        {loading && <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}
        <table className="table">
          <thead>
            <tr>
              <th>Request ID</th>
              <th>Customer</th>
              <th>Order</th>
              <th>Amount</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Merchant Response</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map(r => (
              <tr key={r._id}>
                <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{r._id.slice(-8)}</td>
                <td>
                  <div>
                    <div style={{ fontWeight: '500' }}>{r.customerId?.firstName} {r.customerId?.lastName}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{r.customerId?.email}</div>
                  </div>
                </td>
                <td>
                  <div>
                    <div style={{ fontWeight: '500' }}>{r.orderId?.orderNumber || 'N/A'}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>₦{Intl.NumberFormat('en-NG').format(r.orderId?.total || 0)}</div>
                  </div>
                </td>
                <td>₦{Intl.NumberFormat('en-NG').format(r.refundAmount)}</td>
                <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.refundReason?.replace('_', ' ')}
                </td>
                <td>
                  <span style={{ 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '12px',
                    fontWeight: '600',
                    ...getStatusColor(r.status)
                  }}>
                    {getStatusIcon(r.status)} {r.status?.replace('_', ' ')}
                  </span>
                </td>
                <td>
                  {r.merchantResponse?.action && (
                    <div style={{ fontSize: '12px' }}>
                      <div style={{ fontWeight: '500' }}>{r.merchantResponse.action}</div>
                      {r.merchantResponse.notes && (
                        <div style={{ color: '#6b7280', marginTop: '2px' }}>{r.merchantResponse.notes}</div>
                      )}
                    </div>
                  )}
                </td>
                <td style={{ fontSize: '12px', color: '#6b7280' }}>
                  {new Date(r.createdAt).toLocaleDateString()}
                </td>
                <td style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {hasPermission('refunds:write') && r.status === 'pending_admin' && (
                    <>
                      <button 
                        className="btn btn-primary" 
                        onClick={() => {
                          setSelectedRequest(r);
                          setShowModal(true);
                        }}
                        disabled={loading}
                      >
                        Review
                      </button>
                    </>
                  )}
                  {r.status === 'processing' && (
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Processing...</span>
                  )}
                  {r.status === 'completed' && (
                    <span style={{ fontSize: '12px', color: '#16a34a' }}>Completed</span>
                  )}
                  {r.status === 'rejected' && (
                    <span style={{ fontSize: '12px', color: '#dc2626' }}>Rejected</span>
                  )}
                </td>
              </tr>
            ))}
            {list.length===0 && !loading && (
              <tr><td colSpan={9} className="muted" style={{ padding:14, textAlign: 'center' }}>No refund requests found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Review Modal */}
      {showModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Review Refund Request</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Customer:</strong> {selectedRequest.customerId?.firstName} {selectedRequest.customerId?.lastName} ({selectedRequest.customerId?.email})
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Order:</strong> {selectedRequest.orderId?.orderNumber} - ₦{Intl.NumberFormat('en-NG').format(selectedRequest.orderId?.total)}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Refund Amount:</strong> ₦{Intl.NumberFormat('en-NG').format(selectedRequest.refundAmount)}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Reason:</strong> {selectedRequest.refundReason?.replace('_', ' ')}
            </div>
            
            {selectedRequest.customerNotes && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Customer Notes:</strong> {selectedRequest.customerNotes}
              </div>
            )}
            
            {selectedRequest.merchantResponse?.notes && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Merchant Notes:</strong> {selectedRequest.merchantResponse.notes}
              </div>
            )}
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Admin Notes:
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
                placeholder="Add notes about your decision..."
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="btn" 
                onClick={() => {
                  setShowModal(false);
                  setAdminNotes('');
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="btn" 
                onClick={() => act(selectedRequest._id, 'reject')}
                disabled={loading}
                style={{ background: '#dc2626', color: 'white' }}
              >
                {loading ? 'Rejecting...' : 'Reject'}
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => act(selectedRequest._id, 'approve')}
                disabled={loading}
              >
                {loading ? 'Approving...' : 'Approve & Process'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Disputes(){
  const { apiWithAccessControl } = useAccessControl();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolution, setResolution] = useState('');
  const [notes, setNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [filters, setFilters] = useState({ status: '', merchantId: '', startDate: '', endDate: '' });

  const loadDisputes = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const r = await apiWithAccessControl(`/admin/disputes?${params}`);
      const d = await r.json();

      if (r.ok) {
        setList(d.disputes || []);
        setPagination(d.pagination);
        setErr('');
      } else {
        setErr(d.error || 'Failed to load disputes');
      }
    } catch (error) {
      setErr('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDisputes(pagination.page);
  }, [filters]);

  const handleViewDetails = async (dispute) => {
    try {
      const r = await apiWithAccessControl(`/admin/disputes/${dispute.id}`);
      const d = await r.json();
      if (r.ok) {
        setSelectedDispute(d.dispute);
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Failed to load dispute details', error);
    }
  };

  const handleResolveDispute = async () => {
    if (!resolution || !selectedDispute) return;

    try {
      setResolving(true);
      const r = await apiWithAccessControl(`/admin/disputes/${selectedDispute.id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolution, notes })
      });
      const d = await r.json();

      if (r.ok) {
        alert('Dispute resolved successfully');
        setShowResolveModal(false);
        setShowDetailsModal(false);
        setResolution('');
        setNotes('');
        loadDisputes(pagination.page);
      } else {
        alert(d.error || 'Failed to resolve dispute');
      }
    } catch (error) {
      alert('Network error');
    } finally {
      setResolving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      SUCCESS: '#10b981',
      PENDING: '#f59e0b',
      FAILED: '#ef4444',
      REFUNDED: '#6366f1'
    };
    return (
      <span style={{
        background: statusColors[status] || '#6b7280',
        color: 'white',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="admin-h1">Payment Disputes</div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            >
              <option value="">All Statuses</option>
              <option value="SUCCESS">Success</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            />
          </div>
        </div>
      </div>

      {err && <div style={{ color: '#dc2626', marginBottom: '15px' }}>{err}</div>}

      {/* Disputes Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Loading disputes...</div>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Provider</th>
                  <th>Reference</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Merchant</th>
                  <th>Reason</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map(dispute => (
                  <tr key={dispute.id}>
                    <td>{new Date(dispute.createdAt).toLocaleDateString()}</td>
                    <td><strong>{dispute.provider}</strong></td>
                    <td className="muted">{dispute.providerRef || 'N/A'}</td>
                    <td>₦{Intl.NumberFormat('en-NG').format(Math.round(dispute.amount))}</td>
                    <td>{getStatusBadge(dispute.status)}</td>
                    <td className="muted">{dispute.merchant?.username || dispute.merchant?.email}</td>
                    <td className="muted">{dispute.disputeReason || 'Not specified'}</td>
                    <td>
                      <button
                        onClick={() => handleViewDetails(dispute)}
                        style={{
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td colSpan={8} className="muted" style={{ padding: 20, textAlign: 'center' }}>
                      No disputed transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{ padding: '20px', display: 'flex', justifyContent: 'center', gap: '10px', borderTop: '1px solid #e5e7eb' }}>
                <button
                  onClick={() => loadDisputes(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: pagination.page === 1 ? 'not-allowed' : 'pointer' }}
                >
                  Previous
                </button>
                <span style={{ padding: '8px 16px' }}>Page {pagination.page} of {pagination.pages}</span>
                <button
                  onClick={() => loadDisputes(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer' }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Dispute Details Modal */}
      {showDetailsModal && selectedDispute && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>Dispute Details</h2>

            <div style={{ marginBottom: '20px' }}>
              <h3>Transaction Information</h3>
              <p><strong>Transaction ID:</strong> {selectedDispute.id}</p>
              <p><strong>Provider:</strong> {selectedDispute.provider}</p>
              <p><strong>Reference:</strong> {selectedDispute.providerRef || 'N/A'}</p>
              <p><strong>Amount:</strong> ₦{Intl.NumberFormat('en-NG').format(Math.round(selectedDispute.amount))}</p>
              <p><strong>Status:</strong> {selectedDispute.status}</p>
              <p><strong>Created:</strong> {new Date(selectedDispute.createdAt).toLocaleString()}</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3>Dispute Information</h3>
              <p><strong>Dispute ID:</strong> {selectedDispute.disputeId || 'N/A'}</p>
              <p><strong>Reason:</strong> {selectedDispute.disputeReason || 'Not specified'}</p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3>Merchant Information</h3>
              <p><strong>Name:</strong> {selectedDispute.merchant?.username || 'N/A'}</p>
              <p><strong>Email:</strong> {selectedDispute.merchant?.email}</p>
              <p><strong>Store:</strong> {selectedDispute.store?.name || 'N/A'}</p>
            </div>

            {selectedDispute.customer && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Customer Information</h3>
                <p><strong>Name:</strong> {selectedDispute.customer.firstName} {selectedDispute.customer.lastName}</p>
                <p><strong>Email:</strong> {selectedDispute.customer.email}</p>
                <p><strong>Phone:</strong> {selectedDispute.customer.phone || 'N/A'}</p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => { setShowResolveModal(true); setShowDetailsModal(false); }}
                style={{
                  padding: '10px 20px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Resolve Dispute
              </button>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Dispute Modal */}
      {showResolveModal && selectedDispute && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0 }}>Resolve Dispute</h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Resolution</label>
              <select
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              >
                <option value="">Select resolution...</option>
                <option value="ACCEPTED">Accept (Customer Won)</option>
                <option value="REJECTED">Reject (Merchant Won)</option>
                <option value="REFUNDED">Refund Customer</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add resolution notes..."
                rows={4}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleResolveDispute}
                disabled={!resolution || resolving}
                style={{
                  padding: '10px 20px',
                  background: resolution ? '#10b981' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: resolution && !resolving ? 'pointer' : 'not-allowed',
                  fontWeight: '600'
                }}
              >
                {resolving ? 'Resolving...' : 'Confirm Resolution'}
              </button>
              <button
                onClick={() => { setShowResolveModal(false); setShowDetailsModal(true); }}
                style={{
                  padding: '10px 20px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// DEPRECATED — superseded by components/CustomersDirectory.js. Route
// /customers now points to CustomersDirectory. This function is dead
// code; kept until the next cleanup pass to keep the redesign diff
// small. TODO[cleanup]: delete function body once verified live.
function Customers(){
  const { apiWithAccessControl } = useAccessControl();
  const { hasPermission } = usePermissions();
  const toast = useToast();
  const [list, setList] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [resetUserEmail, setResetUserEmail] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const loadList = async (page = 1, search = '') => {
    setErr(''); setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });
      if (search) queryParams.append('search', search);

      const r = await apiWithAccessControl(`/admin/customers?${queryParams.toString()}`);
      const d = await r.json();
      if(r.ok) {
        setList(d.customers||[]);
        setPagination(d.pagination || { page: 1, limit: 10, total: 0, pages: 0 });
      } else {
        setErr(d.error||'Error');
      }
    } catch { setErr('Network'); } finally { setLoading(false); }
  };

  useEffect(()=>{ loadList(); },[]);

  // Auto-search with debouncing
  useEffect(() => {
    if (searchTerm === '') {
      return;
    }

    const debounceTimer = setTimeout(() => {
      loadList(1, searchTerm);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const loadDetails = async (id) => {
    setErr(''); setSelected(id); setDetails(null); setDetailsLoading(true);
    try {
      const r = await apiWithAccessControl(`/admin/customers/${id}`);
      const d = await r.json();
      if(r.ok) setDetails(d); else setErr(d.error||'Error');
    } catch { setErr('Network'); } finally { setDetailsLoading(false); }
  };

  const activateCustomer = async (id) => {
    setErr('');
    try {
      const r = await apiWithAccessControl(`/admin/customers/${id}/activate`, { method: 'POST' });
      const d = await r.json();
      if(r.ok) {
        await loadList(pagination.page, searchTerm);
      } else {
        setErr(d.error || 'Failed to activate customer');
      }
    } catch { setErr('Network error'); }
  };

  const deactivateCustomer = async (id) => {
    setErr('');
    try {
      const r = await apiWithAccessControl(`/admin/customers/${id}/deactivate`, { method: 'POST' });
      const d = await r.json();
      if(r.ok) {
        await loadList(pagination.page, searchTerm);
      } else {
        setErr(d.error || 'Failed to deactivate customer');
      }
    } catch { setErr('Network error'); }
  };

  const resetPassword = async (customerId, customerEmail) => {
    if (!window.confirm(`Are you sure you want to reset the password for ${customerEmail}?`)) return;
    setErr('');
    try {
      const r = await apiWithAccessControl(`/admin/customers/${customerId}/reset-password`, { method: 'POST' });
      const d = await r.json();
      if(r.ok && d.success) {
        setTemporaryPassword(d.data.temporaryPassword);
        setResetUserEmail(customerEmail);
        setShowPasswordModal(true);
        toast.success('Password reset successfully!');
        await loadList(pagination.page, searchTerm);
      } else {
        toast.error(d.error || 'Failed to reset password');
      }
    } catch {
      toast.error('Network error');
    }
  };

  return (
    <div className="customers-page">
      <div className="customers-header">
        <h1>Customers</h1>
        <div className="customers-header-actions">
          <button className="btn" onClick={() => loadList(pagination.page)} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {err && <div style={{ color:'#dc2626', marginBottom:12, padding:12, background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8 }}>{err}</div>}

      {/* Search Bar */}
      <div className="customers-search-bar">
        <Search size={20} className="search-icon" />
        <input
          type="text"
          className="customers-search-input"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && loadList(1, searchTerm)}
        />
        {searchTerm && (
          <button
            className="search-clear-btn"
            onClick={() => setSearchTerm('')}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
        <button
          className="search-submit-btn"
          onClick={() => loadList(1, searchTerm)}
          disabled={loading}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Customer List Section - Card-Based */}
      <div className="customers-list-header">
        <div>
          <h2>All Customers</h2>
          <span className="customers-count">{pagination.total} customers</span>
        </div>
        <div className="view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid View"
          >
            <LayoutTemplate size={16} />
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List View"
          >
            <MoreHorizontal size={16} style={{ transform: 'rotate(90deg)' }} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="customers-loading">
          <div className="loading-spinner"></div>
          <p>Loading customers...</p>
        </div>
      )}

      {!loading && list.length === 0 && (
        <div className="customers-empty">
          <Users size={48} />
          <h3>No customers found</h3>
          <p>Try adjusting your search criteria</p>
        </div>
      )}

      {!loading && list.length > 0 && (
        <>
          <div className={`customers-${viewMode}`}>
            {list.map(c => {
              const orderCount = c.orderCount || 0;
              const totalSpent = c.totalSpent || 0;
              const avgOrderValue = c.avgOrderValue || 0;
              const joinedDate = new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

              return (
                <div
                  key={c._id || c.id}
                  className="customer-card"
                  onClick={() => loadDetails(c._id || c.id)}
                >
                  {/* Card Header */}
                  <div className="customer-card-header">
                    <div className="customer-card-title">
                      <Users size={16} />
                      <div>
                        <h3>{c.firstName && c.lastName ? `${c.firstName} ${c.lastName}` : 'Unnamed Customer'}</h3>
                        <p>{c.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status Badges */}
                  <div className="customer-card-badges">
                    <span className={`status-badge ${c.isActive ? 'active' : 'inactive'}`}>
                      {c.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                    <span className={`store-badge ${c.merchant?.store?.name ? 'has-store' : 'no-store'}`}>
                      {c.merchant?.store?.name || 'No Store'}
                    </span>
                  </div>

                  {/* Metrics Grid */}
                  <div className="customer-card-metrics">
                    <div className="metric">
                      <ShoppingCart size={16} />
                      <div>
                        <span className="metric-value">{orderCount.toLocaleString()}</span>
                        <span className="metric-label">Orders</span>
                      </div>
                    </div>
                    <div className="metric">
                      <DollarSign size={16} />
                      <div>
                        <span className="metric-value" style={{ color: totalSpent > 0 ? '#16a34a' : 'inherit' }}>
                          ₦{Math.round(totalSpent).toLocaleString()}
                        </span>
                        <span className="metric-label">Total Spent</span>
                      </div>
                    </div>
                    <div className="metric">
                      <Package size={16} />
                      <div>
                        <span className="metric-value" style={{ color: '#0369a1' }}>
                          ₦{Math.round(avgOrderValue).toLocaleString()}
                        </span>
                        <span className="metric-label">Avg Order</span>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="customer-card-footer">
                    <div className="customer-card-date">
                      <Calendar size={14} />
                      <span>Joined {joinedDate}</span>
                    </div>
                    <div className="customer-card-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-sm" onClick={() => loadDetails(c._id || c.id)}>
                        <Eye size={14} />
                        View
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => resetPassword(c._id || c.id, c.email)}
                      >
                        <Key size={14} />
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {pagination.pages > 1 && (
            <div className="customers-pagination">
              <div className="pagination-info">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} customers
              </div>
              <div className="pagination-controls">
                <button
                  className="btn"
                  onClick={() => loadList(pagination.page - 1, searchTerm)}
                  disabled={loading || pagination.page <= 1}
                >
                  <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
                  Previous
                </button>
                <div className="pagination-pages">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(pagination.pages - 4, pagination.page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        className={`btn page-btn ${pageNum === pagination.page ? 'active' : ''}`}
                        onClick={() => loadList(pageNum, searchTerm)}
                        disabled={loading}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="btn"
                  onClick={() => loadList(pagination.page + 1, searchTerm)}
                  disabled={loading || pagination.page >= pagination.pages}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Customer Details Side Panel */}
      {selected && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={() => { setSelected(null); setDetails(null); }}
          />

          {/* Side Panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '680px',
              maxWidth: '90vw',
              backgroundColor: 'var(--bg)',
              zIndex: 1001,
              boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideInRight 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Panel Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--border)',
              backgroundColor: 'var(--panel)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <Users size={20} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Customer Details</h2>
                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
                    {details ? (details.email || 'Loading...') : 'Loading...'}
                  </p>
                </div>
              </div>
              <button
                className="btn"
                onClick={() => { setSelected(null); setDetails(null); }}
                style={{
                  padding: '8px',
                  minWidth: 'auto',
                  background: 'transparent',
                  border: '1px solid var(--border)'
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Panel Content - Scrollable */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px'
            }}>
              {detailsLoading && (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    border: '3px solid var(--border)',
                    borderTop: '3px solid var(--brand)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }}></div>
                  <div className="muted">Loading customer details...</div>
                </div>
              )}

              {details && (
              <div>
              {/* Customer Profile Header */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '700' }}>
                      {details.firstName && details.lastName ? `${details.firstName} ${details.lastName}` : 'Unnamed Customer'}
                    </h3>
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>{details.email}</p>
                  </div>
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: details.isActive ? '#f0fdf4' : '#fef2f2',
                    color: details.isActive ? '#16a34a' : '#dc2626'
                  }}>
                    {details.isActive ? '✓ Active' : '✗ Inactive'}
                  </span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '16px',
                  marginTop: '16px'
                }}>
                  <div style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '4px' }}>Phone</div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{details.phone || 'N/A'}</div>
                  </div>
                  <div style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '4px' }}>Member Since</div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{new Date(details.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                  </div>
                  <div style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '4px' }}>Stores Connected</div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{details.merchant?.store ? 1 : 0}</div>
                  </div>
                  <div style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '4px' }}>Refund Rate</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: (details.refundRate || 0) > 10 ? '#dc2626' : 'inherit' }}>
                      {details.refundRate?.toFixed(1) || 0}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                background: 'var(--panel)',
                border: '1px solid var(--border)',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Financial Overview
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Orders</div>
                    <div style={{ fontSize: '24px', fontWeight: '700' }}>{details.orderCount || details.ordersCount || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Total Spent</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>
                      ₦{Intl.NumberFormat('en-NG').format(Math.round(details.totalSpent || 0))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Avg Order</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#0369a1' }}>
                      ₦{Intl.NumberFormat('en-NG').format(Math.round(details.avgOrderValue || 0))}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Refund Count</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626' }}>{details.refundsCount || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Refund Amount</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626' }}>
                      ₦{Intl.NumberFormat('en-NG').format(Math.round(details.totalRefunded || 0))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Net Spent</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#16a34a' }}>
                      ₦{Intl.NumberFormat('en-NG').format(Math.round(details.netSpent || 0))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Customer Info */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                background: 'var(--panel)',
                border: '1px solid var(--border)',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Account Details
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Loyalty Points</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#f59e0b' }}>{details.loyaltyPoints || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Wallet Balance</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#16a34a' }}>
                      ₦{Intl.NumberFormat('en-NG').format(parseFloat(details.wallet?.balance || 0))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Auth Method</div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: details.authMethod === 'EMAIL' ? '#dbeafe' : '#fef3c7',
                        color: details.authMethod === 'EMAIL' ? '#1e40af' : '#d97706'
                      }}>
                        {details.authMethod || 'EMAIL'}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' }}>Verification Status</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: details.isEmailVerified ? '#f0fdf4' : '#fef2f2',
                        color: details.isEmailVerified ? '#16a34a' : '#dc2626'
                      }}>
                        {details.isEmailVerified ? '✓' : '✗'} Email
                      </span>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: details.isPhoneVerified ? '#f0fdf4' : '#fef2f2',
                        color: details.isPhoneVerified ? '#16a34a' : '#dc2626'
                      }}>
                        {details.isPhoneVerified ? '✓' : '✗'} Phone
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' }}>
                      Tags & Groups ({((details.tags || []).length + (details.groups || []).length)})
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {(details.tags || []).map((tag, idx) => (
                        <span key={`tag-${idx}`} style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: '#f3e8ff',
                          color: '#7c3aed'
                        }}>
                          🏷️ {tag}
                        </span>
                      ))}
                      {(details.groups || []).map((group, idx) => (
                        <span key={`group-${idx}`} style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: '#dbeafe',
                          color: '#1e40af'
                        }}>
                          👥 {group}
                        </span>
                      ))}
                      {(!details.tags || details.tags.length === 0) && (!details.groups || details.groups.length === 0) && (
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No tags or groups</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Merchant/Store Info */}
              {details.merchant && (
                <div style={{
                  padding: '20px',
                  borderRadius: '12px',
                  background: 'var(--panel)',
                  border: '1px solid var(--border)',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Merchant Information
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Store Name</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>{details.merchant.store?.name || 'N/A'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Store Slug</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', fontFamily: 'monospace', color: 'var(--brand)' }}>{details.merchant.store?.slug || 'N/A'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Merchant Email</div>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>{details.merchant.email || 'N/A'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Merchant Username</div>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>{details.merchant.username || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer Addresses */}
              {details.addresses && details.addresses.length > 0 && (
                <div style={{
                  padding: '20px',
                  borderRadius: '12px',
                  background: 'var(--panel)',
                  border: '1px solid var(--border)',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Saved Addresses ({details.addresses.length})
                  </h3>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {details.addresses.map((addr, idx) => (
                      <div key={addr.id} style={{
                        padding: '16px',
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        position: 'relative'
                      }}>
                        {addr.isDefault && (
                          <span style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '700',
                            background: '#dbeafe',
                            color: '#1e40af',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Default
                          </span>
                        )}
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: addr.type === 'SHIPPING' ? '#f0fdf4' : '#fef3c7',
                            color: addr.type === 'SHIPPING' ? '#16a34a' : '#d97706'
                          }}>
                            {addr.type}
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                          {addr.firstName} {addr.lastName}
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                          {addr.address1}
                          {addr.address2 && <>, {addr.address2}</>}<br />
                          {addr.city}, {addr.state} {addr.postalCode}<br />
                          {addr.country}
                        </div>
                        <div style={{ fontSize: '13px', marginTop: '8px', color: 'var(--text-muted)' }}>
                          📞 {addr.phone}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Orders */}
              {details.orders && details.orders.length > 0 && (
                <div style={{
                  padding: '20px',
                  borderRadius: '12px',
                  background: 'var(--panel)',
                  border: '1px solid var(--border)',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Order History ({details.orders.length})
                  </h3>
                  <div style={{ overflow: 'auto' }}>
                    <table className="table" style={{ width: '100%', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ textAlign: 'left' }}>
                          <th>Order ID</th>
                          <th>Amount</th>
                          <th>Currency</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.orders.map(order => (
                          <tr key={order.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '12px 8px' }}>
                              <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: '600', color: 'var(--brand)' }}>
                                {order.id.slice(-8)}
                              </div>
                            </td>
                            <td style={{ padding: '12px 8px', fontWeight: '600', fontSize: '14px' }}>
                              {order.currency === 'NGN' && '₦'}
                              {order.currency === 'USD' && '$'}
                              {order.currency === 'EUR' && '€'}
                              {order.currency === 'GBP' && '£'}
                              {Intl.NumberFormat('en-NG').format(parseFloat(order.total || 0))}
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <span style={{
                                fontFamily: 'monospace',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: 'var(--text-muted)'
                              }}>
                                {order.currency}
                              </span>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                background: order.status === 'DELIVERED' || order.status === 'COMPLETED' ? '#f0fdf4' :
                                          order.status === 'PROCESSING' || order.status === 'PENDING' ? '#fef3c7' :
                                          order.status === 'SHIPPED' ? '#dbeafe' : '#fef2f2',
                                color: order.status === 'DELIVERED' || order.status === 'COMPLETED' ? '#16a34a' :
                                      order.status === 'PROCESSING' || order.status === 'PENDING' ? '#d97706' :
                                      order.status === 'SHIPPED' ? '#1e40af' : '#dc2626'
                              }}>
                                {order.status}
                              </span>
                            </td>
                            <td style={{ padding: '12px 8px', color: 'var(--text-muted)' }}>
                              {new Date(order.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(!details.orders || details.orders.length === 0) && (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  borderRadius: '12px',
                  background: 'var(--panel)',
                  border: '1px solid var(--border)',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>No orders yet</div>
                </div>
              )}

              {/* Recent Refunds */}
              {details.recentRefunds && details.recentRefunds.length > 0 && (
                <div className="card">
                  <div className="card-body">
                    <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Recent Refunds</h3>
                    <div style={{ overflow: 'auto' }}>
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Refund ID</th>
                            <th>Order #</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Reason</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.recentRefunds.map(refund => (
                            <tr key={refund.refundId}>
                              <td style={{ fontWeight: '600' }}>{refund.refundId.slice(-8)}</td>
                              <td>{refund.orderNumber}</td>
                              <td style={{ color: '#dc2626' }}>₦{Intl.NumberFormat('en-NG').format(Math.round(refund.amount || 0))}</td>
                              <td>
                                <span style={{ 
                                  padding: '4px 8px', 
                                  borderRadius: '4px', 
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  background: refund.status === 'completed' ? '#f0fdf4' : 
                                            refund.status === 'pending' ? '#fef3c7' : '#fef2f2',
                                  color: refund.status === 'completed' ? '#16a34a' : 
                                        refund.status === 'pending' ? '#d97706' : '#dc2626'
                                }}>
                                  {refund.status}
                                </span>
                              </td>
                              <td>{refund.reason || 'N/A'}</td>
                              <td>{new Date(refund.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer Actions */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                background: 'var(--panel)',
                border: '1px solid var(--border)',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Customer Actions
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>

                  {/* Activate/Deactivate Actions */}
                  {hasPermission('users.write') && details.isActive && (
                    <button
                      className="btn"
                      onClick={async () => {
                        if (!window.confirm(`Deactivate customer ${details.email}?\n\nThis will prevent them from logging in and making purchases.`)) return;
                        await deactivateCustomer(details.id);
                        await loadDetails(details.id);
                      }}
                      style={{ background: 'var(--warning-bg)', color: 'var(--warning-color)', border: '1px solid var(--warning-color)' }}
                    >
                      <XCircle size={16} />
                      Deactivate Account
                    </button>
                  )}
                  {hasPermission('users.write') && !details.isActive && (
                    <button
                      className="btn"
                      onClick={async () => {
                        await activateCustomer(details.id);
                        await loadDetails(details.id);
                      }}
                      style={{ background: 'var(--success-bg)', color: 'var(--success-color)', border: '1px solid var(--success-color)' }}
                    >
                      <Check size={16} />
                      Activate Account
                    </button>
                  )}

                  {/* Archive Action */}
                  {hasPermission('users.delete') && (
                    <button
                      className="btn"
                      onClick={async () => {
                        if (!window.confirm(`Archive customer ${details.email}?\n\nThis will:\n• Deactivate the customer account\n• Preserve all data (orders, addresses) for compliance\n• This action can be reversed by activating the customer again`)) return;

                        try {
                          const r = await apiWithAccessControl(`/admin/customers/${details.id}/archive`, {
                            method: 'POST',
                            body: JSON.stringify({ reason: 'Customer archived by admin' })
                          });
                          const d = await r.json();
                          if (!r.ok) {
                            toast.error(d.error || 'Archive failed');
                            return;
                          }
                          toast.success(d.message || 'Customer archived successfully');
                          await loadList(pagination.page, searchTerm);
                          setDetails(null);
                        } catch {
                          toast.error('Network error. Please try again.');
                        }
                      }}
                      style={{ background: 'var(--warning-bg)', color: 'var(--warning-color)', border: '1px solid var(--warning-color)' }}
                    >
                      <Archive size={16} />
                      Archive Customer
                    </button>
                  )}

                  {/* Permanently Delete Action */}
                  {hasPermission('users.delete') && (
                    <button
                      className="btn"
                      onClick={async () => {
                        if (!window.confirm(`⚠️ PERMANENTLY DELETE customer ${details.email}?\n\nThis will:\n• Permanently remove the customer account\n• Delete all saved addresses\n\n⚠️ THIS CANNOT BE UNDONE!\n\nNote: Customers with orders cannot be deleted (use Archive instead).\n\nType "DELETE" to confirm.`)) return;

                        const confirmation = prompt('Type "DELETE" to confirm permanent deletion:');
                        if (confirmation !== 'DELETE') {
                          toast.error('Deletion cancelled. Confirmation text did not match.');
                          return;
                        }

                        try {
                          const r = await apiWithAccessControl(`/admin/customers/${details.id}`, {
                            method: 'DELETE',
                            body: JSON.stringify({ confirm: 'DELETE' })
                          });
                          const d = await r.json();
                          if (!r.ok) {
                            toast.error(d.error || 'Deletion failed');
                            if (d.details) {
                              toast.error(`Customer has ${d.details.orders} orders. Please archive instead.`);
                            }
                            return;
                          }
                          toast.success(d.message || 'Customer permanently deleted');
                          await loadList(pagination.page, searchTerm);
                          setDetails(null);
                        } catch {
                          toast.error('Network error. Please try again.');
                        }
                      }}
                      style={{ background: '#dc2626', color: 'white', border: '1px solid #dc2626' }}
                    >
                      <Trash2 size={16} />
                      Permanently Delete
                    </button>
                  )}

                </div>
              </div>
            </div>
          )}
            </div>
          </div>
        </>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={() => setShowPasswordModal(false)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '32px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#16a34a' }}>
                ✓ Password Reset Successful
              </h2>
              <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                Temporary password for <strong>{resetUserEmail}</strong>
              </p>

              <div style={{
                background: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                position: 'relative'
              }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' }}>
                  Temporary Password
                </div>
                <div style={{
                  fontFamily: 'monospace',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#111827',
                  letterSpacing: '2px',
                  userSelect: 'all'
                }}>
                  {temporaryPassword}
                </div>
              </div>

              <button
                className="btn"
                onClick={() => {
                  navigator.clipboard.writeText(temporaryPassword);
                  toast.success('Password copied to clipboard!');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '12px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                📋 Copy Password
              </button>

              <div style={{
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.5' }}>
                  <strong>⚠️ Important:</strong> This password will not be shown again. Please share it securely with the customer.
                </div>
              </div>

              <button
                className="btn"
                onClick={() => setShowPasswordModal(false)}
                style={{ width: '100%', padding: '12px' }}
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Templates(){
  return (
    <div>
      <div className="admin-h1">Email Templates</div>
      <div className="card">
        <div className="card-body">
          <div className="card-title">Template Management</div>
          <p className="muted">Manage email templates for various system notifications.</p>
          <div style={{ display:'grid', gap:12, marginTop:16 }}>
            <div className="card" style={{ border:'1px solid var(--border)' }}>
              <div className="card-body">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontWeight:600 }}>Welcome Email</div>
                    <div className="muted" style={{ fontSize:12 }}>Sent to new merchants upon registration</div>
                  </div>
                  <button className="btn">Edit</button>
                </div>
              </div>
            </div>
            <div className="card" style={{ border:'1px solid var(--border)' }}>
              <div className="card-body">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontWeight:600 }}>Application Approved</div>
                    <div className="muted" style={{ fontSize:12 }}>Sent when merchant application is approved</div>
                  </div>
                  <button className="btn">Edit</button>
                </div>
              </div>
            </div>
            <div className="card" style={{ border:'1px solid var(--border)' }}>
              <div className="card-body">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontWeight:600 }}>Payment Notification</div>
                    <div className="muted" style={{ fontSize:12 }}>Sent to merchants for successful payments</div>
                  </div>
                  <button className="btn">Edit</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Analytics(){
  const { apiWithAccessControl } = useAccessControl();
  const [analytics, setAnalytics] = useState({
    platform: null,
    merchants: null,
    customers: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setError('');
        setLoading(true);
        
        const [platformRes, merchantsRes, customersRes] = await Promise.allSettled([
          apiWithAccessControl('/admin/analytics/platform'),
          apiWithAccessControl('/admin/analytics/merchants'),
          apiWithAccessControl('/admin/analytics/customers')
        ]);

        const newAnalytics = {};
        
        if (platformRes.status === 'fulfilled' && platformRes.value.ok) {
          newAnalytics.platform = await platformRes.value.json();
        }
        
        if (merchantsRes.status === 'fulfilled' && merchantsRes.value.ok) {
          newAnalytics.merchants = await merchantsRes.value.json();
        }
        
        if (customersRes.status === 'fulfilled' && customersRes.value.ok) {
          newAnalytics.customers = await customersRes.value.json();
        }

        setAnalytics(newAnalytics);
      } catch (e) {
        console.error('Analytics load error:', e);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  if (loading) {
    return (
      <div>
        <div className="admin-h1">Analytics & Insights</div>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          background: 'var(--bg)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: 40, 
              height: 40, 
              border: '3px solid var(--border)', 
              borderTop: '3px solid var(--brand)', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <div className="muted">Loading analytics...</div>
          </div>
        </div>
      </div>
    );
  }

  const platformData = analytics.platform?.analytics || {};
  const merchantData = analytics.merchants?.analytics || {};
  const customerData = analytics.customers?.analytics || {};

  return (
    <div>
      <div className="admin-h1">Analytics & Insights</div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '2px solid var(--border)',
        paddingBottom: '0'
      }}>
        <button
          onClick={() => setActiveTab('overview')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'overview' ? 'var(--brand)' : 'transparent',
            color: activeTab === 'overview' ? 'white' : 'var(--text)',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'overview' ? 600 : 400,
            transition: 'all 0.2s'
          }}
        >
          Platform Overview
        </button>
        <button
          onClick={() => setActiveTab('merchants')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'merchants' ? 'var(--brand)' : 'transparent',
            color: activeTab === 'merchants' ? 'white' : 'var(--text)',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'merchants' ? 600 : 400,
            transition: 'all 0.2s'
          }}
        >
          Merchant Analytics
        </button>
        <button
          onClick={() => setActiveTab('comparison')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'comparison' ? 'var(--brand)' : 'transparent',
            color: activeTab === 'comparison' ? 'white' : 'var(--text)',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'comparison' ? 600 : 400,
            transition: 'all 0.2s'
          }}
        >
          Merchants Comparison
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {/* Platform Overview Tab */}
      {activeTab === 'overview' && (
        <>
      <div className="two-col">
        <div className="section">
          <div className="section-header">Platform Metrics</div>
          <div className="section-body">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0,1fr))', gap:16 }}>
              <div className="stat">
                <div className="label">Daily Active Users</div>
                <div className="value">{platformData.dailyActiveUsers?.toLocaleString() || 0}</div>
              </div>
              <div className="stat">
                <div className="label">Monthly Active Users</div>
                <div className="value">{platformData.monthlyActiveUsers?.toLocaleString() || 0}</div>
              </div>
              <div className="stat">
                <div className="label">Conversion Rate</div>
                <div className="value">{platformData.conversionRate?.toFixed(1) || 0}%</div>
              </div>
              <div className="stat">
                <div className="label">Payment Success Rate</div>
                <div className="value">{platformData.paymentSuccessRate?.toFixed(1) || 0}%</div>
              </div>
            </div>
          </div>
        </div>
        <div className="section">
          <div className="section-header">Top Performing Merchants</div>
          <div className="section-body">
            {merchantData.topMerchants?.length > 0 ? (
              <div className="transaction-list">
                {merchantData.topMerchants.slice(0, 5).map((merchant, index) => (
                  <div key={index} className="transaction-item">
                    <div className="transaction-icon">
                      <Store size={16} color="var(--brand)" />
                    </div>
                    <div className="transaction-details">
                      <div className="transaction-name">{merchant.storeName}</div>
                      <div className="transaction-desc">
                        ₦{Intl.NumberFormat('en-NG').format(merchant.monthlyRevenue)} GMV this month
                      </div>
                    </div>
                    <div className="transaction-amount credit">
                      {merchant.totalOrders} orders
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="muted">No merchant data available</div>
            )}
          </div>
        </div>
      </div>

      <div className="two-col" style={{ marginTop: '24px' }}>
        <div className="section">
          <div className="section-header">Customer Analytics</div>
          <div className="section-body">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0,1fr))', gap:16, marginBottom: '16px' }}>
              <div className="stat">
                <div className="label">Active Customers</div>
                <div className="value">{customerData.activeCustomers?.toLocaleString() || 0}</div>
              </div>
              <div className="stat">
                <div className="label">Repeat Customers</div>
                <div className="value">{customerData.repeatCustomers?.toLocaleString() || 0}</div>
              </div>
              <div className="stat">
                <div className="label">New This Month</div>
                <div className="value">{customerData.newCustomers?.toLocaleString() || 0}</div>
              </div>
              <div className="stat">
                <div className="label">Growth Rate</div>
                <div className="value">{customerData.customerGrowthRate?.toFixed(1) || 0}%</div>
              </div>
            </div>
            
            {customerData.fraudCustomers?.length > 0 && (
              <div>
                <h4 style={{ margin: '16px 0 8px 0', color: '#dc2626' }}>High-Risk Customers</h4>
                <div className="transaction-list">
                  {customerData.fraudCustomers.slice(0, 3).map((customer, index) => (
                    <div key={index} className="transaction-item">
                      <div className="transaction-icon" style={{ background: '#fef2f2' }}>
                        <AlertCircle size={16} color="#dc2626" />
                      </div>
                      <div className="transaction-details">
                        <div className="transaction-name">{customer.email}</div>
                        <div className="transaction-desc">
                          {customer.failedCount} failed payments this month
                        </div>
                      </div>
                      <div className="transaction-amount" style={{ color: '#dc2626' }}>
                        High Risk
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="section">
          <div className="section-header">High-Risk Merchants</div>
          <div className="section-body">
            {merchantData.highRiskMerchants?.length > 0 ? (
              <div className="transaction-list">
                {merchantData.highRiskMerchants.slice(0, 5).map((merchant, index) => (
                  <div key={index} className="transaction-item">
                    <div className="transaction-icon" style={{ background: '#fef2f2' }}>
                      <AlertCircle size={16} color="#dc2626" />
                    </div>
                    <div className="transaction-details">
                      <div className="transaction-name">{merchant.storeName}</div>
                      <div className="transaction-desc">
                        {merchant.disputeCount} disputes, {merchant.chargebackCount} chargebacks
                      </div>
                    </div>
                    <div className="transaction-amount" style={{ color: '#dc2626' }}>
                      High Risk
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="muted">No high-risk merchants detected</div>
            )}
          </div>
        </div>
      </div>
        </>
      )}

      {/* Merchant Analytics Tab */}
      {activeTab === 'merchants' && <MerchantAnalyticsView />}

      {/* Merchants Comparison Tab */}
      {activeTab === 'comparison' && <MerchantsComparisonView />}
    </div>
  );
}

// Merchant Analytics View Component
function MerchantAnalyticsView() {
  const { apiWithAccessControl } = useAccessControl();
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchantAnalytics, setMerchantAnalytics] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [period, setPeriod] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load merchants list
  useEffect(() => {
    const loadMerchants = async () => {
      try {
        setLoading(true);
        const response = await apiWithAccessControl('/admin/merchants');
        if (response.ok) {
          const data = await response.json();
          console.log('Merchants loaded:', data);
          setMerchants(data.merchants || []);
          if (data.merchants && data.merchants.length > 0) {
            setSelectedMerchant(data.merchants[0].id);
          }
        }
      } catch (e) {
        console.error('Load merchants error:', e);
        setError('Failed to load merchants');
      } finally {
        setLoading(false);
      }
    };
    loadMerchants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load merchant analytics when merchant or time range changes
  useEffect(() => {
    if (!selectedMerchant) return;

    const loadMerchantAnalytics = async () => {
      try {
        setError('');
        console.log('Loading analytics for merchant:', selectedMerchant, 'timeRange:', timeRange, 'period:', period);
        const [analyticsRes, revenueRes] = await Promise.allSettled([
          apiWithAccessControl(`/admin/analytics/merchant/${selectedMerchant}?timeRange=${timeRange}`),
          apiWithAccessControl(`/admin/analytics/merchant/${selectedMerchant}/revenue?timeRange=${timeRange}&period=${period}`)
        ]);

        console.log('Analytics response:', analyticsRes);
        console.log('Revenue response:', revenueRes);

        if (analyticsRes.status === 'fulfilled' && analyticsRes.value.ok) {
          const data = await analyticsRes.value.json();
          console.log('Merchant analytics data:', data);
          setMerchantAnalytics(data.analytics);
        } else if (analyticsRes.status === 'fulfilled') {
          const errorText = await analyticsRes.value.text();
          console.error('Analytics API error:', analyticsRes.value.status, errorText);
        }

        if (revenueRes.status === 'fulfilled' && revenueRes.value.ok) {
          const data = await revenueRes.value.json();
          console.log('Revenue data:', data);
          setRevenueData(data.data);
        } else if (revenueRes.status === 'fulfilled') {
          const errorText = await revenueRes.value.text();
          console.error('Revenue API error:', revenueRes.value.status, errorText);
        }
      } catch (e) {
        console.error('Load merchant analytics error:', e);
        setError('Failed to load merchant analytics');
      }
    };

    loadMerchantAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMerchant, timeRange, period]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid var(--border)',
          borderTop: '3px solid var(--brand)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        <div className="muted">Loading merchants...</div>
      </div>
    );
  }

  if (merchants.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="muted">No merchants found</div>
      </div>
    );
  }

  const selectedMerchantData = merchants.find(m => m.id === selectedMerchant);

  return (
    <div>
      {/* Merchant Selector */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
          Select Merchant
        </label>
        <select
          className="input"
          value={selectedMerchant || ''}
          onChange={(e) => setSelectedMerchant(e.target.value)}
          style={{ maxWidth: '400px' }}
        >
          {merchants.map(merchant => (
            <option key={merchant.id} value={merchant.id}>
              {merchant.username} ({merchant.email}) - {merchant.store?.name || 'No Store'}
            </option>
          ))}
        </select>
      </div>

      {/* Time Range Filter */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
            Time Range
          </label>
          <select
            className="input"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
            Period
          </label>
          <select
            className="input"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {/* Merchant Info Card */}
      {selectedMerchantData && (
        <div className="section" style={{ marginBottom: '24px' }}>
          <div className="section-header">Merchant Information</div>
          <div className="section-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div className="stat">
                <div className="label">Email</div>
                <div className="value" style={{ fontSize: '16px' }}>{selectedMerchantData.email}</div>
              </div>
              <div className="stat">
                <div className="label">Tier</div>
                <div className="value" style={{ fontSize: '16px' }}>{selectedMerchantData.merchantTier || 'FREE'}</div>
              </div>
              <div className="stat">
                <div className="label">Store Status</div>
                <div className="value" style={{ fontSize: '16px' }}>
                  {selectedMerchantData.store?.applicationStatus || 'No Store'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Cards */}
      {merchantAnalytics !== null ? (
        <>
          <div className="two-col" style={{ marginBottom: '24px' }}>
            <div className="section">
              <div className="section-header">Revenue Metrics</div>
              <div className="section-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div className="stat">
                    <div className="label">Total Revenue</div>
                    <div className="value">₦{merchantAnalytics.totalRevenue?.toLocaleString() || 0}</div>
                  </div>
                  <div className="stat">
                    <div className="label">Average Order Value</div>
                    <div className="value">₦{merchantAnalytics.avgOrderValue?.toLocaleString() || 0}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section">
              <div className="section-header">Order Metrics</div>
              <div className="section-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div className="stat">
                    <div className="label">Total Orders</div>
                    <div className="value">{merchantAnalytics.totalOrders?.toLocaleString() || 0}</div>
                  </div>
                  <div className="stat">
                    <div className="label">Conversion Rate</div>
                    <div className="value">{merchantAnalytics.conversionRate?.toFixed(1) || 0}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="section" style={{ marginBottom: '24px' }}>
            <div className="section-header">Customer Metrics</div>
            <div className="section-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div className="stat">
                  <div className="label">Total Customers</div>
                  <div className="value">{merchantAnalytics.totalCustomers?.toLocaleString() || 0}</div>
                </div>
                <div className="stat">
                  <div className="label">New Customers</div>
                  <div className="value">{merchantAnalytics.newCustomers?.toLocaleString() || 0}</div>
                </div>
                <div className="stat">
                  <div className="label">Returning Customers</div>
                  <div className="value">{merchantAnalytics.returningCustomers?.toLocaleString() || 0}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Trend Chart */}
          {revenueData && revenueData.length > 0 && (
            <div className="section">
              <div className="section-header">Revenue Trend</div>
              <div className="section-body">
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="value" stroke="var(--brand)" name="Revenue (₦)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Recent Orders */}
          {merchantAnalytics.recentOrders && merchantAnalytics.recentOrders.length > 0 && (
            <div className="section" style={{ marginTop: '24px' }}>
              <div className="section-header">Recent Orders</div>
              <div className="section-body">
                <div className="transaction-list">
                  {merchantAnalytics.recentOrders.map((order, index) => (
                    <div key={index} className="transaction-item">
                      <div className="transaction-icon">
                        <ShoppingBag size={16} color="var(--brand)" />
                      </div>
                      <div className="transaction-details">
                        <div className="transaction-name">Order #{order.id}</div>
                        <div className="transaction-desc">
                          {order.customerName} - {new Date(order.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="transaction-amount credit">
                        ₦{order.total?.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            width: 40,
            height: 40,
            border: '3px solid var(--border)',
            borderTop: '3px solid var(--brand)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div className="muted">Loading merchant analytics...</div>
        </div>
      )}
    </div>
  );
}

// Merchants Comparison View Component
function MerchantsComparisonView() {
  const { apiWithAccessControl } = useAccessControl();
  const [comparison, setComparison] = useState([]);
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadComparison = async () => {
      try {
        setLoading(true);
        setError('');
        console.log('Loading merchants comparison with timeRange:', timeRange);
        const response = await apiWithAccessControl(`/admin/analytics/merchants/comparison?timeRange=${timeRange}`);
        console.log('Comparison API response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('Comparison data received:', data);
          setComparison(data.merchants || []);
        } else {
          const errorText = await response.text();
          console.error('Comparison API error:', response.status, errorText);
          setError(`Failed to load: ${errorText}`);
        }
      } catch (e) {
        console.error('Load comparison error:', e);
        setError('Failed to load merchants comparison');
      } finally {
        setLoading(false);
      }
    };

    loadComparison();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid var(--border)',
          borderTop: '3px solid var(--brand)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        <div className="muted">Loading comparison...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Time Range Filter */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
          Time Range
        </label>
        <select
          className="input"
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          style={{ width: 'auto' }}
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="1y">Last Year</option>
        </select>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {/* Comparison Table */}
      <div className="section">
        <div className="section-header">All Merchants Comparison</div>
        <div className="section-body">
          {comparison.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Rank</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Merchant</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Store</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Tier</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Revenue</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Orders</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Avg Order Value</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((merchant, index) => (
                    <tr key={merchant.merchantId} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '12px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: index < 3 ? 'var(--brand)' : 'var(--border)',
                          color: index < 3 ? 'white' : 'var(--text)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600
                        }}>
                          {index + 1}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: 600 }}>{merchant.merchantEmail}</div>
                        <div className="muted" style={{ fontSize: '12px' }}>{merchant.merchantUsername}</div>
                      </td>
                      <td style={{ padding: '12px' }}>{merchant.storeName || 'No Store'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: merchant.merchantTier === 'PREMIUM' ? '#dcfce7' :
                                     merchant.merchantTier === 'PRO' ? '#dbeafe' : '#f3f4f6',
                          color: merchant.merchantTier === 'PREMIUM' ? '#16a34a' :
                                merchant.merchantTier === 'PRO' ? '#2563eb' : '#6b7280'
                        }}>
                          {merchant.merchantTier}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                        ₦{merchant.totalRevenue?.toLocaleString() || 0}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        {merchant.totalOrders?.toLocaleString() || 0}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        ₦{merchant.avgOrderValue?.toLocaleString() || 0}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: merchant.storeStatus === 'APPROVED' ? '#dcfce7' : '#fef2f2',
                          color: merchant.storeStatus === 'APPROVED' ? '#16a34a' : '#dc2626'
                        }}>
                          {merchant.storeStatus || 'No Store'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="muted" style={{ textAlign: 'center', padding: '40px' }}>
              No merchant data available for comparison
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {comparison.length > 0 && (
        <div className="two-col" style={{ marginTop: '24px' }}>
          <div className="section">
            <div className="section-header">Platform Summary</div>
            <div className="section-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                <div className="stat">
                  <div className="label">Total Merchants</div>
                  <div className="value">{comparison.length}</div>
                </div>
                <div className="stat">
                  <div className="label">Active Merchants</div>
                  <div className="value">
                    {comparison.filter(m => m.storeStatus === 'APPROVED').length}
                  </div>
                </div>
                <div className="stat">
                  <div className="label">Total Revenue</div>
                  <div className="value">
                    ₦{comparison.reduce((sum, m) => sum + (m.totalRevenue || 0), 0).toLocaleString()}
                  </div>
                </div>
                <div className="stat">
                  <div className="label">Total Orders</div>
                  <div className="value">
                    {comparison.reduce((sum, m) => sum + (m.totalOrders || 0), 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="section">
            <div className="section-header">Tier Distribution</div>
            <div className="section-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div className="stat">
                  <div className="label">FREE</div>
                  <div className="value">
                    {comparison.filter(m => m.merchantTier === 'FREE').length}
                  </div>
                </div>
                <div className="stat">
                  <div className="label">PRO</div>
                  <div className="value">
                    {comparison.filter(m => m.merchantTier === 'PRO').length}
                  </div>
                </div>
                <div className="stat">
                  <div className="label">PREMIUM</div>
                  <div className="value">
                    {comparison.filter(m => m.merchantTier === 'PREMIUM').length}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Logs(){
  return (
    <div>
      <div className="admin-h1">Audit Logs</div>
      <div className="card">
        <div className="card-body">
          <div className="card-title">System Activity Log</div>
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            <select className="input" style={{ width:'auto' }}>
              <option>All Actions</option>
              <option>Login/Logout</option>
              <option>Merchant Actions</option>
              <option>Financial Actions</option>
            </select>
            <input className="input" placeholder="Search logs..." style={{ flex:1 }} />
            <button className="btn">Filter</button>
          </div>
          <div className="card" style={{ border:'1px solid var(--border)', maxHeight:400, overflow:'auto' }}>
            <div className="card-body">
              <div style={{ display:'grid', gap:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border-light)' }}>
                  <div>
                    <div style={{ fontWeight:600, fontSize:14 }}>Admin login</div>
                    <div className="muted" style={{ fontSize:12 }}>admin@revstar.com logged in from 192.168.1.1</div>
                  </div>
                  <div className="muted" style={{ fontSize:12 }}>2 minutes ago</div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border-light)' }}>
                  <div>
                    <div style={{ fontWeight:600, fontSize:14 }}>Merchant approved</div>
                    <div className="muted" style={{ fontSize:12 }}>Tech Store Pro application approved by admin@revstar.com</div>
                  </div>
                  <div className="muted" style={{ fontSize:12 }}>1 hour ago</div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border-light)' }}>
                  <div>
                    <div style={{ fontWeight:600, fontSize:14 }}>Payout processed</div>
                    <div className="muted" style={{ fontSize:12 }}>₦15,000 payout to Fashion Hub processed</div>
                  </div>
                  <div className="muted" style={{ fontSize:12 }}>3 hours ago</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RBAC(){
  const { apiWithAccessControl } = useAccessControl();
  const [activeTab, setActiveTab] = useState('overview');
  const [permissions, setPermissions] = useState(null);
  const [staff, setStaff] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [system, setSystem] = useState(null);
  const [matrix, setMatrix] = useState([]);
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Staff management states
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [newRole, setNewRole] = useState('');

  // Invitation states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');

  // Role permission management states
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});

  // Password reset modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [resetUserEmail, setResetUserEmail] = useState('');
  const toast = useToast();

  // Helper function for role badge colors
  const getRoleBadgeColor = (role) => {
    const roleUpper = role?.toUpperCase();
    switch (roleUpper) {
      case 'ADMIN':
        return { bg: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)', color: 'white' };
      case 'TECH_SUPPORT':
        return { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' };
      case 'MARKETING':
        return { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' };
      case 'ACCOUNTING':
        return { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' };
      case 'CUSTOMER_SUPPORT':
        return { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' };
      default:
        return { bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#374151' };
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [permsRes, staffRes, invitationsRes, auditRes, systemRes, matrixRes, categoriesRes] = await Promise.all([
        apiWithAccessControl('/admin/rbac/permissions'),
        apiWithAccessControl('/admin/rbac/staff'),
        apiWithAccessControl('/admin/rbac/staff/invitations'),
        apiWithAccessControl('/admin/rbac/audit'),
        apiWithAccessControl('/admin/rbac/system'),
        apiWithAccessControl('/admin/rbac/matrix'),
        apiWithAccessControl('/admin/rbac/permissions/categories')
      ]);

      if (permsRes.ok) {
        const permsData = await permsRes.json();
        // Handle both direct and wrapped response formats
        setPermissions(permsData.data || permsData);
      }

      if (staffRes.ok) {
        const staffData = await staffRes.json();
        setStaff(staffData.staff || []);
      }

      if (invitationsRes.ok) {
        const invitationsData = await invitationsRes.json();
        setInvitations(invitationsData.invitations || []);
      }

      if (auditRes.ok) {
        const auditData = await auditRes.json();
        setAuditLogs(auditData.auditLogs || []);
      }

      if (systemRes.ok) {
        const systemData = await systemRes.json();
        // Handle wrapped response format
        const system = systemData.system || systemData.data?.system || systemData;
        setSystem(system);
      }

      if (matrixRes.ok) {
        const matrixData = await matrixRes.json();
        // Handle wrapped response format
        const matrix = matrixData.matrix || matrixData.data?.matrix || [];
        setMatrix(matrix);
      }

      if (categoriesRes.ok) {
        const categoriesData = await categoriesRes.json();
        // Handle wrapped response format
        const categories = categoriesData.categories || categoriesData.data?.categories || [];
        setPermissionCategories(categories);
      }

    } catch (e) {
      setError('Failed to load RBAC data');
    } finally {
      setLoading(false);
    }
  };

  const updateStaffRole = async (userId, role) => {
    try {
      const csrfRes = await apiWithAccessControl('/admin/session/csrf');
      const csrfData = await csrfRes.json();

      const res = await apiWithAccessControl(`/admin/rbac/staff/${userId}/role`, {
        method: 'POST',
        headers: { 'x-csrf-token': csrfData.csrfToken },
        body: JSON.stringify({ staffRole: role })
      });

      if (res.ok) {
        setSuccess('Staff role updated successfully');
        await loadData();
        setSelectedStaff(null);
        setNewRole('');
      } else {
        const errorData = await res.json();
        if (res.status === 403) {
          // Access denied - could show a global notification here
          setError('Access denied: Only admin users can change roles');
        } else {
          setError(errorData.error || 'Failed to update role');
        }
      }
    } catch (e) {
      setError('Failed to update role');
    }
  };

  const resetStaffPassword = async (staffId, staffEmail) => {
    if (!window.confirm(`Are you sure you want to reset the password for ${staffEmail}?\n\nA new temporary password will be generated and shown to you.`)) return;
    setError('');
    setSuccess('');
    try {
      const res = await apiWithAccessControl(`/admin/users/${staffId}/reset-password`, { method: 'POST' });
      const data = await res.json();
      if (res.ok && data.success) {
        setTemporaryPassword(data.data.temporaryPassword);
        setResetUserEmail(staffEmail);
        setShowPasswordModal(true);
        toast.success('Password reset successfully!');
        setSuccess('Staff password reset successfully');
        await loadData();
      } else {
        setError(data.error || 'Failed to reset password');
        toast.error(data.error || 'Failed to reset password');
      }
    } catch (e) {
      setError('Failed to reset password');
      toast.error('Network error. Please try again.');
    }
  };

  const sendInvitation = async () => {
    try {
      const csrfRes = await apiWithAccessControl('/admin/session/csrf');
      const csrfData = await csrfRes.json();
      
      const res = await apiWithAccessControl('/admin/rbac/staff/invite', {
        method: 'POST',
        headers: { 'x-csrf-token': csrfData.csrfToken },
        body: JSON.stringify({ 
          email: inviteEmail, 
          staffRole: inviteRole, 
          message: inviteMessage 
        })
      });

      if (res.ok) {
        setSuccess('Staff invitation sent successfully');
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteRole('');
        setInviteMessage('');
        await loadData();
      } else {
        const errorData = await res.json();
        setError(errorData.error || 'Failed to send invitation');
      }
    } catch (e) {
      setError('Failed to send invitation');
    }
  };

  const updateRolePermissions = async (role, permissions) => {
    try {
      const csrfRes = await api('/admin/session/csrf');
      const csrfData = await csrfRes.json();
      
      const res = await api(`/admin/rbac/roles/${role}/permissions`, {
        method: 'POST',
        headers: { 'x-csrf-token': csrfData.csrfToken },
        body: JSON.stringify({ permissions })
      });

      if (res.ok) {
        setSuccess('Role permissions updated successfully');
        await loadData();
        setSelectedRole(null);
      } else {
        const errorData = await res.json();
        setError(errorData.error || 'Failed to update permissions');
      }
    } catch (e) {
      setError('Failed to update permissions');
    }
  };

  const handlePermissionToggle = (role, permission) => {
    const currentPermissions = rolePermissions[role] || system?.rolePermissions?.[role] || [];

    // Don't allow modifying ADMIN permissions if they have "*" (all permissions)
    if (role === 'ADMIN' && currentPermissions.includes('*')) {
      setError('ADMIN role has all permissions (*). Cannot modify individual permissions.');
      return;
    }

    const newPermissions = currentPermissions.includes(permission)
      ? currentPermissions.filter(p => p !== permission)
      : [...currentPermissions, permission];

    setRolePermissions(prev => ({
      ...prev,
      [role]: newPermissions
    }));
  };

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '3rem'
      }}>
        <div style={{
          width: 56,
          height: 56,
          border: '4px solid transparent',
          borderTop: '4px solid #1a6f63',
          borderRight: '4px solid #155a51',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          margin: '0 auto 24px',
          boxShadow: '0 4px 12px rgba(26, 111, 99, 0.2)'
        }}></div>
        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Loading RBAC System...
        </div>
        <div className="muted" style={{ marginTop: '8px', fontSize: '14px' }}>Please wait</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 32px' }}>
      {/* Modern Header with Gradient */}
      <div style={{
        marginBottom: '32px',
        padding: '32px',
        background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(26, 111, 99, 0.2)',
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <Shield size={32} strokeWidth={2.5} />
          <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>
            Role-Based Access Control
          </h1>
        </div>
        <p style={{ margin: 0, opacity: 0.9, fontSize: '15px', fontWeight: '500' }}>
          Manage staff permissions, roles, and access control across your platform
        </p>
      </div>

      {/* Modern Alert Messages */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'start',
          gap: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          border: '2px solid #fecaca',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(220, 38, 38, 0.1)'
        }}>
          <AlertCircle size={20} color="#dc2626" strokeWidth={2.5} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1, color: '#dc2626', fontWeight: '600', fontSize: '14px' }}>{error}</div>
          <button
            onClick={() => setError('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', lineHeight: 0 }}
          >
            <X size={18} color="#dc2626" />
          </button>
        </div>
      )}

      {success && (
        <div style={{
          display: 'flex',
          alignItems: 'start',
          gap: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          border: '2px solid #bbf7d0',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(22, 163, 74, 0.1)'
        }}>
          <Check size={20} color="#16a34a" strokeWidth={2.5} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1, color: '#16a34a', fontWeight: '600', fontSize: '14px' }}>{success}</div>
          <button
            onClick={() => setSuccess('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', lineHeight: 0 }}
          >
            <X size={18} color="#16a34a" />
          </button>
        </div>
      )}

      {/* Modern Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '32px',
        padding: '6px',
        background: 'var(--bg)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        border: '1px solid var(--border)',
        flexWrap: 'wrap'
      }}>
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'staff', label: 'Staff Management', icon: Users },
          { id: 'roles', label: 'Role Permissions', icon: Settings },
          { id: 'audit', label: 'Audit Logs', icon: Activity }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: '1',
                minWidth: '140px',
                padding: '12px 20px',
                border: 'none',
                background: isActive ? 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)' : 'transparent',
                color: isActive ? 'white' : 'var(--text-muted)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isActive ? '0 4px 12px rgba(26, 111, 99, 0.3)' : 'none',
                transform: isActive ? 'translateY(-2px)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'var(--panel)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'none';
                }
              }}
            >
              <Icon size={18} strokeWidth={2.5} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Current User Permissions */}
          {permissions && (
            <div style={{
              marginBottom: 32,
              padding: '28px',
              background: 'linear-gradient(135deg, #1a6f6315 0%, #155a5115 100%)',
              border: '2px solid #1a6f6330',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(26, 111, 99, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative Background Element */}
              <div style={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                background: 'radial-gradient(circle, rgba(26, 111, 99, 0.15) 0%, transparent 70%)',
                borderRadius: '50%'
              }}></div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', position: 'relative' }}>
                <div style={{
                  padding: '12px',
                  background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(26, 111, 99, 0.3)',
                  display: 'flex'
                }}>
                  <User size={24} color="white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text)' }}>
                    Your Permissions
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'var(--text-muted)', fontWeight: '500' }}>
                    Current access level and capabilities
                  </p>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 20,
                position: 'relative'
              }}>
                <div style={{
                  padding: '20px',
                  background: 'var(--panel)',
                  borderRadius: '12px',
                  border: '1px solid var(--border)',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                    fontWeight: '700',
                    letterSpacing: '0.5px',
                    marginBottom: '12px',
                    textTransform: 'uppercase'
                  }}>
                    Your Role
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '700',
                    background: getRoleBadgeColor(permissions.role).bg,
                    color: getRoleBadgeColor(permissions.role).color,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                  }}>
                    {permissions.role}
                  </div>
                </div>

                <div style={{
                  padding: '20px',
                  background: 'var(--panel)',
                  borderRadius: '12px',
                  border: '1px solid var(--border)',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                    fontWeight: '700',
                    letterSpacing: '0.5px',
                    marginBottom: '12px',
                    textTransform: 'uppercase'
                  }}>
                    Total Permissions
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '32px', fontWeight: '800', color: '#1a6f63' }}>
                      {permissions.permissions.length}
                    </span>
                    <span style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: '600' }}>
                      active permissions
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Permission Matrix */}
          {matrix.length > 0 && (
            <div style={{
              padding: '28px',
              background: 'var(--panel)',
              borderRadius: '16px',
              border: '1px solid var(--border)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{
                  padding: '10px',
                  background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
                  borderRadius: '10px',
                  display: 'flex'
                }}>
                  <Shield size={20} color="white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>
                    Permission Matrix
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
                    Overview of all roles and their capabilities
                  </p>
                </div>
              </div>

              <div style={{ overflow: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: '0 10px'
                }}>
                  <thead>
                    <tr style={{ background: 'transparent' }}>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Role</th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Permissions</th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Key Capabilities</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.map(m => {
                      const roleColors = getRoleBadgeColor(m.role);
                      return (
                        <tr key={m.role} style={{
                          background: 'var(--bg)',
                          transition: 'all 0.2s ease'
                        }}>
                          <td style={{
                            padding: '16px',
                            borderTopLeftRadius: '12px',
                            borderBottomLeftRadius: '12px',
                            borderTop: '1px solid var(--border)',
                            borderLeft: '1px solid var(--border)',
                            borderBottom: '1px solid var(--border)'
                          }}>
                            <div style={{
                              display: 'inline-block',
                              padding: '6px 14px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: '700',
                              background: roleColors.bg,
                              color: roleColors.color,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
                            }}>
                              {m.role}
                            </div>
                          </td>
                          <td style={{
                            padding: '16px',
                            borderTop: '1px solid var(--border)',
                            borderBottom: '1px solid var(--border)'
                          }}>
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '6px 12px',
                              background: 'linear-gradient(135deg, #1a6f6320 0%, #155a5120 100%)',
                              borderRadius: '8px',
                              border: '1px solid #1a6f6340'
                            }}>
                              <Key size={16} color="#1a6f63" strokeWidth={2.5} />
                              <span style={{ fontSize: '16px', fontWeight: '700', color: '#1a6f63' }}>
                                {m.permissionCount}
                              </span>
                            </div>
                          </td>
                          <td style={{
                            padding: '16px',
                            borderTopRightRadius: '12px',
                            borderBottomRightRadius: '12px',
                            borderTop: '1px solid var(--border)',
                            borderRight: '1px solid var(--border)',
                            borderBottom: '1px solid var(--border)'
                          }}>
                            <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: '500' }}>
                              {m.permissions.slice(0, 3).join(', ')}
                              {m.permissions.length > 3 && (
                                <span style={{
                                  marginLeft: '8px',
                                  padding: '2px 8px',
                                  background: 'var(--panel)',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  color: '#1a6f63'
                                }}>
                                  +{m.permissions.length - 3} more
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Staff Management Tab */}
      {activeTab === 'staff' && (
        <div>
          {/* Staff List */}
          <div style={{
            marginBottom: 32,
            padding: '28px',
            background: 'var(--panel)',
            borderRadius: '16px',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  padding: '10px',
                  background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
                  borderRadius: '10px',
                  display: 'flex'
                }}>
                  <Users size={20} color="white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>
                    Current Staff
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
                    Manage team members and their roles
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowInviteModal(true)}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(26, 111, 99, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(26, 111, 99, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 111, 99, 0.3)';
                }}
              >
                <Plus size={18} strokeWidth={2.5} />
                <span>Invite Staff</span>
              </button>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              {staff.map(s => {
                const roleColors = getRoleBadgeColor(s.staffRole);
                const isCurrentUser = s._id === permissions?.userId;
                return (
                  <div
                    key={s._id}
                    style={{
                      padding: '20px',
                      background: 'var(--bg)',
                      borderRadius: '12px',
                      border: '1px solid var(--border)',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                      e.currentTarget.style.transform = 'none';
                    }}
                  >
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr auto',
                      gap: '20px',
                      alignItems: 'center'
                    }}>
                      {/* User Icon & Info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          background: roleColors.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}>
                          <User size={24} color={roleColors.color} strokeWidth={2.5} />
                        </div>
                        <div>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '700',
                            color: 'var(--text)',
                            marginBottom: '4px'
                          }}>
                            {s.email}
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '13px',
                            color: 'var(--text-muted)'
                          }}>
                            <Calendar size={14} strokeWidth={2} />
                            <span>Joined {new Date(s.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Role Badge */}
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '700',
                          background: roleColors.bg,
                          color: roleColors.color,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          display: 'inline-block'
                        }}>
                          {s.staffRole}
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setSelectedStaff(s)}
                          disabled={isCurrentUser}
                          style={{
                            padding: '10px 18px',
                            background: isCurrentUser ? 'var(--bg)' : 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
                            color: isCurrentUser ? 'var(--text-muted)' : 'white',
                            border: isCurrentUser ? '2px solid var(--border)' : 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '13px',
                            cursor: isCurrentUser ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.3s ease',
                            opacity: isCurrentUser ? 0.5 : 1,
                            boxShadow: isCurrentUser ? 'none' : '0 2px 8px rgba(26, 111, 99, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentUser) {
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 111, 99, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentUser) {
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(26, 111, 99, 0.3)';
                            }
                          }}
                        >
                          <Edit size={16} strokeWidth={2.5} />
                          <span>Change Role</span>
                        </button>
                        <button
                          onClick={() => resetStaffPassword(s._id || s.id, s.email)}
                          style={{
                            padding: '10px 18px',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '13px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
                          }}
                        >
                          <Key size={16} strokeWidth={2.5} />
                          <span>Reset Password</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>

          {/* Pending Invitations */}
          {invitations && invitations.length > 0 && (
            <div style={{
              padding: '28px',
              background: 'var(--panel)',
              borderRadius: '16px',
              border: '1px solid var(--border)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{
                  padding: '10px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  borderRadius: '10px',
                  display: 'flex'
                }}>
                  <Mail size={20} color="white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>
                    Pending Invitations
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
                    Staff invitations waiting to be accepted
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gap: '12px' }}>
                {invitations.map(inv => {
                  const roleColors = getRoleBadgeColor(inv.staffRole);
                  const isExpired = new Date(inv.expiresAt) < new Date();
                  return (
                    <div
                      key={inv.id}
                      style={{
                        padding: '16px 20px',
                        background: isExpired ? 'var(--bg)' : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                        borderRadius: '12px',
                        border: isExpired ? '1px solid var(--border)' : '2px solid #f59e0b',
                        opacity: isExpired ? 0.6 : 1
                      }}
                    >
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto',
                        gap: '16px',
                        alignItems: 'center'
                      }}>
                        {/* Email & Dates */}
                        <div>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '700',
                            color: 'var(--text)',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <Mail size={16} strokeWidth={2.5} />
                            {inv.email}
                          </div>
                          <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Clock size={14} strokeWidth={2} />
                              <span>Invited {new Date(inv.invitedAt).toLocaleDateString()}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <AlertCircle size={14} strokeWidth={2} color={isExpired ? '#dc2626' : '#f59e0b'} />
                              <span style={{ color: isExpired ? '#dc2626' : '#f59e0b', fontWeight: '600' }}>
                                {isExpired ? 'Expired' : `Expires ${new Date(inv.expiresAt).toLocaleDateString()}`}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Role Badge */}
                        <div style={{
                          padding: '6px 14px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '700',
                          background: roleColors.bg,
                          color: roleColors.color,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                          whiteSpace: 'nowrap'
                        }}>
                          {inv.staffRole}
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            style={{
                              padding: '8px 14px',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontWeight: '600',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 10px rgba(16, 185, 129, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = '0 2px 6px rgba(16, 185, 129, 0.3)';
                            }}
                          >
                            <RotateCcw size={14} strokeWidth={2.5} />
                            Resend
                          </button>
                          <button
                            style={{
                              padding: '8px 14px',
                              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontWeight: '600',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = '0 4px 10px rgba(239, 68, 68, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = '0 2px 6px rgba(239, 68, 68, 0.3)';
                            }}
                          >
                            <X size={14} strokeWidth={2.5} />
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Role Permissions Tab */}
      {activeTab === 'roles' && (
        <div>
          <div style={{
            marginBottom: 24,
            padding: '28px',
            background: 'var(--panel)',
            borderRadius: '16px',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <div style={{
                padding: '10px',
                background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
                borderRadius: '10px',
                display: 'flex'
              }}>
                <Settings size={20} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>
                  Role Permission Management
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
                  Configure access control for each staff role
                </p>
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '12px',
                fontSize: '14px',
                fontWeight: '700',
                color: 'var(--text)',
                letterSpacing: '0.3px'
              }}>
                Select Role to Manage
              </label>
              <div style={{ position: 'relative', maxWidth: '300px' }}>
                <select
                  value={selectedRole || ''}
                  onChange={e => {
                    setSelectedRole(e.target.value);
                    setError('');
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: '2px solid var(--border)',
                    borderRadius: '12px',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    cursor: 'pointer',
                    appearance: 'none',
                    transition: 'all 0.3s ease',
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23667eea\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '20px',
                    paddingRight: '45px'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1a6f63';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(26, 111, 99, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <option value="">Choose a role...</option>
                  {system?.roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {selectedRole && (
            <div style={{
              padding: '28px',
              background: 'var(--panel)',
              borderRadius: '16px',
              border: '1px solid var(--border)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                flexWrap: 'wrap',
                gap: '16px',
                paddingBottom: '20px',
                borderBottom: '2px solid var(--border)'
              }}>
                <div>
                  <h3 style={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: getRoleBadgeColor(selectedRole).bg,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    {selectedRole} Permissions
                  </h3>
                  {selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*') && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      borderRadius: '8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#92400e'
                    }}>
                      <Info size={16} strokeWidth={2.5} />
                      Admin has all permissions (*). Individual permissions cannot be modified.
                    </div>
                  )}
                </div>
                <button
                  onClick={() => updateRolePermissions(selectedRole, rolePermissions[selectedRole] || system?.rolePermissions?.[selectedRole] || [])}
                  disabled={selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*')}
                  style={{
                    padding: '14px 28px',
                    background: selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*')
                      ? 'var(--bg)'
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*')
                      ? 'var(--text-muted)'
                      : 'white',
                    border: selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*')
                      ? '2px solid var(--border)'
                      : 'none',
                    borderRadius: '10px',
                    fontWeight: '700',
                    fontSize: '14px',
                    cursor: selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*')
                      ? 'not-allowed'
                      : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                    opacity: selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*') ? 0.5 : 1,
                    boxShadow: selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*')
                      ? 'none'
                      : '0 4px 12px rgba(16, 185, 129, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!(selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*'))) {
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(selectedRole === 'ADMIN' && system?.rolePermissions?.[selectedRole]?.includes('*'))) {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                      e.currentTarget.style.transform = 'none';
                    }
                  }}
                >
                  <Check size={18} strokeWidth={2.5} />
                  Save Changes
                </button>
              </div>

              <div style={{ maxHeight: '600px', overflowY: 'auto', paddingRight: '8px' }}>
                {permissionCategories.map(category => (
                  <div key={category.id || category.category} style={{ marginBottom: '32px' }}>
                    <div style={{
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, #1a6f6315 0%, #155a5115 100%)',
                      borderRadius: '10px',
                      marginBottom: '16px',
                      border: '1px solid #1a6f6330'
                    }}>
                      <h4 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#1a6f63',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Shield size={18} strokeWidth={2.5} />
                        {category.name || category.category}
                      </h4>
                    </div>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {category.permissions.map(perm => {
                        const permKey = typeof perm === 'string' ? perm : perm.key;
                        const currentPermissions = rolePermissions[selectedRole] || system?.rolePermissions?.[selectedRole] || [];
                        const isChecked = currentPermissions.includes(permKey);
                        const isAdminWithAllPermissions = selectedRole === 'ADMIN' && currentPermissions.includes('*');

                        return (
                          <label
                            key={permKey}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '14px',
                              padding: '14px 16px',
                              background: isChecked
                                ? 'linear-gradient(135deg, #1a6f6315 0%, #155a5115 100%)'
                                : 'var(--bg)',
                              borderRadius: '10px',
                              cursor: isAdminWithAllPermissions ? 'not-allowed' : 'pointer',
                              border: isChecked ? '2px solid #1a6f6350' : '2px solid var(--border)',
                              opacity: isAdminWithAllPermissions ? 0.6 : 1,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isAdminWithAllPermissions) {
                                e.currentTarget.style.transform = 'translateX(4px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isAdminWithAllPermissions) {
                                e.currentTarget.style.transform = 'none';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={isAdminWithAllPermissions}
                              onChange={() => handlePermissionToggle(selectedRole, permKey)}
                              style={{
                                width: 20,
                                height: 20,
                                margin: 0,
                                cursor: isAdminWithAllPermissions ? 'not-allowed' : 'pointer',
                                accentColor: '#1a6f63'
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '700',
                                fontSize: '14px',
                                color: isChecked ? '#1a6f63' : 'var(--text)',
                                marginBottom: '4px'
                              }}>
                                {permKey}
                                {isAdminWithAllPermissions && (
                                  <span style={{
                                    fontSize: '11px',
                                    color: 'var(--text-muted)',
                                    marginLeft: '8px',
                                    padding: '2px 6px',
                                    background: 'var(--panel)',
                                    borderRadius: '4px'
                                  }}>
                                    (All permissions)
                                  </span>
                                )}
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: 'var(--text-muted)',
                                lineHeight: '1.5'
                              }}>
                                {perm.description}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'audit' && (
        <div style={{
          padding: '28px',
          background: 'var(--panel)',
          borderRadius: '16px',
          border: '1px solid var(--border)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
            <div style={{
              padding: '10px',
              background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
              borderRadius: '10px',
              display: 'flex'
            }}>
              <Activity size={20} color="white" strokeWidth={2.5} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>
                Recent Audit Logs
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
                Track all permission checks and access control events
              </p>
            </div>
          </div>

          {/* Audit Log Entries */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {auditLogs.length === 0 ? (
              <div style={{
                padding: '48px 24px',
                textAlign: 'center',
                background: 'var(--bg)',
                borderRadius: '12px',
                border: '2px dashed var(--border)'
              }}>
                <Activity size={32} color="var(--text-muted)" style={{ opacity: 0.5, marginBottom: '12px' }} />
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
                  No audit logs available
                </p>
              </div>
            ) : (
              auditLogs.map((log, index) => {
                const isGranted = log.result === 'granted';
                const date = new Date(log.timestamp);
                const timeStr = date.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                });
                const dateStr = date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                });

                return (
                  <div
                    key={log.id || index}
                    style={{
                      padding: '18px 20px',
                      background: 'var(--bg)',
                      borderRadius: '12px',
                      border: '1px solid var(--border)',
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr auto',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'all 0.2s ease',
                      cursor: 'default'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Timestamp & Status Icon */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        background: isGranted
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: isGranted
                          ? '0 4px 12px rgba(16, 185, 129, 0.2)'
                          : '0 4px 12px rgba(239, 68, 68, 0.2)'
                      }}>
                        {isGranted ? (
                          <CheckCircle size={20} color="white" strokeWidth={2.5} />
                        ) : (
                          <XCircle size={20} color="white" strokeWidth={2.5} />
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: '700',
                          color: 'var(--text)',
                          lineHeight: 1.3
                        }}>
                          {timeStr}
                        </span>
                        <span style={{
                          fontSize: '12px',
                          color: 'var(--text-muted)',
                          lineHeight: 1.3
                        }}>
                          {dateStr}
                        </span>
                      </div>
                    </div>

                    {/* Log Details */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--text)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <User size={14} color="var(--text-muted)" />
                          {log.userEmail || log.userId}
                        </span>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
                          color: 'white',
                          boxShadow: '0 2px 8px rgba(26, 111, 99, 0.2)'
                        }}>
                          {log.action}
                        </span>
                        {log.subjectMerchantCode && (
                          <span
                            title={log.subjectMerchantEmail || ''}
                            style={{
                              padding: '3px 8px',
                              borderRadius: '999px',
                              fontSize: '11px',
                              fontWeight: '700',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              letterSpacing: '0.04em',
                              background: '#ecfdf5',
                              color: '#0d7463',
                              border: '1px solid #a7f3d0'
                            }}
                          >
                            {log.subjectMerchantCode}
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        <FileText size={14} style={{ flexShrink: 0 }} />
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontFamily: 'monospace',
                          fontSize: '12px'
                        }}>
                          {log.path}
                        </span>
                      </div>
                    </div>

                    {/* Result Badge */}
                    <div style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      background: isGranted
                        ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                        : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                      color: isGranted ? '#065f46' : '#991b1b',
                      border: isGranted
                        ? '1px solid #6ee7b7'
                        : '1px solid #fca5a5',
                      whiteSpace: 'nowrap'
                    }}>
                      {log.result}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      {selectedStaff && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--panel)',
            borderRadius: '16px',
            padding: '0',
            minWidth: '450px',
            maxWidth: '90vw',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
            animation: 'modalFadeIn 0.3s ease'
          }}>
            {/* Gradient Header */}
            <div style={{
              padding: '24px',
              background: 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Settings size={24} strokeWidth={2.5} />
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                  Change Role
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>
                  Update access level for {selectedStaff.email}
                </p>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '10px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: 'var(--text)'
                }}>
                  New Role
                </label>
                <select
                  value={newRole}
                  onChange={e => setNewRole(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid var(--border)',
                    background: 'var(--bg)',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'var(--text)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={e => e.currentTarget.style.borderColor = '#1a6f63'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <option value="">Select a role</option>
                  {system?.roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setSelectedStaff(null);
                    setNewRole('');
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '2px solid var(--border)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--panel)';
                    e.currentTarget.style.borderColor = 'var(--text-muted)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--bg)';
                    e.currentTarget.style.borderColor = 'var(--border)';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (permissions?.userRole !== 'ADMIN') {
                      setError('Access denied: Only ADMIN users can change roles');
                      return;
                    }
                    updateStaffRole(selectedStaff._id, newRole);
                  }}
                  disabled={!newRole}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: newRole
                      ? 'linear-gradient(135deg, #1a6f63 0%, #155a51 100%)'
                      : '#ccc',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: newRole ? 'pointer' : 'not-allowed',
                    opacity: newRole ? 1 : 0.6,
                    transition: 'all 0.2s ease',
                    boxShadow: newRole ? '0 4px 12px rgba(26, 111, 99, 0.3)' : 'none'
                  }}
                  onMouseEnter={e => {
                    if (newRole) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(26, 111, 99, 0.4)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (newRole) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 111, 99, 0.3)';
                    }
                  }}
                >
                  Update Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Staff Invitation Modal */}
      {showInviteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--panel)',
            borderRadius: '12px',
            padding: '24px',
            minWidth: '500px',
            maxWidth: '90vw'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
              Invite New Staff Member
            </div>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Email Address</label>
                <input 
                  className="input" 
                  type="email"
                  value={inviteEmail} 
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="Enter staff email address"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Role</label>
                <select 
                  className="input" 
                  value={inviteRole} 
                  onChange={e => setInviteRole(e.target.value)}
                >
                  <option value="">Select a role</option>
                  {system?.roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Message (Optional)</label>
                <textarea 
                  className="input" 
                  value={inviteMessage} 
                  onChange={e => setInviteMessage(e.target.value)}
                  placeholder="Welcome to the team! You've been invited to join as..."
                  rows={3}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button 
                className="btn" 
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteRole('');
                  setInviteMessage('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={sendInvitation}
                disabled={!inviteEmail || !inviteRole}
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Success Modal */}
      {showPasswordModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setShowPasswordModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✓</div>
              <h2 style={{ margin: 0, color: '#16a34a', fontSize: '24px' }}>
                Password Reset Successful
              </h2>
            </div>

            {/* User Info */}
            <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '24px' }}>
              Temporary password for <strong style={{ color: '#1f2937' }}>{resetUserEmail}</strong>
            </p>

            {/* Password Display */}
            <div
              style={{
                backgroundColor: '#f3f4f6',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '2px dashed #d1d5db'
              }}
            >
              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  color: '#1f2937',
                  letterSpacing: '2px',
                  userSelect: 'all',
                  wordBreak: 'break-all'
                }}
              >
                {temporaryPassword}
              </div>
            </div>

            {/* Copy Button */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(temporaryPassword);
                toast.success('Password copied to clipboard!');
              }}
              style={{
                width: '100%',
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '20px' }}>📋</span>
              Copy Password
            </button>

            {/* Warning Message */}
            <div
              style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}
            >
              <div style={{ display: 'flex', gap: '12px', alignItems: 'start' }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ color: '#92400e', display: 'block', marginBottom: '4px' }}>
                    Important:
                  </strong>
                  <span style={{ color: '#78350f', fontSize: '14px' }}>
                    This password will not be shown again. Please save it securely and share it with the staff member through a secure channel.
                  </span>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowPasswordModal(false)}
              style={{
                width: '100%',
                padding: '12px 24px',
                backgroundColor: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Payments(){
  const { apiWithAccessControl } = useAccessControl();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('gateways');
  const [gateways, setGateways] = useState([]);
  const [serviceCharges, setServiceCharges] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showGatewayConfig, setShowGatewayConfig] = useState(false);
  const [showGatewayEdit, setShowGatewayEdit] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [splitConfig, setSplitConfig] = useState({
    free: { serviceFeePercentage: 4.5 },
    premium: { serviceFeePercentage: 3.5 },
    pro: { serviceFeePercentage: 2.5 }
  });

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [gatewaysRes, serviceChargesRes, statsRes, splitConfigRes] = await Promise.all([
        apiWithAccessControl('/admin/payments/gateways'),
        apiWithAccessControl('/admin/payments/service-charges'),
        apiWithAccessControl('/admin/payments/statistics'),
        apiWithAccessControl('/split-payments/admin/split-config')
      ]);

      if (gatewaysRes.ok) {
        const gatewaysData = await gatewaysRes.json();
        setGateways(gatewaysData.gateways || []);
      }

      if (serviceChargesRes.ok) {
        const serviceChargesData = await serviceChargesRes.json();
        setServiceCharges(serviceChargesData.serviceCharges || []);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStatistics(statsData.statistics);
      }

      if (splitConfigRes.ok) {
        const splitConfigData = await splitConfigRes.json();
        setSplitConfig(splitConfigData.configuration.merchantTiers);
      }
    } catch (e) {
      setError('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleEditGateway = (gateway) => {
    setEditingGateway(gateway);
    setShowGatewayEdit(true);
  };

  const handleUpdateGateway = async (gatewayData) => {
    try {
      const response = await apiWithAccessControl(`/admin/payments/gateways/${editingGateway.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gatewayData)
      });

      if (response.ok) {
        setSuccess('Gateway updated successfully');
        setShowGatewayEdit(false);
        setEditingGateway(null);
        loadData(); // Reload data
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update gateway');
      }
    } catch (error) {
      setError('Failed to update gateway');
    }
  };

  const handleToggleGatewayStatus = async (gatewayId, isActive) => {
    try {
      const response = await apiWithAccessControl(`/admin/payments/gateways/${gatewayId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !isActive })
      });

      if (response.ok) {
        setSuccess(`Gateway ${!isActive ? 'activated' : 'deactivated'} successfully`);
        loadData(); // Reload data
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update gateway status');
      }
    } catch (error) {
      setError('Failed to update gateway status');
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setError('');

      console.log('[Test Connection] Starting test for gateway:', editingGateway.id);

      // Get credentials from form inputs
      const publicKey = document.getElementById('editPublicKey')?.value;
      const secretKey = document.getElementById('editSecretKey')?.value;

      console.log('[Test Connection] Credentials found:', {
        hasPublicKey: !!publicKey,
        hasSecretKey: !!secretKey
      });

      if (!publicKey || !secretKey) {
        console.log('[Test Connection] Missing credentials');
        setTestResult({
          success: false,
          message: 'Please enter both public and secret keys before testing',
          error: 'Missing credentials'
        });
        setError('Please enter API keys first');
        setTesting(false);
        return;
      }

      const url = `/admin/payments/gateways/${editingGateway.id}/test`;
      const requestBody = {
        credentials: {
          publicKey,
          secretKey
        }
      };

      console.log('[Test Connection] Making request to:', url);
      console.log('[Test Connection] Request body:', requestBody);

      const response = await apiWithAccessControl(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('[Test Connection] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      const data = await response.json();
      console.log('[Test Connection] Response data:', data);

      if (data.success) {
        console.log('[Test Connection] Test successful');
        setTestResult({
          success: true,
          message: data.testResult.message,
          responseTime: data.testResult.responseTime,
          accountStatus: data.testResult.accountStatus
        });
        setSuccess('Connection test successful!');
      } else {
        console.log('[Test Connection] Test failed:', data);
        setTestResult({
          success: false,
          message: data.testResult?.message || data.error || 'Connection test failed',
          error: data.testResult?.error
        });
        setError('Connection test failed');
      }
    } catch (error) {
      console.error('[Test Connection] Error:', error);
      setTestResult({
        success: false,
        message: 'Failed to test connection',
        error: error.message
      });
      setError('Failed to test connection');
    } finally {
      setTesting(false);
      console.log('[Test Connection] Test completed');
    }
  };

  if (loading) {
    return (
      <div>
        <div className="admin-h1">Payment Management</div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
            <div style={{ marginTop: '16px', color: 'var(--text-muted)' }}>Loading payment data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-h1">Payment Management</div>
      
      {/* Error and Success Messages */}
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button 
            onClick={() => setError('')}
            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}
          >
            <X size={16} />
          </button>
        </div>
      )}
      
      {success && (
        <div style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          color: '#166534',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{success}</span>
          <button 
            onClick={() => setSuccess('')}
            style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer' }}
          >
            <X size={16} />
          </button>
        </div>
      )}
      
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border)' }}>
        <button
          className={`btn ${activeTab === 'gateways' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('gateways')}
          style={{ marginBottom: '-1px' }}
        >
          Payment Gateways
        </button>
        <button
          className={`btn ${activeTab === 'service-charges' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('service-charges')}
          style={{ marginBottom: '-1px' }}
        >
          Service Charges
        </button>
        <button
          className={`btn ${activeTab === 'settlements' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('settlements')}
          style={{ marginBottom: '-1px' }}
        >
          Merchant Settlements
        </button>
        <button
          className={`btn ${activeTab === 'split-payments' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('split-payments')}
          style={{ marginBottom: '-1px' }}
        >
          Split Payments
        </button>
        <button
          className={`btn ${activeTab === 'statistics' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('statistics')}
          style={{ marginBottom: '-1px' }}
        >
          Statistics
        </button>
      </div>

      {activeTab === 'gateways' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ margin: 0 }}>Payment Gateways</h2>
            <button className="btn btn-primary">
              <Plus size={16} style={{ marginRight: '8px' }} />
              Add Gateway
            </button>
          </div>

          <div className="cards-4">
            {gateways.map(gateway => (
              <div key={gateway.id} className="card">
                <div className="card-body">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: gateway.color || '#6366f1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '18px'
                    }}>
                      {gateway.icon || '💳'}
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '16px' }}>{gateway.displayName}</h3>
                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>{gateway.name}</p>
                    </div>
                  </div>
                  
                  <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                    {gateway.description}
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: gateway.isActive ? '#10b981' : '#6b7280'
                      }}></div>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {gateway.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditGateway(gateway)}
                        title="Edit Gateway"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className={`btn btn-sm ${gateway.isActive ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => handleToggleGatewayStatus(gateway.id, gateway.isActive)}
                        title={gateway.isActive ? 'Deactivate Gateway' : 'Activate Gateway'}
                      >
                        {gateway.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'service-charges' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ margin: 0 }}>Service Charge Configuration</h2>
            <button className="btn btn-primary">
              <Plus size={16} style={{ marginRight: '8px' }} />
              Add Override
            </button>
          </div>

          <div className="cards-4">
            {serviceCharges.map(charge => (
              <div key={charge._id} className="card">
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', textTransform: 'capitalize' }}>
                      {charge.merchantTier} Tier
                    </h3>
                    <div style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: charge.isActive ? '#dcfce7' : '#f3f4f6',
                      color: charge.isActive ? '#166534' : '#6b7280',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {charge.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--brand)' }}>
                      {charge.rate}%
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Service charge rate
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                      Who Pays: {charge.whoPays}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {charge.description}
                    </div>
                  </div>

                  {charge.merchantOverrides && charge.merchantOverrides.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>
                        Merchant Overrides ({charge.merchantOverrides.length})
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        Custom rates for specific merchants
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => navigate('/finance/service-charge')}
                      title="Edit merchant-specific service charges"
                    >
                      <Edit size={14} style={{ marginRight: '4px' }} />
                      Edit Merchants
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'settlements' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ margin: 0 }}>Merchant Settlements</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-primary">
                <Plus size={16} style={{ marginRight: '8px' }} />
                Process Settlement
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>Pending Settlements</h3>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
                Process payouts to merchants after deducting service charges
              </p>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gap: '16px' }}>
                {/* Mock settlement data */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  background: 'var(--background-light)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      Tech Store Ltd
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                      Merchant ID: 68c878d3d7f5fad02cc75c7b
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Last transaction: 2 hours ago
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '18px' }}>
                      ₦45,250
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Net settlement amount
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Total: ₦50,000 | Service Charge: ₦3,750 | Platform Fee: ₦1,000
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-sm btn-primary">
                      Process Settlement
                    </button>
                    <button className="btn btn-sm btn-secondary">
                      <Eye size={14} />
                    </button>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  background: 'var(--background-light)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      Fashion Hub
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                      Merchant ID: 68c8af40fefbc241ef9354d4
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Last transaction: 1 day ago
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '18px' }}>
                      ₦12,800
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Net settlement amount
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Total: ₦15,000 | Service Charge: ₦1,500 | Platform Fee: ₦700
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-sm btn-primary">
                      Process Settlement
                    </button>
                    <button className="btn btn-sm btn-secondary">
                      <Eye size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: '24px' }}>
            <div className="card-header">
              <h3>Recent Settlements</h3>
            </div>
            <div className="card-body">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Merchant</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Amount</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Method</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Reference</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr key="tech-store" style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '12px' }}>Tech Store Ltd</td>
                      <td style={{ padding: '12px' }}>₦45,250</td>
                      <td style={{ padding: '12px' }}>Bank Transfer</td>
                      <td style={{ padding: '12px' }}>STL-2024-001</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'var(--success-light)',
                          color: 'var(--success)'
                        }}>
                          Completed
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>2 days ago</td>
                    </tr>
                    <tr key="fashion-hub" style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '12px' }}>Fashion Hub</td>
                      <td style={{ padding: '12px' }}>₦12,800</td>
                      <td style={{ padding: '12px' }}>Bank Transfer</td>
                      <td style={{ padding: '12px' }}>FH-2024-002</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'var(--warning-light)',
                          color: 'var(--warning)'
                        }}>
                          Processing
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>1 day ago</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'split-payments' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ margin: 0 }}>Split Payment Management</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                className="btn btn-primary"
                onClick={() => setShowGatewayConfig(true)}
              >
                <Plus size={16} style={{ marginRight: '8px' }} />
                Configure Gateway
              </button>
            </div>
          </div>

          {error && (
            <div style={{
              padding: '12px',
              background: 'var(--error-light)',
              color: 'var(--error)',
              borderRadius: '4px',
              marginBottom: '16px',
              border: '1px solid var(--error)'
            }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{
              padding: '12px',
              background: 'var(--success-light)',
              color: 'var(--success)',
              borderRadius: '4px',
              marginBottom: '16px',
              border: '1px solid var(--success)'
            }}>
              {success}
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3>Split Payment Configuration</h3>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
                Configure split payments for automatic fund distribution
              </p>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gap: '24px' }}>
                {/* Merchant Tiers */}
                <div>
                  <h4 style={{ marginBottom: '16px' }}>Merchant Tiers & Service Fees</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                    <div style={{
                      padding: '16px',
                      background: 'var(--background-light)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h5 style={{ margin: 0, color: 'var(--primary)' }}>Free Tier</h5>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'var(--primary-light)',
                          color: 'var(--primary)'
                        }}>
                          {splitConfig.free.serviceFeePercentage}%
                        </span>
                      </div>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                        Default tier for new merchants
                      </p>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        Service fee: {splitConfig.free.serviceFeePercentage}% of transaction value
                      </div>
                    </div>

                    <div style={{
                      padding: '16px',
                      background: 'var(--background-light)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h5 style={{ margin: 0, color: 'var(--success)' }}>Premium Tier</h5>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'var(--success-light)',
                          color: 'var(--success)'
                        }}>
                          {splitConfig.premium.serviceFeePercentage}%
                        </span>
                      </div>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                        For established merchants
                      </p>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        Service fee: {splitConfig.premium.serviceFeePercentage}% of transaction value
                      </div>
                    </div>

                    <div style={{
                      padding: '16px',
                      background: 'var(--background-light)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h5 style={{ margin: 0, color: 'var(--warning)' }}>Pro Tier</h5>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'var(--warning-light)',
                          color: 'var(--warning)'
                        }}>
                          {splitConfig.pro.serviceFeePercentage}%
                        </span>
                      </div>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                        For high-volume merchants
                      </p>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        Service fee: {splitConfig.pro.serviceFeePercentage}% of transaction value
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Gateways */}
                <div>
                  <h4 style={{ marginBottom: '16px' }}>Supported Payment Gateways</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                    <div style={{
                      padding: '16px',
                      background: 'var(--background-light)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>💳</div>
                      <h5 style={{ margin: '0 0 8px 0' }}>Paystack</h5>
                      <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                        Subaccounts & Split Payments
                      </p>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: 'var(--success-light)',
                        color: 'var(--success)'
                      }}>
                        Active
                      </span>
                    </div>

                    <div style={{
                      padding: '16px',
                      background: 'var(--background-light)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>🌊</div>
                      <h5 style={{ margin: '0 0 8px 0' }}>Flutterwave</h5>
                      <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                        Split Payments
                      </p>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: 'var(--success-light)',
                        color: 'var(--success)'
                      }}>
                        Active
                      </span>
                    </div>

                    <div style={{
                      padding: '16px',
                      background: 'var(--background-light)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-light)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>🏦</div>
                      <h5 style={{ margin: '0 0 8px 0' }}>Monnify</h5>
                      <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                        Split Payments
                      </p>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: 'var(--warning-light)',
                        color: 'var(--warning)'
                      }}>
                        Pending
                      </span>
                    </div>
                  </div>
                </div>

                {/* How It Works */}
                <div>
                  <h4 style={{ marginBottom: '16px' }}>How Split Payments Work</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'var(--primary-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                        fontSize: '24px'
                      }}>
                        1️⃣
                      </div>
                      <h5 style={{ margin: '0 0 8px 0' }}>Merchant Onboards</h5>
                      <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                        Merchant connects via OAuth to PSP
                      </p>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'var(--success-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                        fontSize: '24px'
                      }}>
                        2️⃣
                      </div>
                      <h5 style={{ margin: '0 0 8px 0' }}>Customer Pays</h5>
                      <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                        Single payment with split instructions
                      </p>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'var(--warning-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                        fontSize: '24px'
                      }}>
                        3️⃣
                      </div>
                      <h5 style={{ margin: '0 0 8px 0' }}>Automatic Split</h5>
                      <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                        PSP splits funds automatically
                      </p>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'var(--error-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                        fontSize: '24px'
                      }}>
                        4️⃣
                      </div>
                      <h5 style={{ margin: '0 0 8px 0' }}>Settlement</h5>
                      <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                        Service fee to us, net to merchant
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: '24px' }}>
            <div className="card-header">
              <h3>Connected Merchants</h3>
            </div>
            <div className="card-body">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Merchant</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Gateway</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Tier</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Service Fee</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Connected</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr key="tech-store-payment" style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '12px' }}>Tech Store Ltd</td>
                      <td style={{ padding: '12px' }}>Paystack</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'var(--primary-light)',
                          color: 'var(--primary)'
                        }}>
                          Free
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>4.5%</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'var(--success-light)',
                          color: 'var(--success)'
                        }}>
                          Active
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>2 days ago</td>
                    </tr>
                    <tr key="fashion-hub-payment" style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '12px' }}>Fashion Hub</td>
                      <td style={{ padding: '12px' }}>Flutterwave</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'var(--success-light)',
                          color: 'var(--success)'
                        }}>
                          Premium
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>3.5%</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: 'var(--success-light)',
                          color: 'var(--success)'
                        }}>
                          Active
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>1 week ago</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gateway Configuration Modal */}
      {showGatewayConfig && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0 }}>Configure Split Payment Settings</h3>
              <button 
                onClick={() => setShowGatewayConfig(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '16px' }}>Merchant Tier Service Fees</h4>
              <p style={{ margin: '0 0 16px 0', color: 'var(--text-muted)', fontSize: '14px' }}>
                Configure service fee percentages for each merchant tier. These must match your PSP configuration.
              </p>
              
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{
                  padding: '16px',
                  background: 'var(--background-light)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h5 style={{ margin: 0, color: 'var(--primary)' }}>Free Tier</h5>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        value={splitConfig.free.serviceFeePercentage}
                        onChange={(e) => setSplitConfig({
                          ...splitConfig,
                          free: { serviceFeePercentage: parseFloat(e.target.value) || 0 }
                        })}
                        style={{
                          width: '80px',
                          padding: '8px',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          textAlign: 'center'
                        }}
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>%</span>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                    Default tier for new merchants
                  </p>
                </div>

                <div style={{
                  padding: '16px',
                  background: 'var(--background-light)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h5 style={{ margin: 0, color: 'var(--success)' }}>Premium Tier</h5>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        value={splitConfig.premium.serviceFeePercentage}
                        onChange={(e) => setSplitConfig({
                          ...splitConfig,
                          premium: { serviceFeePercentage: parseFloat(e.target.value) || 0 }
                        })}
                        style={{
                          width: '80px',
                          padding: '8px',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          textAlign: 'center'
                        }}
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>%</span>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                    For established merchants
                  </p>
                </div>

                <div style={{
                  padding: '16px',
                  background: 'var(--background-light)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h5 style={{ margin: 0, color: 'var(--warning)' }}>Pro Tier</h5>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        value={splitConfig.pro.serviceFeePercentage}
                        onChange={(e) => setSplitConfig({
                          ...splitConfig,
                          pro: { serviceFeePercentage: parseFloat(e.target.value) || 0 }
                        })}
                        style={{
                          width: '80px',
                          padding: '8px',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          textAlign: 'center'
                        }}
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>%</span>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                    For high-volume merchants
                  </p>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '16px' }}>PSP Configuration</h4>
              <div style={{
                padding: '16px',
                background: 'var(--warning-light)',
                borderRadius: '8px',
                border: '1px solid var(--warning)',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px' }}>⚠️</span>
                  <strong style={{ color: 'var(--warning)' }}>Important</strong>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                  These percentages must match exactly what you configure in your PSP dashboard (Paystack/Flutterwave/Monnify). 
                  Update your PSP settings to match these values before saving.
                </p>
              </div>

              <div style={{ display: 'grid', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Platform Fee Percentage (Optional)
                  </label>
                  <input
                    type="number"
                    placeholder="0.0"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px'
                    }}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                    Additional platform fee on top of service fees
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Minimum Split Amount (₦)
                  </label>
                  <input
                    type="number"
                    placeholder="100"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px'
                    }}
                    min="0"
                  />
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                    Minimum transaction amount to enable split payments
                  </p>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowGatewayConfig(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={async () => {
                  try {
                    const response = await apiWithAccessControl('/split-payments/admin/split-config', {
                      method: 'PUT',
                      body: JSON.stringify({
                        merchantTiers: splitConfig,
                        platformFeePercentage: 0, // You can add this to the form
                        minimumSplitAmount: 100 // You can add this to the form
                      })
                    });

                    if (response.ok) {
                      setSuccess('Split payment configuration updated successfully');
                      setShowGatewayConfig(false);
                      // Reload data to reflect changes
                      loadData();
                    } else {
                      const errorData = await response.json();
                      setError(errorData.error || 'Failed to save configuration');
                    }
                  } catch (error) {
                    setError('Failed to save configuration');
                  }
                }}
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gateway Edit Modal */}
      {showGatewayEdit && editingGateway && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0 }}>Edit Payment Gateway</h3>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setShowGatewayEdit(false);
                  setEditingGateway(null);
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Display Name</label>
                <input
                  type="text"
                  defaultValue={editingGateway.displayName}
                  id="editDisplayName"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Description</label>
                <textarea
                  defaultValue={editingGateway.description}
                  id="editDescription"
                  rows={3}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Icon</label>
                  <input
                    type="text"
                    defaultValue={editingGateway.icon}
                    id="editIcon"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Color</label>
                  <input
                    type="color"
                    defaultValue={editingGateway.color || '#3B82F6'}
                    id="editColor"
                    style={{ width: '100%', padding: '4px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Public Key</label>
                <input
                  type="text"
                  defaultValue={editingGateway.credentials?.publicKey || ''}
                  id="editPublicKey"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Secret Key</label>
                <input
                  type="password"
                  defaultValue={editingGateway.credentials?.secretKey || ''}
                  id="editSecretKey"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              {testResult && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '4px',
                  backgroundColor: testResult.success ? '#d1fae5' : '#fee2e2',
                  border: `1px solid ${testResult.success ? '#10b981' : '#ef4444'}`
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px', color: testResult.success ? '#065f46' : '#991b1b' }}>
                    {testResult.success ? '✓ ' : '✗ '}
                    {testResult.message}
                  </div>
                  {testResult.responseTime && (
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Response Time: {testResult.responseTime}
                    </div>
                  )}
                  {testResult.error && (
                    <div style={{ fontSize: '12px', color: '#991b1b', marginTop: '4px' }}>
                      Error: {testResult.error}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    const gatewayData = {
                      displayName: document.getElementById('editDisplayName').value,
                      description: document.getElementById('editDescription').value,
                      icon: document.getElementById('editIcon').value,
                      color: document.getElementById('editColor').value,
                      credentials: {
                        ...editingGateway.credentials,
                        publicKey: document.getElementById('editPublicKey').value,
                        secretKey: document.getElementById('editSecretKey').value
                      }
                    };
                    await handleUpdateGateway(gatewayData);
                  }}
                >
                  Save Changes
                </button>
                <button
                  className="btn btn-warning"
                  onClick={handleTestConnection}
                  disabled={testing}
                  style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                >
                  {testing ? 'Testing...' : 'Test Connection'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowGatewayEdit(false);
                    setEditingGateway(null);
                    setTestResult(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'statistics' && statistics && (
        <div>
          <h2 style={{ marginBottom: '24px' }}>Payment Statistics</h2>
          
          <div className="cards-4" style={{ marginBottom: '24px' }}>
            <div className="stat-card">
              <div className="icon" style={{ background: '#3b82f6' }}>
                <CreditCard size={20} color="white" />
              </div>
              <div style={{ padding: '8px 0' }}>
                <div className="stat-value">{statistics.totalTransactions?.toLocaleString()}</div>
                <div className="stat-label">Total Transactions</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="icon" style={{ background: '#10b981' }}>
                <DollarSign size={20} color="white" />
              </div>
              <div style={{ padding: '8px 0' }}>
                <div className="stat-value">₦{statistics.totalVolume?.toLocaleString()}</div>
                <div className="stat-label">Total Volume</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="icon" style={{ background: '#f59e0b' }}>
                <Settings size={20} color="white" />
              </div>
              <div style={{ padding: '8px 0' }}>
                <div className="stat-value">₦{statistics.totalServiceCharges?.toLocaleString()}</div>
                <div className="stat-label">Service Charges</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="icon" style={{ background: '#8b5cf6' }}>
                <TrendingUp size={20} color="white" />
              </div>
              <div style={{ padding: '8px 0' }}>
                <div className="stat-value">{statistics.successRate}%</div>
                <div className="stat-label">Success Rate</div>
              </div>
            </div>
          </div>

          <div className="two-col">
            <div className="section">
              <div className="section-header">
                <h3>Top Payment Gateways</h3>
              </div>
              <div className="section-body">
                <div style={{ display: 'grid', gap: '12px' }}>
                  {statistics.topGateways?.map((gateway, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: 'var(--bg)',
                      borderRadius: '8px',
                      border: '1px solid var(--border)'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{gateway.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {gateway.transactions} transactions
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                          ₦{gateway.volume?.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {((gateway.volume / statistics.totalVolume) * 100).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="section">
              <div className="section-header">
                <h3>Service Charge Breakdown</h3>
              </div>
              <div className="section-body">
                <div style={{ display: 'grid', gap: '12px' }}>
                  {Object.entries(statistics.serviceChargeBreakdown || {}).map(([tier, data]) => (
                    <div key={tier} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: 'var(--bg)',
                      borderRadius: '8px',
                      border: '1px solid var(--border)'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', textTransform: 'capitalize' }}>
                          {tier} Tier
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {data.transactions} transactions
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                          ₦{data.amount?.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {((data.amount / statistics.totalServiceCharges) * 100).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ template, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    type: template?.type || 'custom',
    subject: template?.subject || '',
    htmlContent: template?.htmlContent || '',
    textContent: template?.textContent || '',
    variables: template?.variables || [],
    isActive: template?.isActive ?? true
  });
  const [newVariable, setNewVariable] = useState({ name: '', description: '', example: '', required: false });

  const addVariable = () => {
    if (newVariable.name) {
      setFormData(prev => ({
        ...prev,
        variables: [...prev.variables, { ...newVariable, name: `{{${newVariable.name}}}` }]
      }));
      setNewVariable({ name: '', description: '', example: '', required: false });
    }
  };

  const removeVariable = (index) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 24px 0' }}>
        {template ? 'Edit Template' : 'Create Template'}
      </h3>
      
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                Template Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              >
                <option value="custom">Custom</option>
                <option value="order_confirmation">Order Confirmation</option>
                <option value="order_shipped">Order Shipped</option>
                <option value="order_delivered">Order Delivered</option>
                <option value="order_cancelled">Order Cancelled</option>
                <option value="customer_welcome">Customer Welcome</option>
                <option value="staff_invitation">Staff Invitation</option>
                <option value="system_notification">System Notification</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
              Subject
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
              HTML Content
            </label>
            <textarea
              value={formData.htmlContent}
              onChange={(e) => setFormData(prev => ({ ...prev, htmlContent: e.target.value }))}
              required
              rows={10}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'monospace' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
              Text Content
            </label>
            <textarea
              value={formData.textContent}
              onChange={(e) => setFormData(prev => ({ ...prev, textContent: e.target.value }))}
              rows={6}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'monospace' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Variables
            </label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                placeholder="Variable name (without {{}})"
                value={newVariable.name}
                onChange={(e) => setNewVariable(prev => ({ ...prev, name: e.target.value }))}
                style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Description"
                value={newVariable.description}
                onChange={(e) => setNewVariable(prev => ({ ...prev, description: e.target.value }))}
                style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
              <input
                type="text"
                placeholder="Example"
                value={newVariable.example}
                onChange={(e) => setNewVariable(prev => ({ ...prev, example: e.target.value }))}
                style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="checkbox"
                  checked={newVariable.required}
                  onChange={(e) => setNewVariable(prev => ({ ...prev, required: e.target.checked }))}
                />
                Required
              </label>
              <button type="button" onClick={addVariable} className="btn btn-sm btn-secondary">
                Add
              </button>
            </div>
            
            {formData.variables.map((variable, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '8px', 
                background: '#f9fafb', 
                borderRadius: '4px',
                marginBottom: '4px'
              }}>
                <span style={{ fontWeight: '600' }}>{variable.name}</span>
                <span style={{ color: '#6b7280' }}>{variable.description}</span>
                <span style={{ color: '#6b7280', fontSize: '12px' }}>({variable.example})</span>
                {variable.required && <span style={{ color: '#dc2626', fontSize: '12px' }}>Required</span>}
                <button 
                  type="button" 
                  onClick={() => removeVariable(index)}
                  style={{ marginLeft: 'auto', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              Active
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
          <button type="submit" className="btn btn-primary">
            {template ? 'Update Template' : 'Create Template'}
          </button>
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function Email(){
  const { apiWithAccessControl } = useAccessControl();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [templatesRes, settingsRes, statsRes] = await Promise.all([
        apiWithAccessControl('/admin/email/templates'),
        apiWithAccessControl('/admin/email/settings'),
        apiWithAccessControl('/admin/email/statistics')
      ]);

      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData.templates || []);
      }

      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(settingsData.settings);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStatistics(statsData.statistics);
      }
    } catch (e) {
      setError('Failed to load email data');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (templateData) => {
    try {
      setError('');
      const url = editingTemplate ? `/admin/email/templates/${editingTemplate._id}` : '/admin/email/templates';
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await apiWithAccessControl(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
      });

      if (response.ok) {
        setSuccess(editingTemplate ? 'Template updated successfully' : 'Template created successfully');
        setEditingTemplate(null);
        setShowCreateModal(false);
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save template');
      }
    } catch (e) {
      setError('Failed to save template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      setError('');
      const response = await apiWithAccessControl(`/admin/email/templates/${templateId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Template deleted successfully');
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete template');
      }
    } catch (e) {
      setError('Failed to delete template');
    }
  };

  const testEmailSettings = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    try {
      setTestingEmail(true);
      setError('');
      
      const response = await apiWithAccessControl('/admin/email/settings/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ testEmail })
      });

      if (response.ok) {
        setSuccess('Test email sent successfully!');
        setTestEmail('');
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to send test email');
      }
    } catch (e) {
      setError('Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const updateSettings = async (settingsData) => {
    try {
      setError('');
      const response = await apiWithAccessControl('/admin/email/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsData)
      });

      if (response.ok) {
        setSuccess('Settings updated successfully');
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update settings');
      }
    } catch (e) {
      setError('Failed to update settings');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div>
        <div className="admin-h1">Email Management</div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
            <div style={{ marginTop: '16px', color: 'var(--text-muted)' }}>Loading email data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-h1">Email Management</div>
      
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}
      
      {success && (
        <div style={{
          padding: '12px 16px',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          color: '#166534',
          marginBottom: '16px'
        }}>
          {success}
        </div>
      )}
      
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border)' }}>
        <button
          className={`btn ${activeTab === 'templates' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('templates')}
          style={{ marginBottom: '-1px' }}
        >
          Email Templates
        </button>
        <button
          className={`btn ${activeTab === 'settings' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('settings')}
          style={{ marginBottom: '-1px' }}
        >
          Email Settings
        </button>
        <button
          className={`btn ${activeTab === 'statistics' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('statistics')}
          style={{ marginBottom: '-1px' }}
        >
          Statistics
        </button>
      </div>

      {activeTab === 'templates' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ margin: 0 }}>Email Templates</h2>
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={16} style={{ marginRight: '8px' }} />
              Create Template
            </button>
          </div>

          <div className="cards-4">
            {templates.map(template => (
              <div key={template._id} className="card">
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '16px' }}>{template.name}</h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)', textTransform: 'replace' }}>
                        {template.type.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {template.isSystem && (
                        <div style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: '#f3f4f6',
                          color: '#6b7280',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          System
                        </div>
                      )}
                      <div style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: template.isActive ? '#dcfce7' : '#f3f4f6',
                        color: template.isActive ? '#166534' : '#6b7280',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        {template.isActive ? 'Active' : 'Inactive'}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                      {template.subject}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {template.variables?.length || 0} variables
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Usage Statistics
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '11px' }}>
                      <div>
                        <div style={{ fontWeight: '600' }}>{template.stats?.sent || 0}</div>
                        <div style={{ color: 'var(--text-muted)' }}>Sent</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: '600' }}>{template.stats?.opened || 0}</div>
                        <div style={{ color: 'var(--text-muted)' }}>Opened</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: '600' }}>{template.stats?.clicked || 0}</div>
                        <div style={{ color: 'var(--text-muted)' }}>Clicked</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => setEditingTemplate(template)}
                      title="Edit template"
                    >
                      <Edit size={14} />
                    </button>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        const preview = window.open('', '_blank');
                        preview.document.write(template.htmlContent);
                        preview.document.close();
                      }}
                      title="Preview template"
                    >
                      <Eye size={14} />
                    </button>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        const email = prompt('Enter email address to send test:');
                        if (email) {
                          // TODO: Implement test email sending
                          alert('Test email functionality coming soon');
                        }
                      }}
                      title="Send test email"
                    >
                      <Mail size={14} />
                    </button>
                    {!template.isSystem && (
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteTemplate(template._id)}
                        title="Delete template"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Template Edit/Create Modal */}
      {(editingTemplate || showCreateModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <TemplateEditor
              template={editingTemplate}
              onSave={saveTemplate}
              onCancel={() => {
                setEditingTemplate(null);
                setShowCreateModal(false);
              }}
            />
          </div>
        </div>
      )}

      {activeTab === 'settings' && settings && (
        <div>
          <h2 style={{ marginBottom: '24px' }}>Email Settings</h2>
          
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header">
              <h3>SMTP Configuration</h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Host</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{settings.smtp?.host}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Port</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{settings.smtp?.port}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Username</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{settings.smtp?.auth?.user}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>From Email</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{settings.defaults?.fromEmail}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header">
              <h3>Email Categories</h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div style={{
                  padding: '16px',
                  background: 'var(--bg)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px' }}>Transactional</h4>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: settings.categories?.transactional?.enabled ? '#10b981' : '#6b7280'
                    }}></div>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    Order confirmations, receipts, etc.
                  </div>
                </div>

                <div style={{
                  padding: '16px',
                  background: 'var(--bg)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px' }}>Marketing</h4>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: settings.categories?.marketing?.enabled ? '#10b981' : '#6b7280'
                    }}></div>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    Promotions, newsletters, etc.
                  </div>
                </div>

                <div style={{
                  padding: '16px',
                  background: 'var(--bg)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px' }}>System</h4>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: settings.categories?.system?.enabled ? '#10b981' : '#6b7280'
                    }}></div>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    System notifications, alerts, etc.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              className="btn btn-primary"
              onClick={() => {
                const newHost = prompt('SMTP Host:', settings.smtp?.host);
                const newPort = prompt('SMTP Port:', settings.smtp?.port);
                const newUser = prompt('SMTP Username:', settings.smtp?.auth?.user);
                const newPass = prompt('SMTP Password:', '');
                const newFromEmail = prompt('From Email:', settings.defaults?.fromEmail);
                const newFromName = prompt('From Name:', settings.defaults?.fromName);
                
                if (newHost && newPort && newUser && newPass && newFromEmail) {
                  updateSettings({
                    smtp: {
                      host: newHost,
                      port: parseInt(newPort),
                      secure: settings.smtp?.secure || false,
                      auth: {
                        user: newUser,
                        pass: newPass
                      }
                    },
                    defaults: {
                      fromEmail: newFromEmail,
                      fromName: newFromName || 'Revstar'
                    }
                  });
                }
              }}
            >
              <Settings size={16} style={{ marginRight: '8px' }} />
              Edit Settings
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                const email = prompt('Enter test email address:');
                if (email) {
                  setTestEmail(email);
                  testEmailSettings();
                }
              }}
            >
              <Mail size={16} style={{ marginRight: '8px' }} />
              Test Configuration
            </button>
          </div>
        </div>
      )}

      {activeTab === 'statistics' && statistics && (
        <div>
          <h2 style={{ marginBottom: '24px' }}>Email Statistics</h2>
          
          <div className="cards-4" style={{ marginBottom: '24px' }}>
            <div className="stat-card">
              <div className="icon" style={{ background: '#3b82f6' }}>
                <Mail size={20} color="white" />
              </div>
              <div style={{ padding: '8px 0' }}>
                <div className="stat-value">{statistics.totalSent?.toLocaleString()}</div>
                <div className="stat-label">Total Sent</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="icon" style={{ background: '#10b981' }}>
                <Eye size={20} color="white" />
              </div>
              <div style={{ padding: '8px 0' }}>
                <div className="stat-value">{statistics.openRate}%</div>
                <div className="stat-label">Open Rate</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="icon" style={{ background: '#f59e0b' }}>
                <MousePointer size={20} color="white" />
              </div>
              <div style={{ padding: '8px 0' }}>
                <div className="stat-value">{statistics.clickRate}%</div>
                <div className="stat-label">Click Rate</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="icon" style={{ background: '#ef4444' }}>
                <AlertCircle size={20} color="white" />
              </div>
              <div style={{ padding: '8px 0' }}>
                <div className="stat-value">{statistics.bounceRate}%</div>
                <div className="stat-label">Bounce Rate</div>
              </div>
            </div>
          </div>

          <div className="two-col">
            <div className="section">
              <div className="section-header">
                <h3>Top Templates</h3>
              </div>
              <div className="section-body">
                <div style={{ display: 'grid', gap: '12px' }}>
                  {statistics.topTemplates?.map((template, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: 'var(--bg)',
                      borderRadius: '8px',
                      border: '1px solid var(--border)'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{template.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {template.sent} sent, {template.opened} opened
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                          {((template.opened / template.sent) * 100).toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          Open Rate
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="section">
              <div className="section-header">
                <h3>Recent Activity</h3>
              </div>
              <div className="section-body">
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div style={{
                    padding: '12px 16px',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                      Order Confirmation Sent
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      2 minutes ago
                    </div>
                  </div>
                  <div style={{
                    padding: '12px 16px',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                      Welcome Email Sent
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      5 minutes ago
                    </div>
                  </div>
                  <div style={{
                    padding: '12px 16px',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                      Payment Confirmation Sent
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      8 minutes ago
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function StaffStatusDashboard() {
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { apiWithAccessControl } = useAccessControl();

  const fetchStaffActivity = async () => {
    try {
      setLoading(true);
      const response = await apiWithAccessControl('/admin/staff-status/dashboard');
      if (response.ok) {
        const data = await response.json();
        setStaffData(data.data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        setError('Failed to fetch staff activity data');
      }
    } catch (err) {
      setError('Error fetching staff activity data');
      console.error('Staff activity error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Visibility-gated poll — bumped from 30s to 60s at the same time
    // (staff activity dashboard doesn't need 30-second precision;
    // that cadence was pure edge-request bleed).
    fetchStaffActivity();
    let interval = null;
    const start = () => { if (!interval) interval = setInterval(fetchStaffActivity, 60000); };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    if (!document.hidden) start();

    const onVis = () => {
      if (document.hidden) stop();
      else { fetchStaffActivity(); start(); }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Never';
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#16a34a';
      case 'idle': return '#f59e0b';
      case 'offline': return '#dc2626';
      case 'never-online': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'idle': return 'Idle';
      case 'offline': return 'Offline';
      case 'never-online': return 'Never Online';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading staff activity...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ color: 'red' }}>Error: {error}</div>
      </div>
    );
  }

  if (!staffData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>No staff data available</div>
      </div>
    );
  }

  const { summary, online, idle, offline, neverOnline } = staffData;

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text)' }}>
            Staff Activity Dashboard
          </h1>
          <p style={{ margin: '8px 0 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
            Real-time staff activity and status monitoring
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={fetchStaffActivity}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--brand)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Refresh
          </button>
          {lastUpdated && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <div style={{ 
          padding: '20px', 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          border: '1px solid var(--border)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#16a34a', marginBottom: '8px' }}>
            {summary.online}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: '500' }}>Online</div>
        </div>
        
        <div style={{ 
          padding: '20px', 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          border: '1px solid var(--border)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#f59e0b', marginBottom: '8px' }}>
            {summary.idle}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: '500' }}>Idle</div>
        </div>
        
        <div style={{ 
          padding: '20px', 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          border: '1px solid var(--border)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#dc2626', marginBottom: '8px' }}>
            {summary.offline}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: '500' }}>Offline</div>
        </div>
        
        <div style={{ 
          padding: '20px', 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          border: '1px solid var(--border)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#6b7280', marginBottom: '8px' }}>
            {summary.totalStaff}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)', fontWeight: '500' }}>Total Staff</div>
        </div>
      </div>

      {/* Staff Lists */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
        {/* Online Staff */}
        {online.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            border: '1px solid var(--border)',
            overflow: 'hidden'
          }}>
            <div style={{ 
              padding: '16px 20px', 
              backgroundColor: '#f0fdf4', 
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: '#16a34a',
                animation: 'pulse 2s infinite'
              }}></div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text)' }}>
                Online Staff ({online.length})
              </h3>
            </div>
            <div style={{ padding: '16px' }}>
              {online.map(staff => (
                <div key={staff.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border-light)'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text)' }}>{staff.username || staff.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{staff.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {staff.currentPage || '/'} • {formatLastSeen(staff.lastActivity)}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '4px 8px', 
                    backgroundColor: '#dcfce7', 
                    color: '#16a34a',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {getStatusText(staff.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Idle Staff */}
        {idle.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            border: '1px solid var(--border)',
            overflow: 'hidden'
          }}>
            <div style={{ 
              padding: '16px 20px', 
              backgroundColor: '#fffbeb', 
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: '#f59e0b'
              }}></div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text)' }}>
                Idle Staff ({idle.length})
              </h3>
            </div>
            <div style={{ padding: '16px' }}>
              {idle.map(staff => (
                <div key={staff.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border-light)'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text)' }}>{staff.username || staff.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{staff.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {formatLastSeen(staff.lastActivity)} • {staff.currentPage || '/'}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '4px 8px', 
                    backgroundColor: '#fef3c7', 
                    color: '#f59e0b',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {getStatusText(staff.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Offline Staff */}
        {offline.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            border: '1px solid var(--border)',
            overflow: 'hidden'
          }}>
            <div style={{ 
              padding: '16px 20px', 
              backgroundColor: '#fef2f2', 
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: '#dc2626'
              }}></div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text)' }}>
                Offline Staff ({offline.length})
              </h3>
            </div>
            <div style={{ padding: '16px' }}>
              {offline.map(staff => (
                <div key={staff.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border-light)'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text)' }}>{staff.username || staff.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{staff.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Last seen: {formatLastSeen(staff.lastActivity)}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '4px 8px', 
                    backgroundColor: '#fee2e2', 
                    color: '#dc2626',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {getStatusText(staff.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Never Online Staff */}
        {neverOnline.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            border: '1px solid var(--border)',
            overflow: 'hidden'
          }}>
            <div style={{ 
              padding: '16px 20px', 
              backgroundColor: '#f9fafb', 
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: '#6b7280'
              }}></div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text)' }}>
                Never Online ({neverOnline.length})
              </h3>
            </div>
            <div style={{ padding: '16px' }}>
              {neverOnline.map(staff => (
                <div key={staff.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border-light)'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text)' }}>{staff.username || staff.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{staff.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Never logged in
                    </div>
                  </div>
                  <div style={{ 
                    padding: '4px 8px', 
                    backgroundColor: '#f3f4f6', 
                    color: '#6b7280',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {getStatusText(staff.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {online.length === 0 && idle.length === 0 && offline.length === 0 && neverOnline.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid var(--border)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--text)' }}>No Staff Activity Data</h3>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>
            Staff activity data will appear here once staff members start using the system.
          </p>
        </div>
      )}
    </div>
  );
}

// Invitation acceptance component
function AcceptInvite() {
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    // Fetch invitation data from backend
    const fetchInvitation = async () => {
      try {
        const response = await apiPublic(`/admin/rbac/staff/invitations/${token}`);
        if (response.ok) {
          const data = await response.json();
          setInviteData({
            email: data.invitation.email,
            role: data.invitation.staffRole,
            invitedBy: data.invitation.createdBy?.username || 'Admin',
            expiresAt: new Date(data.invitation.expiresAt)
          });
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Invalid invitation link');
        }
      } catch (err) {
        setError('Failed to load invitation details');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiPublic('/admin/rbac/staff/accept-invite', {
        method: 'POST',
        body: JSON.stringify({
          token: new URLSearchParams(window.location.search).get('token'),
          name: formData.name,
          password: formData.password
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to accept invitation');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        background: 'var(--bg)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: 40, 
            height: 40, 
            border: '3px solid var(--border)', 
            borderTop: '3px solid var(--brand)', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div className="muted">Loading invitation...</div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        background: 'var(--bg)'
      }}>
        <div className="card" style={{ maxWidth: 400, textAlign: 'center' }}>
          <div className="card-body">
            <div style={{ fontSize: '48px', marginBottom: '16px', color: 'var(--success-color)' }}>✓</div>
            <h2 style={{ marginBottom: '8px' }}>Invitation Accepted!</h2>
            <p className="muted">Your account has been created successfully. Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      background: 'var(--bg)'
    }}>
      <div className="card" style={{ maxWidth: 400 }}>
        <div className="card-body">
          <h2 style={{ marginBottom: '16px', textAlign: 'center' }}>Accept Invitation</h2>
          
          {inviteData && (
            <div style={{ 
              background: 'var(--surface)', 
              padding: '16px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              border: '1px solid var(--border)'
            }}>
              <h3 style={{ marginBottom: '8px' }}>Invitation Details</h3>
              <p><strong>Email:</strong> {inviteData.email}</p>
              <p><strong>Role:</strong> {inviteData.role}</p>
              <p><strong>Invited by:</strong> {inviteData.invitedBy}</p>
              <p><strong>Expires:</strong> {inviteData.expiresAt.toLocaleDateString()}</p>
            </div>
          )}

          {error && (
            <div style={{ 
              background: 'var(--error-bg)', 
              color: 'var(--error-color)', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '16px',
              border: '1px solid var(--error-color)'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Full Name</label>
              <input
                className="input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Password</label>
              <input
                className="input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                required
                placeholder="Create a password"
                minLength="6"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Confirm Password</label>
              <input
                className="input"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
                placeholder="Confirm your password"
                minLength="6"
              />
            </div>
            
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              style={{ width: '100%', padding: '12px' }}
            >
              {submitting ? 'Creating Account...' : 'Accept Invitation'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    setMessage('');

    try {
      const response = await fetch('/api/admin-auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setStatus('success');
      } else {
        setError(data.error || 'Failed to send reset email');
        setStatus('error');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#1F2937' }}>
            Reset Admin Password
          </h1>
          <p style={{ margin: '8px 0 0 0', color: '#6B7280', fontSize: '14px' }}>
            Enter your email address to receive a reset link
          </p>
        </div>

        {message && (
          <div style={{
            background: '#D1FAE5',
            border: '1px solid #A7F3D0',
            color: '#065F46',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {message}
          </div>
        )}

        {error && (
          <div style={{
            background: '#FEE2E2',
            border: '1px solid #FECACA',
            color: '#DC2626',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your admin email"
              required
              disabled={status === 'loading'}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={status === 'loading' || !email}
            style={{
              width: '100%',
              padding: '12px',
              background: status === 'loading' ? '#9CA3AF' : '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: status === 'loading' ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            {status === 'loading' ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#6B7280',
              fontSize: '14px',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            Back to Login
          </button>
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '24px',
          padding: '16px',
          background: '#F3F4F6',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#6B7280'
        }}>
          <strong>Need help?</strong><br />
          Contact <a href="mailto:technical@userevstar.com" style={{ color: '#0d7463' }}>technical@userevstar.com</a> for assistance
        </div>
      </div>
    </div>
  );
}

function PasswordReset() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Invalid reset link. Please request a new one.');
      setStatus('error');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    setMessage('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setStatus('error');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setStatus('error');
      return;
    }

    try {
      const response = await fetch('/api/admin-auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Password reset successfully! Redirecting to login...');
        setStatus('success');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(data.error || 'Failed to reset password');
        setStatus('error');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setStatus('error');
    }
  };

  if (status === 'error' && !token) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#1F2937' }}>
            Invalid Reset Link
          </h1>
          <p style={{ margin: '16px 0', color: '#6B7280' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/forgot-password')}
            style={{
              background: '#3B82F6',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Request New Reset Link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔑</div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#1F2937' }}>
            Set New Password
          </h1>
          <p style={{ margin: '8px 0 0 0', color: '#6B7280', fontSize: '14px' }}>
            Enter your new admin password
          </p>
        </div>

        {message && (
          <div style={{
            background: '#D1FAE5',
            border: '1px solid #A7F3D0',
            color: '#065F46',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {message}
          </div>
        )}

        {error && (
          <div style={{
            background: '#FEE2E2',
            border: '1px solid #FECACA',
            color: '#DC2626',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              disabled={status === 'loading'}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              disabled={status === 'loading'}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={status === 'loading' || !password || !confirmPassword}
            style={{
              width: '100%',
              padding: '12px',
              background: status === 'loading' ? '#9CA3AF' : '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: status === 'loading' ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            {status === 'loading' ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#6B7280',
              fontSize: '14px',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}

// Admin Profile Component
function AdminProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const { apiWithAccessControl } = useAccessControl();
  const toast = useToast();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await apiWithAccessControl('/admin/profile');
      if (response.ok) {
        const data = await response.json();
        setProfile(data.admin || data);
        setFormData(data.admin || data);
      } else {
        toast.error('Failed to load profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Error loading profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await apiWithAccessControl('/admin/profile/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.fullName, // fullName maps to username in the database
          email: formData.email
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.admin || data);
        setFormData(data.admin || data);
        setIsEditing(false);
        toast.success('Profile updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({ ...profile });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>Loading profile...</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#dc2626' }}>Failed to load profile</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>
          Admin Profile
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          Manage your admin account information
        </p>
      </div>

      <div style={{
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        {/* Profile Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
          padding: '32px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <Shield size={48} color="#0f766e" />
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'white', marginBottom: '4px' }}>
            {profile.fullName}
          </h2>
          <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>
            {profile.role}
          </p>
        </div>

        {/* Profile Details */}
        <div style={{ padding: '32px' }}>
          <div style={{ display: 'grid', gap: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                  Full Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                ) : (
                  <div style={{ padding: '10px 0', fontSize: '14px', color: '#0f172a' }}>
                    {profile.fullName}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                  Username
                </label>
                <div style={{ padding: '10px 0', fontSize: '14px', color: '#0f172a' }}>
                  {profile.username}
                </div>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                Email Address
              </label>
              {isEditing ? (
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              ) : (
                <div style={{ padding: '10px 0', fontSize: '14px', color: '#0f172a' }}>
                  {profile.email}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                  Last Login
                </label>
                <div style={{ padding: '10px 0', fontSize: '14px', color: '#64748b' }}>
                  {new Date(profile.lastLogin).toLocaleString()}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                  Account Created
                </label>
                <div style={{ padding: '10px 0', fontSize: '14px', color: '#64748b' }}>
                  {new Date(profile.accountCreated).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  style={{
                    padding: '10px 20px',
                    background: 'white',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#475569',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '10px 20px',
                    background: saving ? '#94a3b8' : 'linear-gradient(135deg, #0f766e, #0d9488)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white',
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #0f766e, #0d9488)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                <Edit size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Admin Settings Component
function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { apiWithAccessControl } = useAccessControl();
  const toast = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiWithAccessControl('/admin/settings');
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || data);
      } else {
        toast.error('Failed to load settings');
        // Set default settings if fetch fails
        setSettings({
          emailNotifications: true,
          merchantNotifications: true,
          systemAlerts: true,
          twoFactorAuth: false,
          sessionTimeout: '30'
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error loading settings');
      // Set default settings
      setSettings({
        emailNotifications: true,
        merchantNotifications: true,
        systemAlerts: true,
        twoFactorAuth: false,
        sessionTimeout: '30'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await apiWithAccessControl('/admin/settings/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || data);
        toast.success('Settings saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error saving settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>Loading settings...</div>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#dc2626' }}>Failed to load settings</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>
          Settings
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          Manage your admin preferences and security settings
        </p>
      </div>

      <div style={{ display: 'grid', gap: '20px' }}>
        {/* Notification Settings */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          padding: '24px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>
            Notifications
          </h3>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>
                  Email Notifications
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                  Receive email updates about platform activity
                </div>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={() => handleToggle('emailNotifications')}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: settings.emailNotifications ? '#0f766e' : '#cbd5e1',
                  transition: '0.3s',
                  borderRadius: '26px'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '20px',
                    width: '20px',
                    left: settings.emailNotifications ? '25px' : '3px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '0.3s',
                    borderRadius: '50%'
                  }} />
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>
                  Merchant Notifications
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                  Get notified when merchants require attention
                </div>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                <input
                  type="checkbox"
                  checked={settings.merchantNotifications}
                  onChange={() => handleToggle('merchantNotifications')}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: settings.merchantNotifications ? '#0f766e' : '#cbd5e1',
                  transition: '0.3s',
                  borderRadius: '26px'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '20px',
                    width: '20px',
                    left: settings.merchantNotifications ? '25px' : '3px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '0.3s',
                    borderRadius: '50%'
                  }} />
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>
                  System Alerts
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                  Critical system alerts and warnings
                </div>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                <input
                  type="checkbox"
                  checked={settings.systemAlerts}
                  onChange={() => handleToggle('systemAlerts')}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: settings.systemAlerts ? '#0f766e' : '#cbd5e1',
                  transition: '0.3s',
                  borderRadius: '26px'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '20px',
                    width: '20px',
                    left: settings.systemAlerts ? '25px' : '3px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '0.3s',
                    borderRadius: '50%'
                  }} />
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          padding: '24px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>
            Security
          </h3>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>
                  Two-Factor Authentication
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                  Add an extra layer of security to your account
                </div>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                <input
                  type="checkbox"
                  checked={settings.twoFactorAuth}
                  onChange={() => handleToggle('twoFactorAuth')}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: settings.twoFactorAuth ? '#0f766e' : '#cbd5e1',
                  transition: '0.3s',
                  borderRadius: '26px'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '20px',
                    width: '20px',
                    left: settings.twoFactorAuth ? '25px' : '3px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '0.3s',
                    borderRadius: '50%'
                  }} />
                </span>
              </label>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#0f172a', marginBottom: '8px' }}>
                Session Timeout (minutes)
              </label>
              <select
                value={settings.sessionTimeout}
                onChange={(e) => setSettings({ ...settings, sessionTimeout: e.target.value })}
                style={{
                  width: '200px',
                  padding: '10px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              padding: '12px 24px',
              background: isSaving ? '#94a3b8' : 'linear-gradient(135deg, #0f766e, #0d9488)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'white',
              cursor: isSaving ? 'not-allowed' : 'pointer'
            }}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App(){
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AccessControlProvider>
          <BrowserRouter>
            <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="/invite" element={<AcceptInvite />} />
          <Route path="/" element={<RequireAdmin><Shell><Dashboard /></Shell></RequireAdmin>} />
          <Route path="/health" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="system.health" pageName="System Health"><Health /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/merchants" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions={['stores.read', 'stores.write']} pageName="Merchants"><Merchants /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/merchants/:merchantId/orders" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="orders.read" pageName="Merchant orders"><MerchantOrders /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/merchants/:merchantId/customers" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="stores.read" pageName="Merchant customers"><MerchantCustomers /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/discounts" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="discounts.read" pageName="Discount oversight"><DiscountOversight /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/merchant-engagement" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions={['stores.read', 'stores.write']} pageName="Merchant Engagement"><MerchantNudges /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/engagement/livechat" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="stores.read" pageName="Livechat Adoption"><LivechatAdoption /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/content/knowledgebase" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="content.read" pageName="Knowledge Base"><KnowledgeBaseManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/marketing/*" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Marketing"><MarketingHub /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/announcements" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Announcements"><AnnouncementManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/changelog" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="notifications.broadcast" pageName="What's New"><ChangelogManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/account-deletion-requests" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="users.write" pageName="Account Deletion Requests"><AccountDeletionManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/customers" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="users.read" pageName="Contacts"><CustomersDirectory /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/team-members" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="users.read" pageName="Team Members"><TeamMembersManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/products" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions={['products.read', 'products.write']} pageName="Products"><ProductManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/finance/service-charge" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.write" pageName="Service Charge"><ServiceCharge /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/finance/vat" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Central VAT"><CentralVatManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/finance/payouts" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.write" pageName="Finance Payouts"><FinancePayouts /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/disputes" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.read" pageName="Disputes"><Disputes /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/notifications" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions={['support.tickets', 'support.respond']} pageName="Notifications"><Notifications /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/templates" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Templates"><Templates /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/analytics" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Analytics"><MerchantAnalytics /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/insights" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Platform Insights"><PlatformInsights /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/currency-management" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="merchants.write" pageName="Currency Management"><MerchantCurrencyManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/logs" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="system.logs" pageName="System Logs"><Logs /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/rbac" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="rbac.read" pageName="Role Management"><RBAC /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/platform-team" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="rbac.read" pageName="Platform Team"><PlatformTeam /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/payments" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.write" pageName="Payment Gateways"><PaymentGatewayManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/shipping" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.read" pageName="Shipping"><ShippingManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/treasury" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions={['financial.read', 'financial.settlements']} pageName="Treasury"><TreasuryPage /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/settlements" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.settlements" pageName="Settlements"><Settlements /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/platform-revenue" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.settlements" pageName="Platform Revenue"><PlatformRevenue /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/merchant-gateways" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.settlements" pageName="Merchant Gateways"><MerchantGatewayManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/wallets" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.settlements" pageName="Wallet Management"><WalletManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/chowdeck" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.read" pageName="Chowdeck Delivery"><ChowdeckManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/refunds" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.settlements" pageName="Refunds"><Refunds /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/pos" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.read" pageName="POS Management"><POSManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/pos-settlement" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.settlements" pageName="POS Settlement"><POSSettlement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/appointments" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="orders.read" pageName="Appointments"><AppointmentManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/whatsapp" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="whatsapp.read" pageName="WhatsApp Control Center"><WhatsAppControlCenter /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/whatsapp/legacy" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="whatsapp.read" pageName="WhatsApp Admin (legacy)"><WhatsAppAdmin /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/sms" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="SMS Admin"><SMSAdmin /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/push-notifications" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="notifications.broadcast" pageName="Push Notifications"><PushNotifications /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/google-calendar" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Google Calendar"><GoogleOAuthConfig /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/features" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="rbac.write" pageName="Feature Management"><FeatureManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/tiers" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="rbac.write" pageName="Tier Management"><TierManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/tier-limits" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="rbac.write" pageName="Tier Limits Management"><TierLimitsManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/ai-limits" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="rbac.write" pageName="AI Limits Management"><AILimitsManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/ai-providers" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="AI Providers"><AIProviders /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/locations" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="stores.read" pageName="Locations"><LocationManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/subscriptions" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="stores.read" pageName="Subscriptions"><SubscriptionManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/marketplace" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Marketplace"><MarketplaceManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/custom-domains" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Custom Domains"><CustomDomains /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/currency" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Currency Management"><CurrencyManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/analytics-config" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Analytics Configuration"><GA4PlatformConfig /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/kyc" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.read" pageName="KYC Management"><KYCManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/dva" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.read" pageName="Payment Rails"><DVAManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/dva-settlements" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.settlements" pageName="DVA Settlements"><DVASettlements /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/group-deal-settlements" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="financial.settlements" pageName="Group Deal Settlements"><GroupDealSettlements /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/storefront-ads" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Storefront Ads"><AdFeatureManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/email" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Email Campaigns"><EmailCampaigns /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/staff-activity" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="system.logs" pageName="Staff Activity"><StaffActivity /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/ai-insights" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="AI Insights"><ConsolidatedAIInsights /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/ai-assistant" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="AI Assistant"><AIAssistant /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/waitlist" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Waitlist Management"><WaitlistManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/website-leads" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Website Leads"><WebsiteLeadsManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/affiliates-oversight" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Affiliate Oversight"><AffiliateOversight /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/platform-referrals" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Platform Referrals"><ReferralManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/merchant-activity" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="analytics.read" pageName="Merchant Activity"><MerchantActivityInsights /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/themes" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="themes.read" pageName="Theme Management"><ThemeManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/community" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Community Hub"><CommunityManagement /></PermissionGuard></Shell></RequireAdmin>} />
          <Route path="/profile" element={<RequireAdmin><Shell><AdminProfile /></Shell></RequireAdmin>} />
          <Route path="/settings" element={<RequireAdmin><Shell><PermissionGuard requiredPermissions="settings.write" pageName="Settings"><AdminSettings /></PermissionGuard></Shell></RequireAdmin>} />

          {/* Catch-all for unknown routes - show 404 page */}
          <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </AccessControlProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
